// src/pages/JobEstimateSpreadsheet.js  (Priority Pest Defense)
//
// Ported from the Servicing app's Job Estimate Spreadsheet. Same layout and
// buttons; the data underneath is Priority's:
//   * Recurring jobs are ONE doc. Visits come from getOccurrencesForJob +
//     occurrenceOverrides + excludedDates (same visits as Calendar / Home).
//     Edits made on this page for a single visit are still saved to
//     recurring_job_occurrences/{jobId}_{date} (that collection may be empty).
//   * Income: each row's revenue is its Approved Invoice total when one exists,
//     otherwise its estimate. Checks Received come from `deposits`; a payment
//     recorded against a recurring job's WO is spread over that job's invoiced
//     visits (never counted on more than one visit).
//   * Labor: estimated labor uses the shared Tech Daily Pay rates
//     (utils/techDailyPay). Actual Tech Daily Pay (dailyLabor) replaces the
//     estimate in a row's profit whenever it is > 0. Unassigned daily pay is a
//     period expense in the summary.
import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  collection,
  getDocs,
  addDoc,
  doc,
  updateDoc,
  setDoc,
  deleteDoc,
  serverTimestamp,
  query,
  where,
} from "firebase/firestore";
import { addJobTotal } from "../utils/addJobTotal";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import moment from "moment";
import { db } from "../firebaseConfig";
import {
  buildContractorIdentities,
  techAssignMatches,
  isSubcontractor as isSubcontractorRecord,
  looseNameKey,
} from "../utils/jobEstimateContractorIdentity";
import "./global.css";
import "./SubPay.css";
import "./jobEstimateSpreadsheet.css";
import { useServiceTypes, withCurrentValue } from "../utils/serviceTypeOptions";
import { SubPaymentEntry, SubPaymentHistory } from "./SubPayComponents";
import { makeScheduleId } from "../utils/subPaySchedule";
import { isRecurring, getOverrideScheduledKey } from "../utils/recurringOccurrenceUtils";
import {
  invoiceTotal as sharedInvoiceTotal,
  isInvoiceCovered,
} from "../utils/depositsInvoiceMatching";
import {
  getHourlyPayRate,
  getHoursPerDay,
  getDefaultDailyPay,
  fetchDailyLaborDays,
  summarizeDailyLabor,
  usersToMap,
} from "../utils/techDailyPay";
import {
  dateKeyOf,
  localTodayKey,
  localMonthStartKey,
  getRecurringVisits,
  pickVisitOverride,
  defaultVisitPrice,
  visitStatusFromJob,
  invoiceTotal,
  invoiceNumberOf,
  invoiceStatusOf,
  matchInvoicesToRows,
  recurringPaymentsByRow,
  computeInvoicePaidTotals,
  rowKeyForJob,
  rowKeyForVisit,
  dailyPayByJobDate,
  attributeDailyPayToVisits,
} from "../utils/jobEstimatePriorityData";

// Priority is one company. Checks are recorded under "PPD" (same choices as
// Approved Invoices' "Invoice Paid" popup).
const DEFAULT_CHECK_VENDOR = "PPD";
const checkVendorOptions = ["PPD", "Other"];

const statusOptions = ["Active", "Completed", "Invoice Sent", "Paid In Full", "On Hold", "Closed"];

const monthlyBillTypes = [
  "Utilities",
  "Insurance",
  "Loan",
  "Lease",
  "Rent",
  "Vehicle",
  "Fuel",
  "Phone",
  "Internet",
  "Software",
  "Subscriptions",
  "Credit Card",
  "Bank Fee",
  "Office Supplies",
  "Equipment Rental",
  "Storage",
  "Payroll",
  "Taxes",
  "Licensing",
  "Permits",
  "Marketing",
  "Legal",
  "Accounting",
  "Other",
];

// Local-time date keys (the Servicing copy used UTC, which rolled to tomorrow
// in the evening).
const todayKey = () => localTodayKey();

const currentMonthStartKey = () => localMonthStartKey();

const emptyBulkMaterialForm = {
  date: todayKey(),
  storeName: "",
  amount: "",
  jobId: "",
  note: "",
};

const EXPENSE_START_DATE = "2026-01-01";

const emptyMonthlyExpenseForm = {
  vendorId: "",
  vendorName: "",
  billType: "Utilities",
  billTypeOther: "",
  description: "",
  amount: "",
  frequency: "Monthly",
  startDate: EXPENSE_START_DATE,
};

const emptyBulkIncomeForm = {
  date: todayKey(),
  payerName: "",
  amount: "",
  incomeType: "General Income",
  description: "",
  jobId: "",
};

// Service Type options come from Settings -> Service Types (see useServiceTypes below).

const paymentTypeOptions = ["Deposit", "Progress", "Final", "Other"];

const toDateKey = (value) => dateKeyOf(value);

const money = (value) =>
  Number(value || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });

  const getApprovedInvoiceNumber = (invoice = {}) => {
    return String(invoice.wo || invoice.invoiceNumber || invoice.id || "").trim();
  };

const numberValue = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const normalizePersonName = (value = "") =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const resolveAssignedName = (assignment) => {
  if (typeof assignment === "string") return assignment;
  if (!assignment || typeof assignment !== "object") return "";
  return (
    assignment.name ||
    assignment.fullName ||
    assignment.displayName ||
    assignment.techName ||
    `${assignment.firstName || ""} ${assignment.lastName || ""}`.trim() ||
    assignment.email ||
    ""
  );
};

// Hourly rate, hours per day and day rate come from utils/techDailyPay
// (getHourlyPayRate / getHoursPerDay / getDefaultDailyPay) so this page, Tech
// Daily Pay and every accounting page use the same numbers.

const PAY_METHOD_LABELS = {
  hourly_only: "Hourly Only",
  greater_of_hourly_or_percentage: "Greater of Hourly or Job %",
  percentage_only: "Job % Only",
  hourly_plus_percentage: "Hourly + Job %",
};

const calculatePayByMethod = ({
  hourlyPay = 0,
  estimatedTotal = 0,
  percentageRate = 0,
  method = "hourly_only",
}) => {
  const percentagePay =
    numberValue(estimatedTotal) * (numberValue(percentageRate) / 100);

  switch (method) {
    case "greater_of_hourly_or_percentage":
      return Math.max(numberValue(hourlyPay), percentagePay);
    case "percentage_only":
      return percentagePay;
    case "hourly_plus_percentage":
      return numberValue(hourlyPay) + percentagePay;
    case "hourly_only":
    default:
      return numberValue(hourlyPay);
  }
};

const findStaffPayOverride = (job = {}, tech = {}, techName = "") => {
  const overrides = Array.isArray(job.staffCommissionOverrides)
    ? job.staffCommissionOverrides
    : [];

  const techId = String(tech.id || tech.uid || "");
  const normalizedName = normalizePersonName(techName);

  return overrides.find((item) => {
    const overrideId = String(item.staffId || "");
    const overrideName = normalizePersonName(item.staffName || "");
    return (
      (techId && overrideId && techId === overrideId) ||
      (normalizedName && overrideName === normalizedName)
    );
  });
};


const getRecordedContractIncome = (job = {}) => {
  const paymentTotal = (Array.isArray(job.payments) ? job.payments : []).reduce(
    (sum, payment) => sum + numberValue(payment.amount),
    0
  );
  const checkTotal = (Array.isArray(job.checksReceived) ? job.checksReceived : []).reduce(
    (sum, check) => sum + numberValue(check.amount),
    0
  );
  return paymentTotal + checkTotal;
};

// Marks Priority approvedInvoices Paid / not Paid after a check is added,
// edited or deleted here. Paid amounts, invoice total and "covered" all come
// from utils/depositsInvoiceMatching - the same rule Approved Invoices and
// Deposits use, and the same numbers this page shows. Only invoices this page marked Paid are ever reverted, so a
// manual "Paid" from Approved Invoices is left alone.
const syncApprovedInvoicePaidStatus = async (invoiceNumber, jobsList = []) => {
  const invoiceKey = String(invoiceNumber || "").trim();
  if (!invoiceKey) return;

  const [depositsSnap, approvedInvoicesSnap] = await Promise.all([
    getDocs(collection(db, "deposits")),
    getDocs(collection(db, "approvedInvoices")),
  ]);

  const depositsList = depositsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const invoicesList = approvedInvoicesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const paidTotals = computeInvoicePaidTotals(depositsList, invoicesList, jobsList);

  const legacyOccurrence = /^(.*?)_(\d{4}-\d{2}-\d{2})$/.exec(invoiceKey);
  const matchingApprovedInvoices = invoicesList.filter((invoice) => {
    if (getApprovedInvoiceNumber(invoice) === invoiceKey) return true;
    if (!legacyOccurrence) return false;
    const jobRef = String(invoice.originalJobId || invoice.jobId || "");
    return jobRef === legacyOccurrence[1] && toDateKey(invoice.occurrenceKey) === legacyOccurrence[2];
  });

  for (const invoice of matchingApprovedInvoices) {
    // Same total and coverage rule as Approved Invoices and Deposits.
    const total = sharedInvoiceTotal(invoice);
    if (!total) continue;
    const totalPaid = numberValue(paidTotals[invoice.id]);
    const nowIso = new Date().toISOString();

    if (isInvoiceCovered(invoice, totalPaid)) {
      if (invoiceStatusOf(invoice) === "Paid" && numberValue(invoice.paidAmount) === totalPaid) continue;
      await updateDoc(doc(db, "approvedInvoices", invoice.id), {
        status: "Paid",
        paid: true,
        paidAt: invoice.paidAt || nowIso,
        paidAmount: totalPaid,
        paymentMatchedFrom: "JobEstimateSpreadsheet",
        updatedAt: nowIso,
      });
    } else if (
      invoiceStatusOf(invoice) === "Paid" &&
      invoice.paymentMatchedFrom === "JobEstimateSpreadsheet"
    ) {
      await updateDoc(doc(db, "approvedInvoices", invoice.id), {
        status: "Sent",
        paid: false,
        paidAmount: totalPaid,
        paymentMatchedFrom: "JobEstimateSpreadsheet",
        updatedAt: nowIso,
      });
    }
  }
};

const getBaseWO = (wo) => {
  if (!wo) return "";

  const text = String(wo).trim();

  // Regular extended WO: 2999-1, 3000-2 → 2999, 3000
  if (/^\d+-\d+$/.test(text)) {
    return text.replace(/-\d+$/, "");
  }

  // Proposal extended WO: P-10021-1 → P-10021
  if (/^[A-Za-z]+-\d+-\d+$/.test(text)) {
    return text.replace(/-\d+$/, "");
  }

  // Proposal recurring date WO: P-10021-2026-05-10 → P-10021
  if (/^[A-Za-z]+-\d+-\d{4}-\d{2}-\d{2}$/.test(text)) {
    return text.replace(/-\d{4}-\d{2}-\d{2}$/, "");
  }

  // P-10021, P-10022, P-10035 stay separate
  return text;
};



const getLocalShiftTimes = (dateKey) => ({
  clockIn: new Date(`${dateKey}T08:00:00`),
  clockOut: new Date(`${dateKey}T16:00:00`),
});

// The period the summary boxes cover: From/To when set, else This Week /
// This Month, else Jan 1 2026 through today. Module-level so the Tech Daily
// Pay memos (declared before the component's helpers) can use it too.
const computeSummaryRangeKeys = (fromDate, toDate, summaryRange) => {
  if (fromDate || toDate) {
    return { startKey: fromDate || "0000-01-01", endKey: toDate || "9999-12-31" };
  }
  if (summaryRange === "week") {
    const start = moment().startOf("day").subtract(moment().day(), "days");
    return {
      startKey: start.format("YYYY-MM-DD"),
      endKey: start.clone().add(6, "days").format("YYYY-MM-DD"),
    };
  }
  if (summaryRange === "month") {
    return {
      startKey: moment().startOf("month").format("YYYY-MM-DD"),
      endKey: moment().endOf("month").format("YYYY-MM-DD"),
    };
  }
  return { startKey: EXPENSE_START_DATE, endKey: todayKey() };
};

// "PPD" / "Other" + typed name -> the company saved on the deposit.
const vendorValue = (choice, other) =>
  choice === "Other" ? String(other || "").trim() || "Other" : choice || DEFAULT_CHECK_VENDOR;

// Load a saved deposit's company back into the vendor picker.
const setVendorChoice = (company, setChoice, setOther) => {
  const value = String(company || "").trim();
  if (!value) {
    setChoice(DEFAULT_CHECK_VENDOR);
    setOther("");
  } else if (checkVendorOptions.includes(value) && value !== "Other") {
    setChoice(value);
    setOther("");
  } else {
    setChoice("Other");
    setOther(value === "Other" ? "" : value);
  }
};

// Extra fields on a split-check deposit for one visit of a recurring job.
const splitRowVisitFields = (row = {}) =>
  row.occurrenceKey
    ? {
        jobId: row.originalJobId || "",
        occurrenceKey: row.canonicalOccurrenceKey || "",
        occurrenceDocKey: row.occurrenceKey,
      }
    : { jobId: row.jobId || "" };


export default function JobEstimateSpreadsheet() {
  // Service Type options from Settings -> Service Types.
  const { serviceTypes } = useServiceTypes();
  const [jobs, setJobs] = useState([]);
  const [staff, setStaff] = useState([]);
  // Company names for subcontractors live here, not on the user record.
  const [contractorRecords, setContractorRecords] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [subcontractorPayments, setSubcontractorPayments] = useState([]);
  const [showSubPayables, setShowSubPayables] = useState(false);
  // Sub Pay popup / payables: which save is running, which payment is being deleted,
  // and which contractor the payables form should preselect.
  const [subPaySaving, setSubPaySaving] = useState(false);
  const [deletingSubPaymentId, setDeletingSubPaymentId] = useState("");
  const [payablesPickKey, setPayablesPickKey] = useState("");
  const [subPayOtherLines, setSubPayOtherLines] = useState({});
  const [editedRows, setEditedRows] = useState({});
  const [loading, setLoading] = useState(true);

  const [recurringOverrides, setRecurringOverrides] = useState({});
  // Priority: approvedInvoices (per-visit billing) and saved Tech Daily Pay days.
  const [approvedInvoices, setApprovedInvoices] = useState([]);
  const [dailyLaborDays, setDailyLaborDays] = useState([]);

  const [statusFilter, setStatusFilter] = useState("");
  const [sortBy, setSortBy] = useState("startDate");
  const [search, setSearch] = useState("");
  const [searchDraft, setSearchDraft] = useState("");
  const [summaryRange, setSummaryRange] = useState("all");
  const [fromDate, setFromDate] = useState(currentMonthStartKey());
  const [toDate, setToDate] = useState(todayKey());
  const [needsSchedulingOnly, setNeedsSchedulingOnly] = useState(false);
  const [hidePaidInFull, setHidePaidInFull] = useState(true);

  const [receiptJob, setReceiptJob] = useState(null);
const [showReceiptPopup, setShowReceiptPopup] = useState(false);
const [receiptAmount, setReceiptAmount] = useState("");
const [receiptNote, setReceiptNote] = useState("");
const [receiptFile, setReceiptFile] = useState(null);
const [savingReceipt, setSavingReceipt] = useState(false);

const [showAllJobs, setShowAllJobs] = useState(false);
const [expandedRecurringJobs, setExpandedRecurringJobs] = useState({});
const [expandedAssociatedJobs, setExpandedAssociatedJobs] = useState({});
const [associateJob, setAssociateJob] = useState(null);
const [associateTargetJobId, setAssociateTargetJobId] = useState("");
const [associateTargetJobIds, setAssociateTargetJobIds] = useState([]);
const [associateWOInput, setAssociateWOInput] = useState("");
const [showAssociatePopup, setShowAssociatePopup] = useState(false);
const [busyMessage, setBusyMessage] = useState("");
const [busyAction, setBusyAction] = useState("");
const [showTimeVerification, setShowTimeVerification] = useState(false);
const [missingTimeRows, setMissingTimeRows] = useState([]);
const [loadingTimeVerification, setLoadingTimeVerification] = useState(false);

const [paymentJob, setPaymentJob] = useState(null);
const [showPaymentPopup, setShowPaymentPopup] = useState(false);
const [paymentType, setPaymentType] = useState("Deposit");
const [paymentAmount, setPaymentAmount] = useState("");
const [paymentDate, setPaymentDate] = useState("");
const [editingPaymentIndex, setEditingPaymentIndex] = useState(null);
const [editingCheckIndex, setEditingCheckIndex] = useState(null);
const [checkJob, setCheckJob] = useState(null);
const [showCheckPopup, setShowCheckPopup] = useState(false);
const [checkVendor, setCheckVendor] = useState(DEFAULT_CHECK_VENDOR);
const [checkVendorOther, setCheckVendorOther] = useState("");
const [checkNumber, setCheckNumber] = useState("");
const [checkDate, setCheckDate] = useState("");
const [checkReference, setCheckReference] = useState("");
const [checkAmount, setCheckAmount] = useState("");
const [checkPaymentType, setCheckPaymentType] = useState("Deposit");
const [showSplitCheckPopup, setShowSplitCheckPopup] = useState(false);
const [splitCheckJob, setSplitCheckJob] = useState(null);
const [splitCheckNumber, setSplitCheckNumber] = useState("");
const [splitCheckDate, setSplitCheckDate] = useState("");
const [splitCheckVendor, setSplitCheckVendor] = useState(DEFAULT_CHECK_VENDOR);
const [splitCheckVendorOther, setSplitCheckVendorOther] = useState("");
const [splitCheckPaymentType, setSplitCheckPaymentType] = useState("Deposit");
const [splitCheckTotal, setSplitCheckTotal] = useState("");
const [splitRows, setSplitRows] = useState([]);
const [splitSortBy, setSplitSortBy] = useState("wo");
const [showLinkableWOsSection, setShowLinkableWOsSection] = useState(false);
const [linkableWOSearch, setLinkableWOSearch] = useState("");
const [editingSplitCheckGroupId, setEditingSplitCheckGroupId] = useState("");
const [editingSplitCheckDepositIds, setEditingSplitCheckDepositIds] = useState([]);

const [editingReceiptIndex, setEditingReceiptIndex] = useState(null);

const [subPayJob, setSubPayJob] = useState(null);
const [showSubPayPopup, setShowSubPayPopup] = useState(false);
const [subPayDraft, setSubPayDraft] = useState([]);
const [commissionJob, setCommissionJob] = useState(null);
const [showCommissionPopup, setShowCommissionPopup] = useState(false);
const [commissionDraft, setCommissionDraft] = useState([]);

const [vendors, setVendors] = useState([]);
const [bulkMaterialExpenses, setBulkMaterialExpenses] = useState([]);
const [monthlyExpenses, setMonthlyExpenses] = useState([]);
const [timeOffRequests, setTimeOffRequests] = useState([]);

const [showBulkMaterialPopup, setShowBulkMaterialPopup] = useState(false);
const [bulkMaterialForm, setBulkMaterialForm] = useState(emptyBulkMaterialForm);

const [showMonthlyExpensePopup, setShowMonthlyExpensePopup] = useState(false);
const [monthlyExpenseForm, setMonthlyExpenseForm] = useState(emptyMonthlyExpenseForm);
const [editingMonthlyExpense, setEditingMonthlyExpense] = useState(null);
const [monthlyExpenseEditEffectiveDate, setMonthlyExpenseEditEffectiveDate] = useState(todayKey());

const [bulkIncome, setBulkIncome] = useState([]);
const [showBulkIncomePopup, setShowBulkIncomePopup] = useState(false);
const [bulkIncomeForm, setBulkIncomeForm] = useState(emptyBulkIncomeForm);

  /* Contractor identity (company name + person name + every alias).
     Declared up here, before calculateRow, because calculateRow runs during
     render and now uses bucketKeyForName to match a sub's pay line to their
     tech assignment under either name. */
  const { identities: contractorIdentities, bucketKeyForName } = useMemo(
    () => buildContractorIdentities({ staff, contractorRecords }),
    [staff, contractorRecords]
  );

  /* Priority data indexes. Declared before calculateRow for the same reason
     as above: calculateRow reads them while rendering. */
  const staffById = useMemo(() => usersToMap(staff), [staff]);

  // Visits per recurring job for the current From/To, computed once per job
  // (the table asks for them many times per render).
  const recurringVisitCache = useMemo(() => new Map(), [jobs, fromDate, toDate]);
  const getVisitsForJob = (job) => {
    if (!job?.id) return [];
    if (!recurringVisitCache.has(job.id)) {
      recurringVisitCache.set(job.id, getRecurringVisits(job, fromDate, toDate));
    }
    return recurringVisitCache.get(job.id);
  };

  // Each approved invoice belongs to exactly one row (a one-time job, or one
  // visit of a recurring job).
  const invoiceMatch = useMemo(
    () => matchInvoicesToRows(approvedInvoices, jobs),
    [approvedInvoices, jobs]
  );

  // Paid per approved invoice - the ONE shared rule (Approved Invoices,
  // Deposits and this page all use utils/depositsInvoiceMatching).
  const invoicePaidTotals = useMemo(
    () => computeInvoicePaidTotals(deposits, approvedInvoices, jobs),
    [deposits, approvedInvoices, jobs]
  );

  // Recurring visits: paid = sum of that visit's invoices' paid amounts;
  // money that sits on no invoice is shown once on the job's main row.
  const recurringPayments = useMemo(
    () => recurringPaymentsByRow(deposits, jobs, invoiceMatch, invoicePaidTotals),
    [deposits, jobs, invoiceMatch, invoicePaidTotals]
  );

  // Tech Daily Pay by job and date (all saved days).
  const dailyPayIndex = useMemo(
    () => dailyPayByJobDate(dailyLaborDays, staffById),
    [dailyLaborDays, staffById]
  );

  // Recurring jobs: each day's pay is credited to one visit in the visible
  // From/To range (the visit that day, else the last visit before it).
  const recurringVisitDailyPay = useMemo(() => {
    const out = {};
    jobs.forEach((job) => {
      if (!isRecurring(job) || !dailyPayIndex[job.id]) return;
      const visits = getVisitsForJob(job);
      if (!visits.length) return;
      const { startKey, endKey } = computeSummaryRangeKeys(fromDate, toDate, summaryRange);
      const inRange = {};
      Object.entries(dailyPayIndex[job.id]).forEach(([date, amount]) => {
        if (date < startKey || date > endKey) return;
        inRange[date] = amount;
      });
      const byVisit = attributeDailyPayToVisits(inRange, visits);
      Object.entries(byVisit).forEach(([canonicalKey, amount]) => {
        out[rowKeyForVisit(job.id, canonicalKey)] = amount;
      });
    });
    return out;
  }, [jobs, dailyPayIndex, fromDate, toDate, summaryRange, recurringVisitCache]);

  // Actual Tech Daily Pay for one row, limited to the summary period so the
  // rows add up to the period's Tech Daily Pay. One-time job: its days in the
  // period. Recurring visit: the days credited to that visit above.
  const getActualDailyPay = (job = {}) => {
    if (job.__isRecurringOccurrence) {
      return numberValue(
        recurringVisitDailyPay[rowKeyForVisit(job.originalJobId, job.canonicalOccurrenceKey)]
      );
    }
    if (isRecurring(job)) return 0;
    const { startKey, endKey } = computeSummaryRangeKeys(fromDate, toDate, summaryRange);
    return Object.entries(dailyPayIndex[job.id] || {}).reduce(
      (sum, [date, amount]) =>
        date >= startKey && date <= endKey ? sum + numberValue(amount) : sum,
      0
    );
  };

  // Approved invoices on one row.
  const getRowInvoices = (job = {}) => {
    const rowKey = job.__isRecurringOccurrence
      ? rowKeyForVisit(job.originalJobId, job.canonicalOccurrenceKey)
      : rowKeyForJob(job.id);
    return invoiceMatch.byRow[rowKey] || [];
  };

  // How many jobs/visits each tech has on each day, so an estimated day of pay
  // is split across them (recurring visits included).
  const techDayJobCounts = useMemo(() => {
    const counts = {};
    const add = (dateKey, techs) => {
      if (!dateKey) return;
      (Array.isArray(techs) ? techs : [techs]).filter(Boolean).forEach((assignment) => {
        const key = `${dateKey}|${normalizePersonName(resolveAssignedName(assignment))}`;
        counts[key] = (counts[key] || 0) + 1;
      });
    };
    jobs.forEach((job) => {
      if (!isRecurring(job)) {
        add(job.startDate, job.techAssign);
        return;
      }
      getVisitsForJob(job).forEach(({ canonicalKey, scheduledKey }) => {
        const saved = recurringOverrides[`${job.id}_${canonicalKey}`] || {};
        const visit = pickVisitOverride(job, canonicalKey);
        add(
          saved.startDate || scheduledKey,
          Array.isArray(saved.techAssign)
            ? saved.techAssign
            : Array.isArray(visit.techAssign)
            ? visit.techAssign
            : job.techAssign
        );
      });
    });
    return counts;
  }, [jobs, recurringOverrides, fromDate, toDate, recurringVisitCache]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);

    // Every collection except jobs/users is optional in Priority: an empty or
    // unreadable collection (e.g. recurring_job_occurrences, which Priority
    // itself never writes) must not stop the page from loading.
    const safeGetDocs = async (name) => {
      try {
        return await getDocs(collection(db, name));
      } catch (error) {
        console.warn(`Job Estimate Spreadsheet could not read ${name}:`, error);
        return { docs: [], forEach: () => {} };
      }
    };

    const [
      jobsSnap,
      staffSnap,
      contractorRecordsSnap,
      totalsSnap,
      depositsSnap,
      recurringSnap,
      vendorsSnap,
      bulkMaterialsSnap,
      monthlyExpensesSnap,
      bulkIncomeSnap,
      timeOffSnap,
      subcontractorPaymentsSnap,
      legacySubcontractorPaymentsSnap,
      approvedInvoicesSnap,
      laborDays,
    ] = await Promise.all([
      getDocs(collection(db, "jobs")),
      getDocs(collection(db, "users")),
      safeGetDocs("contractors"),
      safeGetDocs("job_totals"),
      safeGetDocs("deposits"),
      safeGetDocs("recurring_job_occurrences"),
      safeGetDocs("vendors"),
      safeGetDocs("bulk_material_expenses"),
      safeGetDocs("monthly_expenses"),
      safeGetDocs("bulk_income"),
      safeGetDocs("timeOff"),
      safeGetDocs("subcontractorPay"),
      safeGetDocs("subcontractorPayments"),
      safeGetDocs("approvedInvoices"),
      fetchDailyLaborDays().catch((error) => {
        console.warn("Job Estimate Spreadsheet could not read Tech Daily Pay:", error);
        return [];
      }),
    ]);

    setApprovedInvoices(
      approvedInvoicesSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((invoice) => !invoice.deleted && String(invoice.status || "").toLowerCase() !== "void")
    );
    setDailyLaborDays(Array.isArray(laborDays) ? laborDays : []);
  
    const totalsMap = {};
    totalsSnap.forEach((d) => {
      totalsMap[d.id] = numberValue(d.data().total);
    });

    const depositsData = depositsSnap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    }));
    
    setDeposits(depositsData);

    const normalizeSubcontractorPayment = (d, sourceCollection) => {
      const data = d.data();
      return {
        id: d.id,
        ...data,
        sourceCollection,
        subcontractorId: data.subcontractorId || data.userId || "",
        subcontractorName: data.subcontractorName || data.userName || data.vendor || data.property || "",
        amount: numberValue(data.amount ?? data.amountPaid),
        amountPaid: numberValue(data.amountPaid ?? data.amount),
        date: toDateKey(data.date || data.datePaid || data.createdAt),
      };
    };

    setSubcontractorPayments([
      ...subcontractorPaymentsSnap.docs.map((d) => normalizeSubcontractorPayment(d, "subcontractorPay")),
      ...legacySubcontractorPaymentsSnap.docs.map((d) => normalizeSubcontractorPayment(d, "subcontractorPayments")),
    ]);

    const vendorsData = vendorsSnap.docs
  .map((d) => ({
    id: d.id,
    ...d.data(),
    companyName: d.data().companyName || d.data().vendor || d.data().name || "",
  }))
  .filter((v) => v.companyName)
  .sort((a, b) => String(a.companyName).localeCompare(String(b.companyName)));

setVendors(vendorsData);

const bulkMaterialsData = bulkMaterialsSnap.docs.map((d) => ({
  id: d.id,
  ...d.data(),
}));

setBulkMaterialExpenses(bulkMaterialsData);

const monthlyExpensesData = monthlyExpensesSnap.docs.map((d) => ({
  id: d.id,
  ...d.data(),
}));

setMonthlyExpenses(monthlyExpensesData);

const bulkIncomeData = bulkIncomeSnap.docs.map((d) => ({
  id: d.id,
  ...d.data(),
}));

setBulkIncome(bulkIncomeData);

const timeOffData = timeOffSnap.docs.map((d) => ({
  id: d.id,
  ...d.data(),
}));

setTimeOffRequests(timeOffData);

    const recurringMap = {};
recurringSnap.forEach((d) => {
  recurringMap[d.id] = {
    id: d.id,
    ...d.data(),
  };
});

setRecurringOverrides(recurringMap);
  
    const staffData = staffSnap.docs
      .map((d) => {
        const data = d.data();
  
        const firstName = data.firstName || data.first || data.fname || "";
        const lastName = data.lastName || data.last || data.lname || "";
  
        const fullName =
          `${firstName} ${lastName}`.trim() ||
          data.name ||
          data.displayName ||
          data.employeeName ||
          data.techName ||
          data.email ||
          "";
  
        // Pay fields (salary = WORK HOURLY RATE, hoursPerDay, dailyPayRate,
        // yearlySalary) are kept exactly as stored; utils/techDailyPay reads them.
        return {
          id: d.id,
          ...data,
          firstName,
          lastName,
          name: fullName,
        };
      })
      .filter((staffMember) => staffMember.name);
  
    const jobsData = jobsSnap.docs.map((d) => {
      const data = d.data();
      const jobKey = String(data.wo || data.WO || d.id);
      // Estimated total is the contract / job value.
      // Do NOT let check/deposit totals overwrite it.
      const syncedEstimatedTotal =
        data.estimatedTotal ?? totalsMap[jobKey] ?? totalsMap[d.id] ?? 0;
  
      return {
        // Priority fields the spreadsheet needs to expand and price visits:
        // completedDates, invoicedDates, excludedDates, occurrenceOverrides,
        // approvedInvoiceIdByDate, initialAmount, recurringInvoiceAmount...
        ...data,
        id: d.id,
        siteName: data.siteName || data.propertyName || "",
        city: data.city || "",
        wo: data.wo || data.WO || "",
        serviceType: data.serviceType || "",
        scope: data.scope || data.description || "",
        estimatedDays: data.estimatedDays || "",
        estimatedLabor: data.estimatedLabor || data.etimatedLabor || "",
        receipts: Array.isArray(data.receipts) ? data.receipts : [],
        receiptsTotal: numberValue(data.receiptsTotal),
        payments: Array.isArray(data.payments) ? data.payments : [],
        checksReceived: Array.isArray(data.checksReceived) ? data.checksReceived : [],
        estimatedMaterial: data.estimatedMaterial || 0,
        subcontractorPay: Array.isArray(data.subcontractorPay) ? data.subcontractorPay : [],
subPay: data.subPay || calculateSubPayTotal(data.subcontractorPay || []),
        estimatedTotal: syncedEstimatedTotal,
        estimatedLaborCost: data.estimatedLaborCost || 0,
        startDate: toDateKey(data.startDate),
        endDate: toDateKey(data.endDate || data.endDtate),
        creationDate: toDateKey(data.creationDate || data.createdAt || data.createdDate || data.dateCreated),
        createdAt: data.createdAt || data.creationDate || "",
        recurringStartDate: toDateKey(
          data.recurringStartDate ||
            data.seriesStartDate ||
            data.originalStartDate ||
            data.firstStartDate ||
            data.recurrence?.startsOn ||
            data.recurrence?.startDate ||
            data.recurrence?.originalStartDate ||
            data.recurrence?.seriesStartDate
        ),
        techAssign: Array.isArray(data.techAssign)
          ? data.techAssign
          : data.techAssign
          ? [data.techAssign]
          : [],
          status: data.status || "Active",
          // Never null: Priority's isRecurring() treats a null object as "none".
          recurrence: data.recurrence || data.reccurance || "none",
          isContractJob: data.isContractJob === true,
          isWarranty: data.isWarranty === true,
          warrantyMarkedAt: data.warrantyMarkedAt || "",
          contractBillingFrequency: data.contractBillingFrequency || "Monthly",
          contractMonthlyAmount: numberValue(data.contractMonthlyAmount),
          contractStartDate: toDateKey(data.contractStartDate),
          contractEndDate: toDateKey(data.contractEndDate),
          associatedJobIds: Array.isArray(data.associatedJobIds) ? data.associatedJobIds : [],
          associatedChildJobIds: Array.isArray(data.associatedChildJobIds) ? data.associatedChildJobIds : [],
          parentJobId: data.parentJobId || "",
      };
    });
  
    setContractorRecords(
      contractorRecordsSnap.docs.map((d) => ({ id: d.id, ...d.data() }))
    );
    setStaff(staffData);
    setJobs(jobsData);
    setLoading(false);
  };

  const getTechDailyJobCount = (techName, startDate) => {
    if (!techName || !startDate) return 1;
    return techDayJobCounts[`${startDate}|${normalizePersonName(techName)}`] || 1;
  };

  const daysBetweenKeys = (a, b) => {
    if (!a || !b) return 9999;
  
    const dateA = new Date(`${a}T00:00:00`);
    const dateB = new Date(`${b}T00:00:00`);
  
    return Math.abs(Math.round((dateA - dateB) / (1000 * 60 * 60 * 24)));
  };
  
  const getPrevDateKey = (dateKey) => {
    const d = new Date(`${dateKey}T00:00:00`);
    d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
  };
  
  const addFrequencyDate = (date, frequency) => {
    const copy = new Date(date);
  
    if (frequency === "Weekly") {
      copy.setDate(copy.getDate() + 7);
    } else if (frequency === "Quarterly") {
      copy.setMonth(copy.getMonth() + 3);
    } else if (frequency === "Yearly") {
      copy.setFullYear(copy.getFullYear() + 1);
    } else {
      copy.setMonth(copy.getMonth() + 1);
    }
  
    return copy;
  };
  
  const getSummaryRangeKeys = () => computeSummaryRangeKeys(fromDate, toDate, summaryRange);
  
  const isDateInRange = (dateKey, startKey, endKey) => {
    if (!dateKey) return false;
    return dateKey >= startKey && dateKey <= endKey;
  };

  const isPaidTimeOffRequest = (request = {}) => {
    const type = String(request.paidTimeType || request.timeOffType || request.type || "").toLowerCase();
    return !type.includes("unpaid") && !type.includes("no pay");
  };

  const getTimeOffStaffMember = (request = {}) => {
    const requestUserId = String(request.userId || request.employeeId || request.staffId || "").trim();
    if (requestUserId) {
      const byId = staff.find((person) => String(person.id) === requestUserId);
      if (byId) return byId;
    }

    const requestName = normalizePersonName(request.name || request.userName || request.employeeName || request.displayName || "");
    if (!requestName) return null;

    return staff.find((person) => {
      const candidates = [
        person.name,
        person.fullName,
        person.displayName,
        person.employeeName,
        `${person.firstName || ""} ${person.lastName || ""}`,
        person.email,
      ];

      return candidates.some((candidate) => normalizePersonName(candidate) === requestName);
    }) || null;
  };
  
  // A monthly expense made from a bank statement line for payroll (ADP) or a
  // contractor. Those are already in job labor / Sub Pay.
  const isBankPayrollOrSubMonthly = (expense = {}) => {
    const fromBank =
      expense.source === "BankStatementEntriesRecurringImport" || expense.sourceCollection === "bankStatementEntries";
    const type = String(expense.billType || expense.category || "").toLowerCase();
    return fromBank && (type === "payroll" || type === "subcontractor");
  };

  const getMonthlyExpenseTotalForRange = (expense, startKey, endKey) => {
    const amount = numberValue(expense.amount);
    if (!amount || !expense.startDate) return 0;
  
    const expenseStartKey = expense.startDate;
    const expenseEndKey = expense.endDate || "9999-12-31";
  
    const activeStartKey = expenseStartKey > startKey ? expenseStartKey : startKey;
    const activeEndKey = expenseEndKey < endKey ? expenseEndKey : endKey;
  
    if (activeStartKey > activeEndKey) return 0;
  
    if (expense.frequency === "One Time") {
      return isDateInRange(expenseStartKey, startKey, endKey) ? amount : 0;
    }
  
    let total = 0;
    let current = new Date(`${expenseStartKey}T00:00:00`);
    const finalDate = new Date(`${activeEndKey}T00:00:00`);
  
    while (current <= finalDate) {
      const key = current.toISOString().slice(0, 10);
  
      if (key >= activeStartKey && key <= activeEndKey) {
        total += amount;
      }
  
      current = addFrequencyDate(current, expense.frequency || "Monthly");
    }
  
    return total;
  };

  const bulkIncomePossibleJobs = useMemo(() => {
    if (!bulkIncomeForm.date) return [];
  
    return jobs
      .filter((job) => {
        if (!job.startDate) return false;
        return daysBetweenKeys(job.startDate, bulkIncomeForm.date) <= 3;
      })
      .sort((a, b) => String(a.startDate || "").localeCompare(String(b.startDate || "")));
  }, [jobs, bulkIncomeForm.date]);
  
  const materialPossibleJobs = useMemo(() => {
    if (!bulkMaterialForm.date) return [];
  
    return jobs
      .filter((job) => {
        if (!job.startDate) return false;
        return daysBetweenKeys(job.startDate, bulkMaterialForm.date) <= 3;
      })
      .sort((a, b) => String(a.startDate || "").localeCompare(String(b.startDate || "")));
  }, [jobs, bulkMaterialForm.date]);
  
  const materialReceiptMatches = useMemo(() => {
    const amount = numberValue(bulkMaterialForm.amount);
  
    if (!amount || !bulkMaterialForm.date) return [];
  
    const matches = [];
  
    jobs.forEach((job) => {
      const receipts = Array.isArray(job.receipts) ? job.receipts : [];
  
      receipts.forEach((receipt) => {
        const receiptAmount = numberValue(receipt.amount);
        const receiptDate =
          receipt.purchaseDate ||
          receipt.date ||
          toDateKey(receipt.uploadedAt) ||
          toDateKey(receipt.updatedAt);
  
        if (
          receiptAmount === amount &&
          receiptDate &&
          daysBetweenKeys(receiptDate, bulkMaterialForm.date) <= 3
        ) {
          matches.push({
            job,
            receipt,
            receiptDate,
          });
        }
      });
    });
  
    return matches;
  }, [jobs, bulkMaterialForm.amount, bulkMaterialForm.date]);

  const calculateSubPayTotal = (items = []) => {
    return items.reduce((sum, item) => {
      const basePay = numberValue(item.basePay);
      const changeOrdersTotal = Array.isArray(item.changeOrders)
        ? item.changeOrders.reduce((coSum, co) => coSum + numberValue(co.amount), 0)
        : 0;
  
      return sum + basePay + changeOrdersTotal;
    }, 0);
  };
  
  const getMatchingSubPayEntry = (job = {}, tech = {}, techName = "") => {
    const rawName = techName || tech.name || `${tech.firstName || ""} ${tech.lastName || ""}`;
    const target = normalizePersonName(rawName);
    // A sub is often assigned under their person name but paid under their
    // company name. Match either way so their hourly rate is not ALSO counted
    // on top of the job's sub pay.
    const targetBucket = bucketKeyForName(rawName);
    return (Array.isArray(job.subcontractorPay) ? job.subcontractorPay : []).find((item) => {
      const lineName = item?.subcontractorName || item?.name || "";
      if (tech.id && item?.subcontractorId && String(item.subcontractorId) === String(tech.id)) return true;
      if (normalizePersonName(lineName) === target) return true;
      return Boolean(targetBucket) && bucketKeyForName(lineName) === targetBucket;
    });
  };

  const hasSpecificJobSubAmount = (job = {}, tech = {}, techName = "") => {
    const entry = getMatchingSubPayEntry(job, tech, techName);
    if (!entry) return false;
    const base = numberValue(entry.basePay);
    const coTotal = (Array.isArray(entry.changeOrders) ? entry.changeOrders : []).reduce(
      (sum, co) => sum + numberValue(co.amount),
      0
    );
    return base > 0 || coTotal > 0;
  };

  const getJobWorkDays = (job = {}) => {
    const explicit = numberValue(job.estimatedDays);
    if (explicit > 0) return explicit;
    const start = toDateKey(job.startDate);
    const end = toDateKey(job.endDate);
    if (!start) return 1;
    if (!end || end < start) return 1;
    const startDate = new Date(`${start}T12:00:00`);
    const endDate = new Date(`${end}T12:00:00`);
    return Math.max(1, Math.floor((endDate - startDate) / 86400000) + 1);
  };

  const calculateRow = (job) => {
    const assignedTechs = Array.isArray(job.techAssign) ? job.techAssign : [];
    const estimatedDays = getJobWorkDays(job);
    const estimatedTotal = numberValue(job.estimatedTotal);
    const receiptsTotal = numberValue(job.receiptsTotal);

    // ESTIMATED labor: each assigned tech's normal day pay (Tech Daily Pay
    // rates from the Staff page), split over the jobs they have that day.
    const laborCost = assignedTechs.reduce((total, assignment) => {
      const techName = resolveAssignedName(assignment);
      const normalizedTechName = normalizePersonName(techName);

      const tech = staff.find((person) => {
        const candidates = [
          person.name,
          person.fullName,
          person.displayName,
          person.employeeName,
          person.techName,
          `${person.firstName || ""} ${person.lastName || ""}`,
          person.email,
        ];

        return candidates.some(
          (candidate) => normalizePersonName(candidate) === normalizedTechName
        );
      });

      if (!tech) {
        return total;
      }

      const isSubContractor = String(tech.workerType || "")
        .toLowerCase()
        .replace(/\s+/g, "")
        .includes("subcontractor");

      if (isSubContractor && hasSpecificJobSubAmount(job, tech, techName)) {
        return total;
      }

      const jobsForTechSameDay = getTechDailyJobCount(techName, job.startDate);

      const dailyLaborCost = getDefaultDailyPay(tech);
      const hourlyPay =
        (dailyLaborCost / jobsForTechSameDay) * estimatedDays;

      const override = findStaffPayOverride(job, tech, techName);
      const hasOverrideRate =
        override &&
        override.commissionRate !== "" &&
        override.commissionRate !== null &&
        override.commissionRate !== undefined;

      const percentageRate = hasOverrideRate
        ? numberValue(override.commissionRate)
        : numberValue(tech.commissionRate);

      const method =
        override?.payCalculationMethod ||
        tech.payCalculationMethod ||
        "hourly_only";

      return total + calculatePayByMethod({
        hourlyPay,
        estimatedTotal,
        percentageRate,
        method,
      });
    }, 0);

    // ACTUAL labor from Tech Daily Pay. When there is any, it replaces the
    // estimate in profit (never both).
    const actualDailyPay = getActualDailyPay(job);
    const laborUsed = actualDailyPay > 0 ? actualDailyPay : laborCost;

    const rowInvoices = getRowInvoices(job);
    const invoicedTotal = rowInvoices.reduce((sum, invoice) => sum + invoiceTotal(invoice), 0);

    const estimatedMaterial = numberValue(job.estimatedMaterial);
    const subPay = numberValue(job.subPay);
    const isWarranty = job.isWarranty === true;

    // Revenue: the Approved Invoice total when this job / visit was invoiced,
    // otherwise the estimate. (Payments and checks are money collected against
    // this, not extra income.)
    const recognizedIncome = isWarranty
      ? 0
      : rowInvoices.length
      ? invoicedTotal
      : job.isContractJob
      ? getRecordedContractIncome(job)
      : estimatedTotal;

    const totalExpenses = estimatedMaterial + receiptsTotal + laborUsed + subPay;
    const warrantyCost = isWarranty ? totalExpenses : 0;
    const profit = isWarranty ? 0 : recognizedIncome - totalExpenses;

    return {
      ...job,
      isWarranty,
      receiptsTotal,
      totalExpenses,
      warrantyCost,
      recognizedIncome,
      invoicedTotal,
      invoiceCount: rowInvoices.length,
      estimatedLaborCost: laborCost,
      actualDailyPay,
      laborUsed,
      subPay,
      estimatedProfit: profit,
    };
  };

const updateRecurringOverride = (occurrenceKey, field, value) => {
  setRecurringOverrides((prev) => {
    const current = prev[occurrenceKey] || {};

    const updated = {
      ...current,
      [field]: value,
    };

    if (field === "techAssign") {
      updated.estimatedLabor = Array.isArray(value)
        ? value.filter(Boolean).length
        : 0;
    }

    return {
      ...prev,
      [occurrenceKey]: updated,
    };
  });

  setEditedRows((old) => ({
    ...old,
    [occurrenceKey]: true,
  }));
};

  const updateRow = (jobId, field, value) => {
    if (String(jobId).includes("_")) {
      updateRecurringOverride(jobId, field, value);
      return;
    }
    setJobs((prev) =>
      prev.map((job) => {
        if (job.id !== jobId) return job;
  
        let updated = {
          ...job,
          [field]: value,
        };
  
        if (field === "techAssign") {
          updated.estimatedLabor = value.filter(Boolean).length;
        }
  
        updated = calculateRow(updated, prev);
  
        setEditedRows((old) => ({
          ...old,
          [jobId]: true,
        }));
  
        return updated;
      })
    );
  };

  const updateTechDropdown = (jobId, index, techName, currentTechAssign = []) => {
    if (String(jobId).includes("_")) {
      const current = recurringOverrides[jobId] || {};
      const techs = Array.isArray(current.techAssign)
        ? [...current.techAssign]
        : Array.isArray(currentTechAssign)
        ? [...currentTechAssign]
        : [];
      techs[index] = techName;
      updateRecurringOverride(jobId, "techAssign", techs.filter(Boolean));
      return;
    }

    setJobs((prev) =>
      prev.map((job) => {
        if (job.id !== jobId) return job;
  
        const techs = Array.isArray(job.techAssign) ? [...job.techAssign] : [];
        techs[index] = techName;
  
        const cleanedTechs = techs.filter(Boolean);
  
        const updated = calculateRow(
          {
            ...job,
            techAssign: cleanedTechs,
            estimatedLabor: cleanedTechs.length,
          },
          prev
        );
  
        setEditedRows((old) => ({
          ...old,
          [jobId]: true,
        }));
  
        return updated;
      })
    );
  };

  const addTechAssign = (jobId, currentTechAssign = []) => {
    if (String(jobId).includes("_")) {
      const current = recurringOverrides[jobId] || {};
      const techs = Array.isArray(current.techAssign)
        ? [...current.techAssign]
        : Array.isArray(currentTechAssign)
        ? [...currentTechAssign]
        : [];
      techs.push("");
      updateRecurringOverride(jobId, "techAssign", techs);
      return;
    }

    setJobs((prev) =>
      prev.map((job) => {
        if (job.id !== jobId) return job;
  
        const techs = Array.isArray(job.techAssign) ? [...job.techAssign] : [];
        techs.push("");
  
        setEditedRows((old) => ({
          ...old,
          [jobId]: true,
        }));
  
        return {
          ...job,
          techAssign: techs,
        };
      })
    );
  };
  
  const removeTechAssign = (jobId, index, currentTechAssign = []) => {
    if (String(jobId).includes("_")) {
      const current = recurringOverrides[jobId] || {};
      const techs = Array.isArray(current.techAssign)
        ? [...current.techAssign]
        : Array.isArray(currentTechAssign)
        ? [...currentTechAssign]
        : [];
      techs.splice(index, 1);
      updateRecurringOverride(jobId, "techAssign", techs.filter(Boolean));
      return;
    }

    setJobs((prev) =>
      prev.map((job) => {
        if (job.id !== jobId) return job;
  
        const techs = Array.isArray(job.techAssign) ? [...job.techAssign] : [];
        techs.splice(index, 1);
  
        const cleanedTechs = techs.filter(Boolean);
  
        const updated = calculateRow(
          {
            ...job,
            techAssign: cleanedTechs,
            estimatedLabor: cleanedTechs.length,
          },
          prev
        );
  
        setEditedRows((old) => ({
          ...old,
          [jobId]: true,
        }));
  
        return updated;
      })
    );
  };

  const saveBulkIncome = async () => {
    setBusyAction("saveBulkIncome");
    setBusyMessage("Saving bulk income...");
    try {
    const amount = numberValue(bulkIncomeForm.amount);
  
    if (!bulkIncomeForm.date || !bulkIncomeForm.payerName || !amount) {
      alert("Please enter date, payer/source, and amount.");
      return;
    }
  
    const selectedJob = bulkIncomeForm.jobId
      ? jobs.find((job) => job.id === bulkIncomeForm.jobId)
      : null;
  
    const bulkIncomeData = {
      date: bulkIncomeForm.date,
      payerName: bulkIncomeForm.payerName,
      amount,
      incomeType: bulkIncomeForm.incomeType || "General Income",
      description: bulkIncomeForm.description || "",
      jobId: selectedJob?.id || "",
      wo: selectedJob?.wo || "",
      siteName: selectedJob?.siteName || "",
      matchedToJob: !!selectedJob,
      source: "JobEstimateSpreadsheetBulkIncome",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
  
    const docRef = await addDoc(collection(db, "bulk_income"), bulkIncomeData);
  
    const savedIncome = {
      id: docRef.id,
      ...bulkIncomeData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  
    setBulkIncome((prev) => [...prev, savedIncome]);
  
    if (selectedJob?.id) {
      const newPayment = {
        type: bulkIncomeForm.incomeType || "Bulk Income",
        amount,
        date: bulkIncomeForm.date,
        note: bulkIncomeForm.description || "",
        payerName: bulkIncomeForm.payerName,
        source: "BulkIncome",
        bulkIncomeId: docRef.id,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
  
      const updatedPayments = Array.isArray(selectedJob.payments)
        ? [...selectedJob.payments, newPayment]
        : [newPayment];
  
      // Payment notes do not change the estimated/contract total.
      await updateDoc(doc(db, "jobs", selectedJob.id), {
        payments: updatedPayments,
        updatedAt: serverTimestamp(),
      });
  
      setJobs((prev) =>
        prev.map((job) =>
          job.id === selectedJob.id
            ? {
                ...job,
                payments: updatedPayments,
              }
            : job
        )
      );
    }
  
    setBulkIncomeForm(emptyBulkIncomeForm);
    setShowBulkIncomePopup(false);
    } finally {
      setBusyMessage("");
      setBusyAction("");
    }
  };

  const saveBulkMaterialExpense = async () => {
    setBusyAction("saveBulkMaterialExpense");
    setBusyMessage("Saving bulk material...");
    try {
    const amount = numberValue(bulkMaterialForm.amount);
  
    if (!bulkMaterialForm.date || !bulkMaterialForm.storeName || !amount) {
      alert("Please enter date, store name, and amount.");
      return;
    }
  
    const selectedJob = bulkMaterialForm.jobId
      ? jobs.find((job) => job.id === bulkMaterialForm.jobId)
      : null;
  
    const bulkMaterialData = {
      date: bulkMaterialForm.date,
      storeName: bulkMaterialForm.storeName,
      amount,
      note: bulkMaterialForm.note || "",
      jobId: selectedJob?.id || "",
      wo: selectedJob?.wo || "",
      siteName: selectedJob?.siteName || "",
      matchedToJob: !!selectedJob,
      source: "JobEstimateSpreadsheetBulkMaterial",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
  
    const docRef = await addDoc(collection(db, "bulk_material_expenses"), bulkMaterialData);
  
    const savedBulkMaterial = {
      id: docRef.id,
      ...bulkMaterialData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  
    setBulkMaterialExpenses((prev) => [...prev, savedBulkMaterial]);
  
    if (selectedJob?.id) {
      const receiptData = {
        amount,
        note: `Bulk Material - ${bulkMaterialForm.storeName}${
          bulkMaterialForm.note ? ` - ${bulkMaterialForm.note}` : ""
        }`,
        storeName: bulkMaterialForm.storeName,
        purchaseDate: bulkMaterialForm.date,
        fileURL: "",
        fileName: "",
        source: "BulkMaterial",
        bulkMaterialExpenseId: docRef.id,
        uploadedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
  
      const updatedReceipts = Array.isArray(selectedJob.receipts)
        ? [...selectedJob.receipts, receiptData]
        : [receiptData];
  
      const receiptsTotal = updatedReceipts.reduce(
        (sum, receipt) => sum + numberValue(receipt.amount),
        0
      );
  
      await updateDoc(doc(db, "jobs", selectedJob.id), {
        receipts: updatedReceipts,
        receiptsTotal,
        updatedAt: serverTimestamp(),
      });
  
      setJobs((prev) =>
        prev.map((job) =>
          job.id === selectedJob.id
            ? calculateRow(
                {
                  ...job,
                  receipts: updatedReceipts,
                  receiptsTotal,
                },
                prev
              )
            : job
        )
      );
    }
  
    setBulkMaterialForm(emptyBulkMaterialForm);
    setShowBulkMaterialPopup(false);
    } finally {
      setBusyMessage("");
      setBusyAction("");
    }
  };
  
  const saveMonthlyExpense = async () => {
    setBusyAction("saveMonthlyExpense");
    setBusyMessage("Saving monthly expense...");
    try {
    const amount = numberValue(monthlyExpenseForm.amount);
  
    if (!monthlyExpenseForm.vendorName || !monthlyExpenseForm.billType || !amount || !monthlyExpenseForm.startDate) {
      alert("Please enter vendor, bill type, amount, and start date.");
      return;
    }
  
    const billType =
      monthlyExpenseForm.billType === "Other"
        ? monthlyExpenseForm.billTypeOther || "Other"
        : monthlyExpenseForm.billType;
  
    const payload = {
      vendorId: monthlyExpenseForm.vendorId || "",
      vendorName: monthlyExpenseForm.vendorName || "",
      billType,
      billTypeOther: monthlyExpenseForm.billTypeOther || "",
      description: monthlyExpenseForm.description || "",
      amount,
      frequency: monthlyExpenseForm.frequency || "Monthly",
      startDate: monthlyExpenseForm.startDate,
      source: "JobEstimateSpreadsheetMonthlyExpense",
      updatedAt: serverTimestamp(),
    };
  
    if (editingMonthlyExpense?.id) {
      if (!monthlyExpenseEditEffectiveDate) {
        alert("Please enter the date this change starts.");
        return;
      }
  
      await updateDoc(doc(db, "monthly_expenses", editingMonthlyExpense.id), {
        endDate: getPrevDateKey(monthlyExpenseEditEffectiveDate),
        updatedAt: serverTimestamp(),
      });
  
      const newVersionPayload = {
        ...payload,
        startDate: monthlyExpenseEditEffectiveDate,
        previousExpenseId: editingMonthlyExpense.id,
        parentExpenseId: editingMonthlyExpense.parentExpenseId || editingMonthlyExpense.id,
        createdAt: serverTimestamp(),
      };
  
      const docRef = await addDoc(collection(db, "monthly_expenses"), newVersionPayload);
  
      setMonthlyExpenses((prev) => [
        ...prev.map((expense) =>
          expense.id === editingMonthlyExpense.id
            ? {
                ...expense,
                endDate: getPrevDateKey(monthlyExpenseEditEffectiveDate),
                updatedAt: new Date().toISOString(),
              }
            : expense
        ),
        {
          id: docRef.id,
          ...newVersionPayload,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
      ]);
    } else {
      const docRef = await addDoc(collection(db, "monthly_expenses"), {
        ...payload,
        createdAt: serverTimestamp(),
      });
  
      setMonthlyExpenses((prev) => [
        ...prev,
        {
          id: docRef.id,
          ...payload,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
      ]);
    }
  
    setMonthlyExpenseForm(emptyMonthlyExpenseForm);
    setEditingMonthlyExpense(null);
    setMonthlyExpenseEditEffectiveDate(todayKey());
    setShowMonthlyExpensePopup(false);
    } finally {
      setBusyMessage("");
      setBusyAction("");
    }
  };
  
  const deleteMonthlyExpenseFromDate = async (expense) => {
    if (!expense?.id) return;
  
    const deleteFromDate = window.prompt(
      "Enter the date this expense should stop from. Example: 2026-05-01",
      todayKey()
    );
  
    if (!deleteFromDate) return;
  
    const ok = window.confirm(
      `This will stop this expense starting ${deleteFromDate}. Previous records will stay. Continue?`
    );
  
    if (!ok) return;
    setBusyAction("deleteMonthlyExpenseFromDate");
    setBusyMessage("Updating monthly expense...");
    try {
  
    await updateDoc(doc(db, "monthly_expenses", expense.id), {
      endDate: getPrevDateKey(deleteFromDate),
      deletedFromDate: deleteFromDate,
      deletedMode: "fromDate",
      updatedAt: serverTimestamp(),
    });
  
    setMonthlyExpenses((prev) =>
      prev.map((item) =>
        item.id === expense.id
          ? {
              ...item,
              endDate: getPrevDateKey(deleteFromDate),
              deletedFromDate: deleteFromDate,
              deletedMode: "fromDate",
              updatedAt: new Date().toISOString(),
            }
          : item
      )
    );
    } finally {
      setBusyMessage("");
      setBusyAction("");
    }
  };
  
  const deleteMonthlyExpenseTotal = async (expense) => {
    if (!expense?.id) return;
  
    const ok = window.confirm(
      `Delete this expense completely?\n\n${expense.vendorName || ""} - ${money(
        expense.amount
      )}\n\nThis removes the whole expense from all totals.`
    );
  
    if (!ok) return;
    setBusyAction("deleteMonthlyExpenseTotal");
    setBusyMessage("Deleting monthly expense...");
    try {
  
    await deleteDoc(doc(db, "monthly_expenses", expense.id));
  
    setMonthlyExpenses((prev) => prev.filter((item) => item.id !== expense.id));
    } finally {
      setBusyMessage("");
      setBusyAction("");
    }
  };

  const editMonthlyExpense = (expense) => {
    setEditingMonthlyExpense(expense);
    setMonthlyExpenseEditEffectiveDate(todayKey());
  
    setMonthlyExpenseForm({
      vendorId: expense.vendorId || "",
      vendorName: expense.vendorName || "",
      billType: monthlyBillTypes.includes(expense.billType) ? expense.billType : "Other",
      billTypeOther: monthlyBillTypes.includes(expense.billType) ? "" : expense.billType || "",
      description: expense.description || "",
      amount: expense.amount || "",
      frequency: expense.frequency || "Monthly",
      startDate: expense.startDate || todayKey(),
    });
  
    setShowMonthlyExpensePopup(true);
  };

  const openCommissionPopup = (job) => {
    const existing = Array.isArray(job.staffCommissionOverrides)
      ? job.staffCommissionOverrides
      : [];

    const assignedNames = Array.isArray(job.techAssign)
      ? job.techAssign.map(resolveAssignedName).filter(Boolean)
      : [];

    const rows = assignedNames.map((staffName) => {
      const normalizedName = normalizePersonName(staffName);
      const person = staff.find((item) =>
        [
          item.name,
          item.fullName,
          item.displayName,
          item.employeeName,
          item.techName,
          `${item.firstName || ""} ${item.lastName || ""}`,
          item.email,
        ].some(
          (candidate) => normalizePersonName(candidate) === normalizedName
        )
      );

      const saved = existing.find((item) => {
        const sameId =
          person?.id &&
          item.staffId &&
          String(person.id) === String(item.staffId);
        const sameName =
          normalizePersonName(item.staffName || "") === normalizedName;
        return sameId || sameName;
      });

      return {
        staffId: person?.id || "",
        staffName,
        defaultMethod: person?.payCalculationMethod || "hourly_only",
        defaultRate: person?.commissionRate ?? "",
        payCalculationMethod: saved?.payCalculationMethod || "",
        commissionRate:
          saved?.commissionRate !== undefined &&
          saved?.commissionRate !== null
            ? saved.commissionRate
            : "",
      };
    });

    setCommissionJob(job);
    setCommissionDraft(rows);
    setShowCommissionPopup(true);
  };

  const saveCommissionOverrides = async () => {
    if (!commissionJob?.id) return;

    setBusyAction("saveCommissionOverrides");
    setBusyMessage("Saving staff commission overrides...");

    try {
      const cleaned = commissionDraft
        .filter(
          (item) =>
            item.payCalculationMethod ||
            (item.commissionRate !== "" &&
              item.commissionRate !== null &&
              item.commissionRate !== undefined)
        )
        .map((item) => ({
          staffId: item.staffId || "",
          staffName: item.staffName || "",
          payCalculationMethod: item.payCalculationMethod || "",
          commissionRate:
            item.commissionRate === "" ? "" : numberValue(item.commissionRate),
        }));

      if (commissionJob.__isRecurringOccurrence) {
        await setDoc(
          doc(db, "recurring_job_occurrences", commissionJob.occurrenceKey),
          {
            originalJobId: commissionJob.originalJobId,
            occurrenceKey: commissionJob.occurrenceKey,
            occurrenceDate:
              commissionJob.occurrenceDate || commissionJob.startDate,
            staffCommissionOverrides: cleaned,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );

        setRecurringOverrides((prev) => ({
          ...prev,
          [commissionJob.occurrenceKey]: {
            ...prev[commissionJob.occurrenceKey],
            staffCommissionOverrides: cleaned,
          },
        }));
      } else {
        await updateDoc(doc(db, "jobs", commissionJob.id), {
          staffCommissionOverrides: cleaned,
          updatedAt: serverTimestamp(),
        });

        setJobs((prev) =>
          prev.map((item) =>
            item.id === commissionJob.id
              ? calculateRow(
                  { ...item, staffCommissionOverrides: cleaned },
                  prev
                )
              : item
          )
        );
      }

      setShowCommissionPopup(false);
      setCommissionJob(null);
      setCommissionDraft([]);
    } finally {
      setBusyMessage("");
      setBusyAction("");
    }
  };

  const openSubPayPopup = (job) => {
    const lines = Array.isArray(job.subcontractorPay) ? job.subcontractorPay : [];
    const legacySubPay = numberValue(job.subPay);

    setSubPayJob(job);
    setSubPayOtherLines({});
    setSubPayDraft(
      lines.length > 0
        ? JSON.parse(JSON.stringify(lines))
        : [
            {
              subcontractorName: "",
              // Older jobs can have a Sub Pay amount with no line behind it.
              // Keep that amount instead of wiping it to $0 on save.
              basePay: legacySubPay > 0 ? legacySubPay : "",
              changeOrders: [],
              __legacyAmount: legacySubPay > 0,
            },
          ]
    );
    setShowSubPayPopup(true);
  };

  const cleanSubPayLines = (lines = []) =>
    lines.map((item) => {
      const cleaned = {
        subcontractorName: String(item.subcontractorName || "").trim(),
        basePay: numberValue(item.basePay),
        changeOrders: Array.isArray(item.changeOrders)
          ? item.changeOrders.map((co) => ({
              description: co.description || "",
              amount: numberValue(co.amount),
              date: co.date || "",
            }))
          : [],
      };
      // Additive: remember which roster record the line belongs to.
      if (item.subcontractorId) cleaned.subcontractorId = String(item.subcontractorId);
      return cleaned;
    });

  const subPayLineTotal = (line = {}) =>
    numberValue(line.basePay) +
    (Array.isArray(line.changeOrders) ? line.changeOrders : []).reduce(
      (sum, co) => sum + numberValue(co.amount),
      0
    );

  // The name to put in Tech Assign so it matches the tech dropdown when possible.
  const techNameForTarget = (target) => {
    if (!target) return "";
    const staffName = !target.sub?.__freeText && target.sub?.name ? String(target.sub.name).trim() : "";
    return staffName || target.subName || "";
  };

  /**
   * Writes a job's sub pay lines (and any auto-added tech assignments) to
   * Firestore and to local state. Works for regular jobs and for a single
   * recurring date. Returns the saved lines.
   */
  const persistJobSubPay = async (job, lines, techNamesToAdd = []) => {
    const cleaned = cleanSubPayLines(lines);
    const subPayTotal = calculateSubPayTotal(cleaned);

    const isOccurrence = Boolean(job.__isRecurringOccurrence);
    const liveJob = isOccurrence ? job : jobs.find((j) => j.id === job.id) || job;
    const currentTechs = (Array.isArray(liveJob.techAssign) ? liveJob.techAssign : []).filter(Boolean);
    const nextTechs = [...currentTechs];

    techNamesToAdd.filter(Boolean).forEach((name) => {
      const already = techAssignMatches(
        nextTechs.map(resolveAssignedName),
        name,
        bucketKeyForName
      );
      if (!already) nextTechs.push(name);
    });

    const techsChanged = nextTechs.length !== currentTechs.length;
    const techFields = techsChanged
      ? { techAssign: nextTechs, estimatedLabor: nextTechs.length }
      : {};

    if (isOccurrence) {
      await setDoc(
        doc(db, "recurring_job_occurrences", job.occurrenceKey),
        {
          originalJobId: job.originalJobId,
          occurrenceKey: job.occurrenceKey,
          occurrenceDate: job.occurrenceDate || job.startDate,
          subcontractorPay: cleaned,
          subPay: subPayTotal,
          ...techFields,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      setRecurringOverrides((prev) => ({
        ...prev,
        [job.occurrenceKey]: {
          ...prev[job.occurrenceKey],
          originalJobId: job.originalJobId,
          occurrenceKey: job.occurrenceKey,
          occurrenceDate: job.occurrenceDate || job.startDate,
          subcontractorPay: cleaned,
          subPay: subPayTotal,
          ...techFields,
          updatedAt: new Date().toISOString(),
        },
      }));
    } else {
      await updateDoc(doc(db, "jobs", job.id), {
        subcontractorPay: cleaned,
        subPay: subPayTotal,
        ...techFields,
        updatedAt: serverTimestamp(),
      });

      setJobs((prev) =>
        prev.map((item) => {
          if (item.id !== job.id) return item;
          return calculateRow(
            {
              ...item,
              subcontractorPay: cleaned,
              subPay: subPayTotal,
              ...techFields,
            },
            prev
          );
        })
      );
    }

    setSubPayJob((prev) =>
      prev && prev.id === job.id
        ? { ...prev, subcontractorPay: cleaned, subPay: subPayTotal, ...techFields }
        : prev
    );

    return { cleaned, subPayTotal, addedTechs: nextTechs.slice(currentTechs.length) };
  };

  const saveSubPay = async () => {
    if (!subPayJob?.id) return;

    const unnamed = subPayDraft.filter(
      (line) => !String(line.subcontractorName || "").trim() && subPayLineTotal(line) > 0
    );
    if (unnamed.length) {
      const ok = window.confirm(
        "A Sub Pay amount has no subcontractor picked, so it won't show in Subcontractor Payables.\n\nOK = Save anyway\nCancel = Go back and pick one"
      );
      if (!ok) return;
    }

    setBusyAction("saveSubPay");
    setBusyMessage("Saving sub pay...");
    try {
      // Anyone with a sub pay line on this job is working it: add them to Tech Assign.
      const techNames = subPayDraft
        .filter((line) => String(line.subcontractorName || "").trim())
        .map((line) => {
          const target = findPayableTarget(line.subcontractorName, line.subcontractorId);
          return target ? techNameForTarget(target) : String(line.subcontractorName).trim();
        });

      await persistJobSubPay(subPayJob, subPayDraft, techNames);

      setShowSubPayPopup(false);
      setSubPayJob(null);
      setSubPayDraft([]);
    } catch (error) {
      console.error("Sub pay did not save:", error);
      alert("Sub pay did not save. Check your connection and try again.");
    } finally {
      setBusyMessage("");
      setBusyAction("");
    }
  };


  const saveReceipt = async () => {
    if (!receiptJob?.id) return;
    setBusyAction("saveReceipt");
    setBusyMessage(editingReceiptIndex !== null ? "Updating receipt..." : "Saving receipt...");
  
    if (!receiptAmount && !receiptFile && editingReceiptIndex === null) {
      alert("Please add a receipt amount or upload a receipt image.");
      return;
    }
  
    setSavingReceipt(true);
  
    try {
      let fileURL = receiptJob.receipts?.[editingReceiptIndex]?.fileURL || "";
      let fileName = receiptJob.receipts?.[editingReceiptIndex]?.fileName || "";
  
      if (receiptFile) {
        const storage = getStorage();
        const fileRef = ref(
          storage,
          `jobReceipts/${receiptJob.id}/${Date.now()}_${receiptFile.name}`
        );
  
        await uploadBytes(fileRef, receiptFile);
        fileURL = await getDownloadURL(fileRef);
        fileName = receiptFile.name;
      }
  
      const receiptData = {
        amount: numberValue(receiptAmount),
        note: receiptNote || "",
        fileURL,
        fileName,
        uploadedAt:
          editingReceiptIndex !== null
            ? receiptJob.receipts?.[editingReceiptIndex]?.uploadedAt || new Date().toISOString()
            : new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
  
      let updatedReceipts = Array.isArray(receiptJob.receipts)
        ? [...receiptJob.receipts]
        : [];
  
      if (editingReceiptIndex !== null) {
        updatedReceipts[editingReceiptIndex] = receiptData;
      } else {
        updatedReceipts.push(receiptData);
      }
  
      const receiptsTotal = updatedReceipts.reduce(
        (sum, receipt) => sum + numberValue(receipt.amount),
        0
      );

      if (receiptJob.__isRecurringOccurrence) {
        await setDoc(
          doc(db, "recurring_job_occurrences", receiptJob.occurrenceKey),
          {
            originalJobId: receiptJob.originalJobId,
            occurrenceKey: receiptJob.occurrenceKey,
            occurrenceDate: receiptJob.occurrenceDate || receiptJob.startDate,
            receipts: updatedReceipts,
            receiptsTotal,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );

        setRecurringOverrides((prev) => ({
          ...prev,
          [receiptJob.occurrenceKey]: {
            ...prev[receiptJob.occurrenceKey],
            receipts: updatedReceipts,
            receiptsTotal,
            updatedAt: new Date().toISOString(),
          },
        }));
      } else {
        await updateDoc(doc(db, "jobs", receiptJob.id), {
          receipts: updatedReceipts,
          receiptsTotal,
          updatedAt: serverTimestamp(),
        });
    
        setJobs((prev) =>
          prev.map((job) =>
            job.id === receiptJob.id
              ? { ...job, receipts: updatedReceipts, receiptsTotal }
              : job
          )
        );
      }
  
      setReceiptAmount("");
      setReceiptNote("");
      setReceiptFile(null);
      setReceiptJob(null);
      setEditingReceiptIndex(null);
      setShowReceiptPopup(false);
    } catch (err) {
      console.error("Error saving receipt:", err);
      alert("Could not save receipt.");
    } finally {
      setSavingReceipt(false);
      setBusyMessage("");
      setBusyAction("");
    }
  };

  const deleteReceipt = async (job, receiptIndex) => {
    if (!job?.id) return;
  
    const ok = window.confirm("Delete this receipt?");
    if (!ok) return;
    setBusyAction("deleteReceipt");
    setBusyMessage("Deleting receipt...");
    try {
  
    const updatedReceipts = [...(job.receipts || [])];
    updatedReceipts.splice(receiptIndex, 1);
  
    const receiptsTotal = updatedReceipts.reduce(
      (sum, receipt) => sum + numberValue(receipt.amount),
      0
    );

    if (job.__isRecurringOccurrence) {
      await setDoc(
        doc(db, "recurring_job_occurrences", job.occurrenceKey),
        {
          originalJobId: job.originalJobId,
          occurrenceKey: job.occurrenceKey,
          occurrenceDate: job.occurrenceDate || job.startDate,
          receipts: updatedReceipts,
          receiptsTotal,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      setRecurringOverrides((prev) => ({
        ...prev,
        [job.occurrenceKey]: {
          ...prev[job.occurrenceKey],
          receipts: updatedReceipts,
          receiptsTotal,
          updatedAt: new Date().toISOString(),
        },
      }));
    } else {
      await updateDoc(doc(db, "jobs", job.id), {
        receipts: updatedReceipts,
        receiptsTotal,
        updatedAt: serverTimestamp(),
      });
    
      setJobs((prev) =>
        prev.map((j) =>
          j.id === job.id
            ? { ...j, receipts: updatedReceipts, receiptsTotal }
            : j
        )
      );
    }
    } finally {
      setBusyMessage("");
      setBusyAction("");
    }
  };

  // Helper

  const jobSort = (a, b) =>
    String(a.wo || "").localeCompare(String(b.wo || ""), undefined, {
      numeric: true,
    });

  const associationTree = useMemo(() => {
    const byId = {};
    const childrenMap = {};
    const parentMap = {};

    jobs.forEach((job) => {
      byId[job.id] = job;
      childrenMap[job.id] = [];
    });

    const addChild = (parentId, childId) => {
      if (!parentId || !childId || parentId === childId || !byId[parentId] || !byId[childId]) return;
      if (!childrenMap[parentId]) childrenMap[parentId] = [];
      if (!childrenMap[parentId].includes(childId)) childrenMap[parentId].push(childId);
      if (!parentMap[childId]) parentMap[childId] = parentId;
    };

    // New tree behavior: parentJobId and associatedChildJobIds create true branches.
    jobs.forEach((job) => {
      if (job.parentJobId) addChild(job.parentJobId, job.id);
      (Array.isArray(job.associatedChildJobIds) ? job.associatedChildJobIds : []).forEach((childId) => {
        addChild(job.id, childId);
      });
    });

    // Backward support for older flat associatedJobIds.
    // If old records have two jobs linked to each other, the lower WO becomes the parent so it does not loop forever.
    const legacyPairs = new Set();
    jobs.forEach((job) => {
      (Array.isArray(job.associatedJobIds) ? job.associatedJobIds : []).forEach((linkedId) => {
        if (!byId[linkedId] || job.id === linkedId) return;
        const pairKey = [job.id, linkedId].sort().join("|");
        if (legacyPairs.has(pairKey)) return;
        legacyPairs.add(pairKey);
        const sorted = [job, byId[linkedId]].sort(jobSort);
        addChild(sorted[0].id, sorted[1].id);
      });
    });

    // Automatic continued-job grouping: WO 3000, 3000-2, 3000-3 (and proposal-WO equivalents)
    // become one branch with the earliest/lower WO as the master.
    const jobsByBaseWO = {};
    jobs.forEach((job) => {
      const baseWO = getBaseWO(job.wo);
      if (!baseWO) return;
      if (!jobsByBaseWO[baseWO]) jobsByBaseWO[baseWO] = [];
      jobsByBaseWO[baseWO].push(job);
    });

    Object.values(jobsByBaseWO).forEach((group) => {
      if (group.length < 2) return;
      const sortedGroup = [...group].sort(jobSort);
      const master = sortedGroup[0];
      sortedGroup.slice(1).forEach((child) => addChild(master.id, child.id));
    });

    Object.keys(childrenMap).forEach((id) => {
      childrenMap[id] = childrenMap[id]
        .filter((childId) => childId !== id && byId[childId])
        .sort((a, b) => jobSort(byId[a], byId[b]));
    });

    const getRootId = (id) => {
      let current = id;
      const seen = new Set();
      while (parentMap[current] && !seen.has(current)) {
        seen.add(current);
        current = parentMap[current];
      }
      return current;
    };

    const getDescendantIds = (id) => {
      const result = [];
      const seen = new Set([id]);
      const walk = (parentId) => {
        (childrenMap[parentId] || []).forEach((childId) => {
          if (seen.has(childId)) return;
          seen.add(childId);
          result.push(childId);
          walk(childId);
        });
      };
      walk(id);
      return result;
    };

    const getDepth = (id) => {
      let depth = 0;
      let current = id;
      const seen = new Set();
      while (parentMap[current] && !seen.has(current)) {
        seen.add(current);
        depth += 1;
        current = parentMap[current];
      }
      return depth;
    };

    return { byId, childrenMap, parentMap, getRootId, getDescendantIds, getDepth };
  }, [jobs]);

  const getAssociatedChildren = (job) => {
    if (!job?.id) return [];
    return (associationTree.childrenMap[job.id] || [])
      .map((id) => associationTree.byId[id])
      .filter(Boolean);
  };

  const getAssociatedDescendants = (job) => {
    if (!job?.id) return [];
    return associationTree.getDescendantIds(job.id)
      .map((id) => associationTree.byId[id])
      .filter(Boolean);
  };

  const getJobTallyUnits = (job) => {
    if (!job) return [];

    // Recurring group controller: the first visible row is also an editable date row,
    // but its branch totals still need to include every date in the selected range.
    if (job.__isRecurringMain && job.__recurringSourceJob) {
      const dates = Array.isArray(job.__recurringDates)
        ? job.__recurringDates
        : generateRecurringDates(job.__recurringSourceJob);

      if (!dates.length) return [job];
      return dates.map((dateKey) => buildOccurrenceJob(job.__recurringSourceJob, dateKey));
    }

    // A rendered recurring occurrence is already one date/child row.
    if (job.__isRecurringOccurrence) return [job];

    // Recurring masters tally each occurrence inside the selected date range.
    if (isRecurringJob(job)) {
      const dates = Array.isArray(job.__recurringDates)
        ? job.__recurringDates
        : generateRecurringDates(job);

      if (!dates.length) return [job];
      return dates.map((dateKey) => buildOccurrenceJob(job, dateKey));
    }

    return [job];
  };

  const getWOGroupJobs = (job) => {
    if (!job?.id) return job ? [job] : [];
    return [job, ...getAssociatedDescendants(job)].flatMap(getJobTallyUnits);
  };

  const getWOGroupController = (job) => {
    if (!job?.id) return job;
    const rootId = associationTree.getRootId(job.id);
    return associationTree.byId[rootId] || job;
  };
  

  const getAssociationKey = (job) => getWOGroupController(job)?.id || job?.id || "";


  const getAssociatedJobCount = (job) => getAssociatedDescendants(job).length;

  const getAssociationDepth = (job) => (job?.id ? associationTree.getDepth(job.id) : 0);

  const isAssociationBranchOpen = (job) => !!expandedAssociatedJobs[job?.id];

  const hasBranchTotals = (job) => getWOGroupJobs(job).length > 1;

  const branchOwnLabelStyle = { fontSize: 12, color: "#64748b", marginTop: 4 };

  const getRowAssociatedChildren = (job) => {
    if (Array.isArray(job?.__associatedChildren)) return job.__associatedChildren;
    return getAssociatedChildren(job);
  };

  const getRowControllerWO = (job) =>
    job?.__recurringSourceJob?.wo || getWOGroupController(job)?.wo || job?.wo || "";

  const groupedMaterialTotal = (job) =>
    getWOGroupJobs(job).reduce((sum, j) => sum + numberValue(j.estimatedMaterial), 0);
  
  const groupedSubPayTotal = (job) =>
    getWOGroupJobs(job).reduce((sum, j) => sum + numberValue(j.subPay), 0);
  
  const groupedLaborCount = (job) =>
    getWOGroupJobs(job).reduce(
      (sum, j) => sum + (Array.isArray(j.techAssign) ? j.techAssign.filter(Boolean).length : 0),
      0
    );
  
  const groupedPaymentTotal = (job) =>
    getWOGroupJobs(job).reduce((sum, j) => sum + singleJobPaymentTotal(j), 0);
  
  const groupedEstimatedTotal = (job) => {
    return getWOGroupJobs(job).reduce((sum, j) => sum + numberValue(j.estimatedTotal), 0);
  };
  
  const groupedReceiptsTotal = (job) => {
    return getWOGroupJobs(job).reduce(
      (sum, j) => sum + numberValue(j.receiptsTotal),
      0
    );
  };
  
  const groupedLaborCost = (job) => {
    return getWOGroupJobs(job).reduce(
      (sum, j) => sum + numberValue(calculateRow(j).estimatedLaborCost),
      0
    );
  };
  
  const groupedExpensesTotal = (job) => {
    return getWOGroupJobs(job).reduce(
      (sum, j) => sum + numberValue(calculateRow(j).totalExpenses),
      0
    );
  };

  const groupedWarrantyCost = (job) => {
    return getWOGroupJobs(job).reduce(
      (sum, j) => sum + numberValue(calculateRow(j).warrantyCost),
      0
    );
  };
  
  const groupedActualDailyPay = (job) =>
    getWOGroupJobs(job).reduce(
      (sum, j) => sum + numberValue(calculateRow(j).actualDailyPay),
      0
    );

  const groupedInvoicedTotal = (job) =>
    getWOGroupJobs(job).reduce(
      (sum, j) => sum + numberValue(calculateRow(j).invoicedTotal),
      0
    );

  // Revenue the branch is billed for: invoice totals where invoiced, else
  // estimates (same rule as each row's profit).
  const groupedRecognizedIncome = (job) =>
    getWOGroupJobs(job).reduce(
      (sum, j) => sum + numberValue(calculateRow(j).recognizedIncome),
      0
    );

  const groupedProfitTotal = (job) => {
    const group = getWOGroupJobs(job);
    if (group.length && group.every((j) => j.isWarranty === true)) return 0;
    return groupedRecognizedIncome(job) - groupedExpensesTotal(job);
  };

  const singleJobPaymentTotal = (job) => {
    return Array.isArray(job.payments)
      ? job.payments.reduce((sum, p) => sum + numberValue(p.amount), 0)
      : 0;
  };

  const paymentTotal = (job) => groupedPaymentTotal(job);
  
    const getCheckRecordKey = (check = {}) => {
      if (check.id) return `id:${check.id}`;
      if (check.depositId) return `deposit:${check.depositId}`;
      if (check.checkGroupId && check.invoiceNumber) {
        return `group:${check.checkGroupId}:${check.invoiceNumber}`;
      }
      return [
        check.checkNumber || "",
        check.date || "",
        check.invoiceNumber || "",
        numberValue(check.amount),
      ].join("|");
    };

    const normalizeCheckRecord = (check = {}, fallback = {}) => ({
      ...check,
      id: check.id || check.depositId || check.id || "",
      amount: numberValue(check.amount),
      checkNumber: check.checkNumber || fallback.checkNumber || "",
      paymentType: check.paymentType || fallback.paymentType || "Other",
      invoiceNumber: String(check.invoiceNumber || fallback.invoiceNumber || "").trim(),
      invoiceNumber2: check.invoiceNumber2 || fallback.invoiceNumber2 || "",
      company: check.company || fallback.company || "",
      date: check.date || fallback.date || "",
      description: check.description || fallback.description || "",
      splitCheck: check.splitCheck === true || fallback.splitCheck === true,
      checkGroupId: check.checkGroupId || fallback.checkGroupId || "",
      depositId: check.depositId || check.id || fallback.depositId || "",
      invoiceOnly: check.invoiceOnly === true || check.splitInvoiceOnly === true,
      splitInvoiceOnly: check.splitInvoiceOnly === true,
    });

    const addUniqueCheckRecord = (found, check = {}, fallback = {}) => {
      const normalized = normalizeCheckRecord(check, fallback);
      if (!normalized.invoiceNumber && !fallback.invoiceNumber) return;
      if (normalized.invoiceOnly || normalized.splitInvoiceOnly) return;
      const key = getCheckRecordKey(normalized);
      if (!found.some((existing) => getCheckRecordKey(existing) === key)) {
        found.push(normalized);
      }
    };

    const depositsByBaseWO = useMemo(() => {
      const map = {};
    
      deposits.forEach((deposit) => {
        const baseWO = getBaseWO(deposit.invoiceNumber || "");
        const directWO = String(deposit.invoiceNumber || "").trim();
        const keys = [baseWO, directWO].filter(Boolean);

        keys.forEach((key) => {
          if (!map[key]) map[key] = [];
          if (!map[key].some((existing) => existing.id === deposit.id)) {
            map[key].push(deposit);
          }
        });
      });
    
      return map;
    }, [deposits]);
    
    const getSingleJobDeposits = (job) => {
      const directWO = String(job?.wo || "").trim();
      const keys = job?.__isRecurringOccurrence
        ? [directWO].filter(Boolean)
        : [getBaseWO(job?.wo), directWO].filter(Boolean);
      const found = [];

      if (job?.__isRecurringOccurrence) {
        // Priority recurring visit. "Received" is what the shared paid rule
        // (same as Approved Invoices / Deposits) puts on this visit's
        // invoices. Checks recorded on this visit are listed so they can be
        // edited or deleted, but their money is already inside that amount.
        const rowKey = rowKeyForVisit(job.originalJobId, job.canonicalOccurrenceKey);
        (recurringPayments.directByRow[rowKey] || []).forEach((deposit) => {
          addUniqueCheckRecord(found, { ...deposit, __listOnly: true }, { invoiceNumber: job.masterWO || directWO });
        });
        const paidOnInvoices = numberValue(recurringPayments.paidByRow[rowKey]);
        if (paidOnInvoices > 0) {
          found.push({
            id: `applied:${rowKey}`,
            amount: paidOnInvoices,
            paymentType: "Paid on this visit's invoice",
            company: "",
            checkNumber: "",
            invoiceNumber: job.masterWO || directWO,
            description: `Payments on WO ${job.masterWO || ""} applied to this visit's invoice`,
            __applied: true,
          });
        }
      } else {
        keys.forEach((key) => {
          (depositsByBaseWO[key] || []).forEach((deposit) => {
            addUniqueCheckRecord(found, deposit, { invoiceNumber: key });
          });
        });
      }

      // Split checks also get saved onto the job/recurring occurrence as checksReceived.
      // Read those back too, so every split WO/date immediately shows Check Received
      // even before a full page reload. Dedupe by depositId/checkGroupId so the same
      // split check is never counted twice when the deposit record is also loaded.
      (Array.isArray(job?.checksReceived) ? job.checksReceived : []).forEach((check) => {
        const checkInvoice = String(check.invoiceNumber || directWO || "").trim();
        const matchesThisJob = !directWO || !checkInvoice || checkInvoice === directWO || keys.includes(checkInvoice);
        if (!matchesThisJob) return;
        addUniqueCheckRecord(
          found,
          job?.__isRecurringOccurrence ? { ...check, __listOnly: true } : check,
          { invoiceNumber: directWO }
        );
      });

      return found;
    };

    const getJobDeposits = (job) => {
      const found = [];
      getWOGroupJobs(job).forEach((groupJob) => {
        getSingleJobDeposits(groupJob).forEach((deposit) => {
          if (deposit.__applied) {
            if (!found.some((existing) => existing.id === deposit.id)) found.push(deposit);
            return;
          }
          addUniqueCheckRecord(found, deposit, { invoiceNumber: groupJob.wo });
        });
      });
      // WO payments on a recurring job that are more than its invoiced visits
      // so far: shown once, on the job's main row.
      const seriesId = job?.__isRecurringMain
        ? job.originalJobId
        : isRecurringJob(job) && !job?.__isRecurringOccurrence
        ? job.id
        : "";
      if (seriesId) {
        const unapplied = numberValue(recurringPayments.unappliedByJob[seriesId]);
        if (unapplied > 0) {
          found.push({
            id: `unapplied:${seriesId}`,
            amount: unapplied,
            paymentType: "Unapplied payment",
            company: "",
            checkNumber: "",
            invoiceNumber: job.masterWO || "",
            description: `Paid on WO ${job.masterWO || ""} but not yet matched to an invoiced visit`,
            __applied: true,
          });
        }
      }
      return found;
    };
      
      // List-only rows (checks recorded on a recurring visit) are already
      // inside that visit's "Paid on this visit's invoice" amount.
      const checksTotal = (job) =>
        getJobDeposits(job)
          .filter((check) => !check.__listOnly)
          .reduce((sum, check) => sum + numberValue(check.amount), 0);

      // What the customer owes for the branch: invoice totals where a visit /
      // job was invoiced, otherwise the estimate.
      const contractTotalForJob = (job) => groupedRecognizedIncome(job);

      const checkBalanceDue = (job) =>
        Math.max(contractTotalForJob(job) - checksTotal(job), 0);

      const checkPaymentStatus = (job) => {
        const contractTotal = contractTotalForJob(job);
        const receivedTotal = checksTotal(job);

        if (job?.status === "Paid In Full") {
          return { label: "Paid In Full", color: "#16a34a", flag: false };
        }

        if (!contractTotal) return { label: "No Estimate", color: "#64748b", flag: false };
        if (receivedTotal >= contractTotal) return { label: "Paid In Full", color: "#16a34a", flag: false };
        if (receivedTotal <= 0) return { label: "No Checks Received", color: "#dc2626", flag: true };
        return { label: "Balance Due", color: "#f97316", flag: true };
      };

      const markZeroDollarJobPaidInFull = async (job) => {
        if (!job?.id) return;

        const ownTotal = numberValue(job.estimatedTotal);
        const branchTotal = contractTotalForJob(job);

        if (ownTotal !== 0 || branchTotal !== 0) {
          alert("This button is only for jobs/linked branches with a $0 estimate and no more money needed.");
          return;
        }

        const ok = window.confirm(`Mark WO ${job.wo || ""} as Paid In Full?`);
        if (!ok) return;

        const nowIso = new Date().toISOString();
        const targetId = job.originalJobId || job.id;

        setBusyAction(`markPaid-${job.id}`);
        setBusyMessage("Marking $0 job paid in full...");

        try {
          if (job.__isRecurringOccurrence && job.occurrenceKey) {
            await setDoc(
              doc(db, "recurring_job_occurrences", job.occurrenceKey),
              {
                originalJobId: job.originalJobId,
                occurrenceKey: job.occurrenceKey,
                occurrenceDate: job.occurrenceDate || job.startDate || "",
                status: "Paid In Full",
                paidInFullMarkedAt: nowIso,
                updatedAt: serverTimestamp(),
              },
              { merge: true }
            );

            setRecurringOverrides((prev) => ({
              ...prev,
              [job.occurrenceKey]: {
                ...prev[job.occurrenceKey],
                status: "Paid In Full",
                paidInFullMarkedAt: nowIso,
                updatedAt: nowIso,
              },
            }));
          } else {
            await updateDoc(doc(db, "jobs", targetId), {
              status: "Paid In Full",
              paidInFullMarkedAt: nowIso,
              updatedAt: serverTimestamp(),
            });

            setJobs((prev) =>
              prev.map((item) =>
                item.id === targetId
                  ? { ...item, status: "Paid In Full", paidInFullMarkedAt: nowIso }
                  : item
              )
            );
          }
        } finally {
          setBusyMessage("");
          setBusyAction("");
        }
      };

      const openAssociatePopup = (job) => {
        setAssociateJob(job);
        setAssociateTargetJobId("");
        setAssociateTargetJobIds([]);
        setAssociateWOInput("");
        setShowAssociatePopup(true);
      };

      const getTypedWOTokens = () => {
        return String(associateWOInput || "")
          .split(/[\s,;]+/)
          .map((item) => item.trim().toLowerCase())
          .filter(Boolean);
      };

      const findAssociationTargetsFromWO = () => {
        const typedTokens = getTypedWOTokens();
        if (!typedTokens.length) return [];

        const matches = [];
        typedTokens.forEach((typedWO) => {
          const match = candidateChildJobs.find((job) => {
            const wo = String(job.wo || "").trim().toLowerCase();
            const baseWO = getBaseWO(job.wo || "").toLowerCase();
            return wo === typedWO || baseWO === typedWO;
          });

          if (match && !matches.some((item) => item.id === match.id)) {
            matches.push(match);
          }
        });

        return matches;
      };

      const candidateChildJobs = useMemo(() => {
        return jobs
          .filter((job) => {
            if (!associateJob?.id || job.id === associateJob.id) return false;
            if (job.parentJobId && job.parentJobId !== associateJob.id) return false;
            if (getAssociatedDescendants(job).some((child) => child.id === associateJob.id)) return false;
            return true;
          })
          .sort(jobSort);
      }, [jobs, associateJob, associationTree]);

      const getSelectedAssociationTargetIds = () => {
        const typedTargets = findAssociationTargetsFromWO();
        return Array.from(
          new Set([
            ...associateTargetJobIds,
            associateTargetJobId,
            ...typedTargets.map((job) => job.id),
          ].filter(Boolean))
        );
      };

      const associateJobs = async () => {
        const finalTargetJobIds = getSelectedAssociationTargetIds().filter(
          (id) => associateJob?.id && id !== associateJob.id
        );

        if (!associateJob?.id || finalTargetJobIds.length === 0) return;

        const targetJobs = finalTargetJobIds
          .map((id) => jobs.find((job) => job.id === id))
          .filter(Boolean);

        if (!targetJobs.length) return;

        const loopJob = targetJobs.find((targetJob) =>
          getAssociatedDescendants(targetJob).some((child) => child.id === associateJob.id)
        );

        if (loopJob) {
          alert(`WO ${loopJob.wo || ""} would create a loop. Choose a different child job.`);
          return;
        }

        try {
          setBusyAction("associateJobs");
          setBusyMessage(
            targetJobs.length === 1 ? "Associating job..." : `Associating ${targetJobs.length} jobs...`
          );

          const targetIds = targetJobs.map((job) => job.id);
          const sourceChildren = Array.from(
            new Set([...(associateJob.associatedChildJobIds || []), ...targetIds])
          );
          const sourceLegacyLinks = (associateJob.associatedJobIds || []).filter(
            (id) => !targetIds.includes(id)
          );

          await Promise.all([
            updateDoc(doc(db, "jobs", associateJob.id), {
              associatedChildJobIds: sourceChildren,
              associatedJobIds: sourceLegacyLinks,
              updatedAt: serverTimestamp(),
            }),
            ...targetJobs.map((targetJob) => {
              const targetLegacyLinks = (targetJob.associatedJobIds || []).filter(
                (id) => id !== associateJob.id
              );

              return updateDoc(doc(db, "jobs", targetJob.id), {
                parentJobId: associateJob.id,
                associatedJobIds: targetLegacyLinks,
                updatedAt: serverTimestamp(),
              });
            }),
          ]);

          setJobs((prev) =>
            prev.map((job) => {
              if (job.id === associateJob.id) {
                return {
                  ...job,
                  associatedChildJobIds: sourceChildren,
                  associatedJobIds: sourceLegacyLinks,
                };
              }

              if (targetIds.includes(job.id)) {
                return {
                  ...job,
                  parentJobId: associateJob.id,
                  associatedJobIds: (job.associatedJobIds || []).filter((id) => id !== associateJob.id),
                };
              }

              return job;
            })
          );

          setExpandedAssociatedJobs((prev) => ({
            ...prev,
            [associateJob.id]: true,
          }));
          setAssociateJob(null);
          setAssociateTargetJobId("");
          setAssociateTargetJobIds([]);
          setAssociateWOInput("");
          setShowAssociatePopup(false);
        } finally {
          setBusyMessage("");
          setBusyAction("");
        }
      };

      const removeJobAssociation = async (job, targetJobId) => {
        if (!job?.id || !targetJobId) return;
        const targetJob = jobs.find((item) => item.id === targetJobId);
        if (!targetJob?.id) return;

        const ok = window.confirm(`Remove association between WO ${job.wo || ""} and WO ${targetJob.wo || ""}?`);
        if (!ok) return;

        try {
          setBusyAction("removeAssociation");
          setBusyMessage("Removing association...");
          const sourceChildren = (job.associatedChildJobIds || []).filter((id) => id !== targetJob.id);
          const targetParent = targetJob.parentJobId === job.id ? "" : targetJob.parentJobId || "";
          const sourceLegacyLinks = (job.associatedJobIds || []).filter((id) => id !== targetJob.id);
          const targetLegacyLinks = (targetJob.associatedJobIds || []).filter((id) => id !== job.id);

          await Promise.all([
            updateDoc(doc(db, "jobs", job.id), {
              associatedChildJobIds: sourceChildren,
              associatedJobIds: sourceLegacyLinks,
              updatedAt: serverTimestamp(),
            }),
            updateDoc(doc(db, "jobs", targetJob.id), {
              parentJobId: targetParent,
              associatedJobIds: targetLegacyLinks,
              updatedAt: serverTimestamp(),
            }),
          ]);

          setJobs((prev) =>
            prev.map((item) => {
              if (item.id === job.id) {
                return { ...item, associatedChildJobIds: sourceChildren, associatedJobIds: sourceLegacyLinks };
              }
              if (item.id === targetJob.id) {
                return { ...item, parentJobId: targetParent, associatedJobIds: targetLegacyLinks };
              }
              return item;
            })
          );
        } finally {
          setBusyMessage("");
          setBusyAction("");
        }
      };

      const savePayment = async () => {
        if (!paymentJob?.id || !paymentAmount) return;
        setBusyAction("savePayment");
        setBusyMessage(editingPaymentIndex !== null ? "Updating payment..." : "Saving payment...");
        try {
      
        const newPayment = {
          type: paymentType,
          amount: numberValue(paymentAmount),
          date: paymentDate || "",
          createdAt:
            editingPaymentIndex !== null
              ? paymentJob.payments?.[editingPaymentIndex]?.createdAt || new Date().toISOString()
              : new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
      
        let updatedPayments = Array.isArray(paymentJob.payments)
          ? [...paymentJob.payments]
          : [];
      
        if (editingPaymentIndex !== null) {
          updatedPayments[editingPaymentIndex] = newPayment;
        } else {
          updatedPayments.push(newPayment);
        }
      
        const newEstimatedTotal = numberValue(paymentJob.estimatedTotal);
      
        const jobKey = String(paymentJob.wo || paymentJob.id);
      
        if (paymentJob.__isRecurringOccurrence) {
          await setDoc(
            doc(db, "recurring_job_occurrences", paymentJob.occurrenceKey),
            {
              originalJobId: paymentJob.originalJobId,
              occurrenceKey: paymentJob.occurrenceKey,
              occurrenceDate: paymentJob.occurrenceDate || paymentJob.startDate,
              payments: updatedPayments,
              estimatedTotal: newEstimatedTotal,
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );
        
          await addJobTotal({
            jobId: paymentJob.occurrenceKey,
            wo: paymentJob.wo ? String(paymentJob.wo) : null,
            proposalId: paymentJob.proposalId || null,
            total: newEstimatedTotal,
            source: "JobEstimateSpreadsheetRecurringPayment",
          });
        
          setRecurringOverrides((prev) => ({
            ...prev,
            [paymentJob.occurrenceKey]: {
              ...prev[paymentJob.occurrenceKey],
              payments: updatedPayments,
              estimatedTotal: newEstimatedTotal,
            },
          }));
        
          setPaymentJob(null);
          setShowPaymentPopup(false);
          setPaymentType("Deposit");
          setPaymentAmount("");
          setPaymentDate("");
          setEditingPaymentIndex(null);
          return;
        }

        await updateDoc(doc(db, "jobs", paymentJob.id), {
          payments: updatedPayments,
          updatedAt: serverTimestamp(),
        });
      
        setJobs((prev) =>
          prev.map((job) =>
            job.id === paymentJob.id
              ? { ...job, payments: updatedPayments }
              : job
          )
        );
      
        setPaymentJob(null);
        setShowPaymentPopup(false);
        setPaymentType("Deposit");
        setPaymentAmount("");
        setPaymentDate("");
        setEditingPaymentIndex(null);
        } finally {
          setBusyMessage("");
          setBusyAction("");
        }
      };

      const deletePayment = async (job, paymentIndex) => {
        if (!job?.id) return;
      
        const ok = window.confirm("Delete this deposit/payment?");
        if (!ok) return;
        setBusyAction("deletePayment");
        setBusyMessage("Deleting payment...");
        try {
      
        const updatedPayments = [...(job.payments || [])];
        updatedPayments.splice(paymentIndex, 1);

        if (job.__isRecurringOccurrence) {
          await setDoc(
            doc(db, "recurring_job_occurrences", job.occurrenceKey),
            {
              originalJobId: job.originalJobId,
              occurrenceKey: job.occurrenceKey,
              occurrenceDate: job.occurrenceDate || job.startDate,
              payments: updatedPayments,
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );

          setRecurringOverrides((prev) => ({
            ...prev,
            [job.occurrenceKey]: {
              ...prev[job.occurrenceKey],
              payments: updatedPayments,
              updatedAt: new Date().toISOString(),
            },
          }));
        } else {
          await updateDoc(doc(db, "jobs", job.id), {
            payments: updatedPayments,
            updatedAt: serverTimestamp(),
          });
        
          setJobs((prev) =>
            prev.map((j) =>
              j.id === job.id
                ? { ...j, payments: updatedPayments }
                : j
            )
          );
        }
        } finally {
          setBusyMessage("");
          setBusyAction("");
        }
      };
  
      const sortSplitRows = (rows, sortType = splitSortBy) => {
        return [...rows].sort((a, b) => {
          if (sortType === "wo") {
            return String(a.wo || "").localeCompare(String(b.wo || ""), undefined, {
              numeric: true,
              sensitivity: "base",
            });
          }
      
          if (sortType === "date") {
            return String(a.startDate || "").localeCompare(String(b.startDate || ""));
          }
      
          return 0;
        });
      };

      const getFilteredLinkableSplitRows = () => {
        const query = String(linkableWOSearch || "").trim().toLowerCase();

        const rows = !query
          ? splitRows
          : splitRows.filter((row) =>
              [row.wo, row.siteName, row.scope, row.startDate].some((value) =>
                String(value || "").toLowerCase().includes(query)
              )
            );

        return sortSplitRows(rows, splitSortBy);
      };

      const updateSplitRowByKey = (rowKey, updates) => {
        setSplitRows((prev) =>
          prev.map((row) => {
            const key = String(row.wo || row.jobId || "");
            if (key !== String(rowKey || "")) return row;
            return {
              ...row,
              ...(typeof updates === "function" ? updates(row) : updates),
            };
          })
        );
      };

      const equalDivideSplitCheck = () => {
        const totalCents = Math.round(numberValue(splitCheckTotal) * 100);
        const selectedIndexes = splitRows
          .map((row, index) => (row.selected && !row.invoiceOnly ? index : null))
          .filter((index) => index !== null);

        if (!totalCents || totalCents <= 0) {
          alert("Please enter the total check amount first.");
          return;
        }

        if (!selectedIndexes.length) {
          alert("Please select at least one real job to split this check across. Invoice-only rows are not included in Equal Divide.");
          return;
        }

        const baseCents = Math.floor(totalCents / selectedIndexes.length);
        let remainderCents = totalCents - baseCents * selectedIndexes.length;

        setSplitRows((prev) =>
          prev.map((row, index) => {
            if (!selectedIndexes.includes(index)) return row;

            const addOneCent = remainderCents > 0 ? 1 : 0;
            if (remainderCents > 0) remainderCents -= 1;

            return {
              ...row,
              amount: ((baseCents + addOneCent) / 100).toFixed(2),
            };
          })
        );
      };

     const openSplitCheckPopup = (job, existingSplitDeposits = []) => {
  const splitRowsByKey = new Map();
  const editingDeposits = Array.isArray(existingSplitDeposits)
    ? existingSplitDeposits.filter(Boolean)
    : [];
  const editingByInvoice = {};
  const invoiceOnlyKeys = new Set(
    editingDeposits
      .flatMap((deposit) => deposit.invoiceOnlyKeys || deposit.dummyInvoiceKeys || [])
      .map((key) => String(key || "").trim())
      .filter(Boolean)
  );

  editingDeposits.forEach((deposit) => {
    // Visit checks are saved under the job's WO with the visit in
    // occurrenceDocKey; older ones used "<jobId>_<date>" as the invoice number.
    const invoiceKey = String(deposit.occurrenceDocKey || deposit.invoiceNumber || "").trim();
    if (!invoiceKey) return;
    editingByInvoice[invoiceKey] = deposit;
  });

  const selectedInvoiceKey = String(
    job?.__isRecurringOccurrence
      ? job.occurrenceKey || job.wo || job.id
      : job?.wo || job?.id || ""
  ).trim();

  const addSplitRow = (sourceJob) => {
    if (!sourceJob?.wo) return;

    const invoiceKey = String(
      sourceJob.__isRecurringOccurrence
        ? sourceJob.occurrenceKey || sourceJob.wo
        : sourceJob.wo
    ).trim();

    if (!invoiceKey || splitRowsByKey.has(invoiceKey)) return;

    const existingDeposit = editingByInvoice[invoiceKey];
    const invoiceOnly = invoiceOnlyKeys.has(invoiceKey);

    splitRowsByKey.set(invoiceKey, {
      jobId: sourceJob.id,
      originalJobId: sourceJob.originalJobId || sourceJob.id,
      occurrenceKey: sourceJob.__isRecurringOccurrence ? invoiceKey : "",
      canonicalOccurrenceKey: sourceJob.canonicalOccurrenceKey || "",
      // Priority: a visit's payment is filed under the job's WO (what Approved
      // Invoices and Deposits look up) with the visit kept in occurrenceDocKey.
      depositInvoiceNumber: sourceJob.__isRecurringOccurrence
        ? String(sourceJob.masterWO || invoiceKey)
        : invoiceKey,
      displayWO: String(sourceJob.wo || invoiceKey),
      siteName: sourceJob.siteName,
      wo: invoiceKey,
      scope: sourceJob.scope,
      startDate: sourceJob.occurrenceDate || sourceJob.startDate || "",
      amount: invoiceOnly ? "" : existingDeposit ? String(existingDeposit.amount || "") : "",
      selected: invoiceOnly || Boolean(existingDeposit) || invoiceKey === selectedInvoiceKey,
      invoiceOnly,
      paymentType: existingDeposit?.paymentType || splitCheckPaymentType || "Deposit",
      existingDepositId: existingDeposit?.id || "",
    });
  };

  jobs
    .filter((j) => j.siteName === job.siteName && j.wo)
    .forEach((j) => {
      if (isRecurringJob(j)) {
        const dates = generateRecurringDates(j);
        dates.forEach((dateKey) => addSplitRow(buildOccurrenceJob(j, dateKey)));
      } else {
        addSplitRow(j);
      }
    });

  const firstDeposit = editingDeposits[0] || null;
  const totalFromDeposits = editingDeposits.reduce(
    (sum, deposit) => sum + numberValue(deposit.amount),
    0
  );

  setSplitSortBy("wo");
  setSplitCheckJob(job);
  setSplitRows(sortSplitRows(Array.from(splitRowsByKey.values()), "wo"));
  setSplitCheckNumber(firstDeposit?.checkNumber || "");
  setSplitCheckDate(firstDeposit?.date || "");
  setVendorChoice(firstDeposit?.company, setSplitCheckVendor, setSplitCheckVendorOther);
  setSplitCheckPaymentType(firstDeposit?.paymentType || "Deposit");
  setSplitCheckTotal(
    firstDeposit
      ? String(firstDeposit.originalCheckAmount || totalFromDeposits || "")
      : ""
  );
  setEditingSplitCheckGroupId(firstDeposit?.checkGroupId || "");
  setEditingSplitCheckDepositIds(editingDeposits.map((deposit) => deposit.id).filter(Boolean));
  setShowSplitCheckPopup(true);
};

      const saveCheckReceived = async () => {
        if (!checkJob?.id || !checkAmount) return;
        setBusyAction("saveCheckReceived");
        setBusyMessage(editingCheckIndex !== null ? "Updating check..." : "Saving check...");
        try {

        let oldInvoiceNumber = "";
      
        // Priority: a check on one visit of a recurring job is filed under the
        // job's WO (so Approved Invoices / Deposits see it) and tagged with the
        // visit (jobId + occurrenceKey) so this page credits that visit.
        const isVisit = Boolean(checkJob.__isRecurringOccurrence);
        const depositData = {
          property: checkJob.siteName || "",
          amount: numberValue(checkAmount),
          checkNumber: checkNumber || "",
          paymentType: checkPaymentType || "Other",
          invoiceNumber: isVisit
            ? String(checkJob.masterWO || checkJob.occurrenceKey)
            : checkJob.wo
            ? String(checkJob.wo)
            : "",
          ...(isVisit
            ? {
                jobId: checkJob.originalJobId || "",
                occurrenceKey: checkJob.canonicalOccurrenceKey || "",
                occurrenceDocKey: checkJob.occurrenceKey || "",
              }
            : { jobId: checkJob.id || "" }),
          invoiceNumber2: checkReference || "",
          description: checkJob.scope || "",
          company: vendorValue(checkVendor, checkVendorOther),
          date: checkDate || "",
          createdFrom: "JobEstimateSpreadsheet",
          updatedAt: serverTimestamp(),
        };
      
        if (editingCheckIndex !== null) {
          const existingDeposit = getJobDeposits(checkJob)[editingCheckIndex];
          oldInvoiceNumber = existingDeposit?.invoiceNumber || "";
      
          if (existingDeposit?.id && !existingDeposit.__applied) {
            await updateDoc(doc(db, "deposits", existingDeposit.id), depositData);
      
            setDeposits((prev) =>
              prev.map((deposit) =>
                deposit.id === existingDeposit.id
                  ? { ...deposit, ...depositData, updatedAt: new Date().toISOString() }
                  : deposit
              )
            );
          }
        } else {
          const docRef = await addDoc(collection(db, "deposits"), {
            ...depositData,
            createdAt: serverTimestamp(),
          });
      
          setDeposits((prev) => [
            ...prev,
            {
              id: docRef.id,
              ...depositData,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
          ]);
        }

        await syncApprovedInvoicePaidStatus(depositData.invoiceNumber, jobs);

if (
  editingCheckIndex !== null &&
  oldInvoiceNumber &&
  String(oldInvoiceNumber) !== String(depositData.invoiceNumber)
) {
  await syncApprovedInvoicePaidStatus(oldInvoiceNumber, jobs);
}
      
        setCheckJob(null);
        setShowCheckPopup(false);
        setCheckVendor(DEFAULT_CHECK_VENDOR);
        setCheckVendorOther("");
        setCheckNumber("");
        setCheckDate("");
        setCheckReference("");
        setCheckAmount("");
        setEditingCheckIndex(null);
        } finally {
          setBusyMessage("");
          setBusyAction("");
        }
      };

      const deleteCheckReceived = async (depositId) => {
        if (!depositId || !deposits.some((deposit) => deposit.id === depositId)) return;
      
        const ok = window.confirm("Delete this check/deposit?");
        if (!ok) return;
        setBusyAction("deleteCheckReceived");
        setBusyMessage("Deleting check/deposit...");
        try {

        const depositBeingDeleted = deposits.find((deposit) => deposit.id === depositId);
const invoiceNumberToSync = depositBeingDeleted?.invoiceNumber;
      
        await deleteDoc(doc(db, "deposits", depositId));
      
        setDeposits((prev) => prev.filter((deposit) => deposit.id !== depositId));
        if (invoiceNumberToSync) {
          await syncApprovedInvoicePaidStatus(invoiceNumberToSync, jobs);
        }
        } finally {
          setBusyMessage("");
          setBusyAction("");
        }
      };

      // Records a split-check share on the job / visit (checksReceived) so it
      // shows right away. A payment never changes the job's PRICE: no
      // estimatedTotal or job_totals writes here.
      const applySplitAmountToEstimatedTotal = async (row, amount, checkMeta = {}) => {
        const invoiceKey = String(row.wo || "").trim();
        const numericAmount = numberValue(amount);
        const checkReceivedEntry = {
          amount: numericAmount,
          checkNumber: checkMeta.checkNumber || splitCheckNumber || "",
          date: checkMeta.date || splitCheckDate || "",
          paymentType: checkMeta.paymentType || row.paymentType || splitCheckPaymentType || "Other",
          invoiceNumber: invoiceKey,
          invoiceNumber2: checkMeta.invoiceNumber2 || "",
          checkGroupId: checkMeta.checkGroupId || "",
          depositId: checkMeta.depositId || "",
          source: "SplitCheck",
          updatedAt: new Date().toISOString(),
        };

        const replaceGroupCheck = (items = []) => [
          ...(Array.isArray(items) ? items : []).filter(
            (item) =>
              !checkReceivedEntry.checkGroupId ||
              item.checkGroupId !== checkReceivedEntry.checkGroupId
          ),
          checkReceivedEntry,
        ];

        if (row.occurrenceKey) {
          const currentOverride = recurringOverrides[row.occurrenceKey] || {};
          const updatedChecksReceived = replaceGroupCheck(currentOverride.checksReceived || []);

          await setDoc(
            doc(db, "recurring_job_occurrences", row.occurrenceKey),
            {
              originalJobId: row.originalJobId || row.jobId,
              occurrenceKey: row.occurrenceKey,
              occurrenceDate: row.startDate || "",
              checksReceived: updatedChecksReceived,
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );

          setRecurringOverrides((prev) => ({
            ...prev,
            [row.occurrenceKey]: {
              ...prev[row.occurrenceKey],
              originalJobId: row.originalJobId || row.jobId,
              occurrenceKey: row.occurrenceKey,
              occurrenceDate: row.startDate || "",
              checksReceived: updatedChecksReceived,
              updatedAt: new Date().toISOString(),
            },
          }));

          return;
        }

        if (!row.jobId) return;

        const existingJob = jobs.find((job) => job.id === row.jobId) || {};
        const updatedChecksReceived = replaceGroupCheck(existingJob.checksReceived || []);

        await updateDoc(doc(db, "jobs", row.jobId), {
          checksReceived: updatedChecksReceived,
          updatedAt: serverTimestamp(),
        });

        setJobs((prev) =>
          prev.map((job) =>
            job.id === row.jobId ? { ...job, checksReceived: updatedChecksReceived } : job
          )
        );
      };

      const saveSplitCheck = async () => {
        setBusyAction("saveSplitCheck");
        setBusyMessage(editingSplitCheckGroupId ? "Updating split check..." : "Saving split check...");
        try {
        const selectedDisplayRows = splitRows.filter((row) => row.selected);
        const invoiceOnlyRows = selectedDisplayRows.filter((row) => row.invoiceOnly);
        const selectedRows = selectedDisplayRows.filter(
          (row) => !row.invoiceOnly && numberValue(row.amount) > 0
        );
      
        if (!splitCheckJob?.id || !splitCheckNumber || !splitCheckDate || selectedRows.length === 0) {
          alert("Please enter a check number, date, and at least one real selected job amount. Invoice-only rows are for invoice tracking only and do not receive money.");
          return;
        }
      
        const enteredTotal = selectedRows.reduce(
          (sum, row) => sum + numberValue(row.amount),
          0
        );
      
        if (splitCheckTotal && numberValue(splitCheckTotal) !== enteredTotal) {
          const proceed = window.confirm(
            `The split amounts total ${money(enteredTotal)}, but the check total is ${money(
              splitCheckTotal
            )}. Do you want to continue?`
          );
      
          if (!proceed) return;
        }

        const existingDepositsToReplace = editingSplitCheckGroupId
          ? deposits.filter(
              (deposit) =>
                deposit.checkGroupId === editingSplitCheckGroupId ||
                editingSplitCheckDepositIds.includes(deposit.id)
            )
          : [];

        const oldInvoiceNumbers = new Set(
          existingDepositsToReplace
            .map((deposit) => String(deposit.invoiceNumber || "").trim())
            .filter(Boolean)
        );

        if (existingDepositsToReplace.length) {
          await Promise.all(
            existingDepositsToReplace.map((deposit) =>
              deleteDoc(doc(db, "deposits", deposit.id))
            )
          );
        }
      
        const checkGroupId = editingSplitCheckGroupId || `${splitCheckNumber}_${splitCheckDate}_${splitCheckJob.siteName}`
          .replace(/\s+/g, "_")
          .replace(/[^a-zA-Z0-9_-]/g, "");
      
        const newDeposits = [];
      
        for (const row of selectedRows) {
          const rowAmount = numberValue(row.amount);
          const depositData = {
            property: splitCheckJob.siteName || "",
            amount: rowAmount,
            checkNumber: splitCheckNumber || "",
            paymentType: row.paymentType || splitCheckPaymentType || "Other",
            invoiceNumber: String(row.depositInvoiceNumber || row.wo || ""),
            ...splitRowVisitFields(row),
            invoiceNumber2: selectedDisplayRows.map((r) => r.displayWO || r.wo).join(", "),
            invoiceOnlyKeys: invoiceOnlyRows.map((r) => r.wo).filter(Boolean),
            invoiceOnlyLabels: invoiceOnlyRows.map((r) => `WO ${r.displayWO || r.wo}`).filter(Boolean),
            description: row.scope || splitCheckJob.scope || "",
            company: vendorValue(splitCheckVendor, splitCheckVendorOther),
            date: splitCheckDate || "",
            checkGroupId,
            originalCheckAmount: numberValue(splitCheckTotal) || enteredTotal,
            splitCheck: true,
            createdFrom: "JobEstimateSpreadsheetSplitCheck",
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          };
      
          const docRef = await addDoc(collection(db, "deposits"), depositData);

          await applySplitAmountToEstimatedTotal(row, rowAmount, {
            ...depositData,
            depositId: docRef.id,
          });
      
          newDeposits.push({
            id: docRef.id,
            ...depositData,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          });
        }

        for (const row of invoiceOnlyRows) {
          const invoiceOnlyDepositData = {
            property: splitCheckJob.siteName || "",
            amount: 0,
            checkNumber: splitCheckNumber || "",
            paymentType: row.paymentType || splitCheckPaymentType || "Other",
            invoiceNumber: String(row.depositInvoiceNumber || row.wo || ""),
            ...splitRowVisitFields(row),
            invoiceNumber2: selectedRows.map((r) => r.displayWO || r.wo).join(", "),
            splitChildKeys: selectedRows.map((r) => r.wo).filter(Boolean),
            splitChildLabels: selectedRows.map((r) => `WO ${r.displayWO || r.wo}`).filter(Boolean),
            description: row.scope || splitCheckJob.scope || "",
            company: vendorValue(splitCheckVendor, splitCheckVendorOther),
            date: splitCheckDate || "",
            checkGroupId,
            originalCheckAmount: numberValue(splitCheckTotal) || enteredTotal,
            splitCheck: true,
            splitInvoiceOnly: true,
            invoiceOnly: true,
            createdFrom: "JobEstimateSpreadsheetSplitCheckInvoiceOnly",
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          };

          const docRef = await addDoc(collection(db, "deposits"), invoiceOnlyDepositData);

          newDeposits.push({
            id: docRef.id,
            ...invoiceOnlyDepositData,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          });
        }
      
        setDeposits((prev) => [
          ...prev.filter(
            (deposit) =>
              deposit.checkGroupId !== editingSplitCheckGroupId &&
              !editingSplitCheckDepositIds.includes(deposit.id)
          ),
          ...newDeposits,
        ]);

        const affectedInvoiceNumbers = new Set([
          ...Array.from(oldInvoiceNumbers),
          ...newDeposits.map((deposit) => String(deposit.invoiceNumber || "").trim()).filter(Boolean),
        ]);
        
        for (const invoiceNumber of affectedInvoiceNumbers) {
          await syncApprovedInvoicePaidStatus(invoiceNumber, jobs);
        }
      
        setShowLinkableWOsSection(false); setShowSplitCheckPopup(false);
        setSplitCheckJob(null);
        setSplitCheckNumber("");
        setSplitCheckDate("");
        setSplitCheckVendor(DEFAULT_CHECK_VENDOR);
        setSplitCheckVendorOther("");
        setSplitCheckPaymentType("Deposit");
        setSplitCheckTotal("");
        setSplitRows([]);
        setEditingSplitCheckGroupId("");
        setEditingSplitCheckDepositIds([]);
        } finally {
          setBusyMessage("");
          setBusyAction("");
        }
      };

      const findStaffByAssignment = (assignment) => {
        const assignmentName = normalizePersonName(resolveAssignedName(assignment));
        if (!assignmentName) return null;

        return staff.find((person) => {
          const candidates = [
            person.name,
            person.fullName,
            person.displayName,
            person.employeeName,
            person.techName,
            `${person.firstName || ""} ${person.lastName || ""}`,
            person.email,
          ];
          return candidates.some((candidate) => normalizePersonName(candidate) === assignmentName);
        }) || null;
      };

      const getVerificationDateKeys = (record = {}) => {
        const startKey = toDateKey(record.startDate || record.occurrenceDate);
        if (!startKey) return [];

        const endKey = toDateKey(record.endDate) || startKey;
        const today = todayKey();
        const lastKey = endKey < today ? endKey : today;
        if (startKey > lastKey) return [];

        const dates = [];
        const cursor = new Date(`${startKey}T12:00:00`);
        const last = new Date(`${lastKey}T12:00:00`);
        while (cursor <= last) {
          dates.push(cursor.toISOString().slice(0, 10));
          cursor.setDate(cursor.getDate() + 1);
        }
        return dates;
      };

      const loadMissingTimeVerifications = async () => {
        setLoadingTimeVerification(true);
        setShowTimeVerification(true);
        try {
          const [jobsSnap, occurrencesSnap, logsSnap] = await Promise.all([
            getDocs(collection(db, "jobs")),
            getDocs(collection(db, "recurring_job_occurrences")).catch(() => ({ docs: [] })),
            getDocs(collection(db, "time_logs")),
          ]);

          const assignmentMap = new Map();

          // Subs paid a set amount through Sub Pay don't clock in, so being on
          // Tech Assign for that job shouldn't flag them as missing time.
          const masterLinesById = {};
          jobsSnap.docs.forEach((d) => {
            const lines = d.data().subcontractorPay;
            masterLinesById[d.id] = Array.isArray(lines) ? lines : [];
          });
          const isPaidThroughSubPay = (record, person, assignedName) => {
            if (!isSubcontractorRecord(person)) return false;
            const lines = Array.isArray(record.subcontractorPay)
              ? record.subcontractorPay
              : masterLinesById[record.originalJobId] || [];
            const personKeys = new Set(
              [assignedName, person.name, `${person.firstName || ""} ${person.lastName || ""}`, person.companyName]
                .map((n) => bucketKeyForName(n || ""))
                .filter(Boolean)
            );
            return lines.some((line) => {
              if (subPayLineTotal(line) <= 0) return false;
              if (line.subcontractorId && String(line.subcontractorId) === String(person.id)) return true;
              return personKeys.has(bucketKeyForName(line.subcontractorName || line.name || ""));
            });
          };

          const addRecordAssignments = (record, recordId, isOccurrence = false) => {
            const techs = Array.isArray(record.techAssign)
              ? record.techAssign
              : record.techAssign
              ? [record.techAssign]
              : [];
            if (!techs.filter(Boolean).length) return;

            getVerificationDateKeys(record).forEach((dateKey) => {
              techs.filter(Boolean).forEach((assignment) => {
                const person = findStaffByAssignment(assignment);
                if (!person?.id) return;
                if (isPaidThroughSubPay(record, person, resolveAssignedName(assignment))) return;
                const key = `${person.id}__${dateKey}`;
                const existing = assignmentMap.get(key) || {
                  key,
                  userId: person.id,
                  userName: person.name || resolveAssignedName(assignment),
                  dateKey,
                  jobs: [],
                };
                existing.jobs.push({
                  jobId: recordId,
                  wo: record.wo || "",
                  siteName: record.siteName || "",
                  isOccurrence,
                });
                assignmentMap.set(key, existing);
              });
            });
          };

          // Priority recurring jobs: one record per visit (same visits as
          // Calendar), techs from this page's saved visit edits, then the
          // Calendar "this job only" edit, then the job. Checked from the From
          // date (or 60 days back) through today.
          const occurrenceDocs = {};
          occurrencesSnap.docs.forEach((d) => {
            occurrenceDocs[d.id] = d.data();
          });
          const verifyStart = fromDate || moment().subtract(60, "days").format("YYYY-MM-DD");
          const verifyEnd = toDate && toDate < todayKey() ? toDate : todayKey();
          const handledOccurrenceIds = new Set();

          jobsSnap.docs.forEach((d) => {
            const data = { id: d.id, ...d.data() };
            if (!isRecurring(data)) {
              addRecordAssignments(data, d.id, false);
              return;
            }
            const master = { ...data, startDate: toDateKey(data.startDate) };
            getRecurringVisits(master, verifyStart, verifyEnd).forEach(({ canonicalKey, scheduledKey }) => {
              const occurrenceId = `${d.id}_${canonicalKey}`;
              handledOccurrenceIds.add(occurrenceId);
              const saved = occurrenceDocs[occurrenceId] || {};
              const visit = pickVisitOverride(master, canonicalKey);
              addRecordAssignments(
                {
                  ...saved,
                  originalJobId: d.id,
                  wo: data.wo || "",
                  siteName: data.siteName || data.propertyName || "",
                  startDate: saved.startDate || scheduledKey,
                  endDate: "",
                  subcontractorPay:
                    Array.isArray(saved.subcontractorPay) && saved.subcontractorPay.length
                      ? saved.subcontractorPay
                      : undefined,
                  techAssign: Array.isArray(saved.techAssign)
                    ? saved.techAssign
                    : Array.isArray(visit.techAssign)
                    ? visit.techAssign
                    : data.techAssign,
                },
                occurrenceId,
                true
              );
            });
          });
          occurrencesSnap.docs.forEach((d) => {
            if (handledOccurrenceIds.has(d.id)) return;
            const masterExists = jobsSnap.docs.some((jobDoc) => jobDoc.id === d.data().originalJobId);
            if (masterExists) return; // visit no longer on the schedule / out of range
            addRecordAssignments(d.data(), d.id, true);
          });

          const logs = logsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
          const missing = Array.from(assignmentMap.values()).filter((row) => {
            return !logs.some((log) => {
              if (String(log.userId || "") !== String(row.userId)) return false;
              if (toDateKey(log.clockIn) !== row.dateKey) return false;

              const reason = String(log.reason || "").toLowerCase();
              const isNonWorkMarker = reason.includes("off") || reason.includes("payment");
              if (isNonWorkMarker) return false;

              // Any real clock-in, active clock-in, manual entry, or previously approved
              // spreadsheet entry fills the day and prevents a duplicate suggestion.
              return !!log.clockIn && (log.clockOut == null || numberValue(log.duration) > 0);
            });
          });

          missing.sort((a, b) =>
            String(b.dateKey).localeCompare(String(a.dateKey)) ||
            String(a.userName).localeCompare(String(b.userName))
          );
          setMissingTimeRows(missing);
        } catch (error) {
          console.error("Unable to compare tech assignments to clock time:", error);
          alert("Unable to load missing time verification. See console for details.");
        } finally {
          setLoadingTimeVerification(false);
        }
      };

      const approveMissingTimeRow = async (row) => {
        setBusyAction(`approveTime-${row.key}`);
        setBusyMessage(`Adding 8 hours for ${row.userName} on ${row.dateKey}...`);
        try {
          // Re-check immediately before writing so a clock-in that happened after
          // the review screen opened still wins and no duplicate is created.
          const userLogsSnap = await getDocs(
            query(collection(db, "time_logs"), where("userId", "==", row.userId))
          );
          const existingWorkLog = userLogsSnap.docs.some((d) => {
            const log = d.data();
            if (toDateKey(log.clockIn) !== row.dateKey) return false;
            const reason = String(log.reason || "").toLowerCase();
            if (reason.includes("off") || reason.includes("payment")) return false;
            return !!log.clockIn && (log.clockOut == null || numberValue(log.duration) > 0);
          });

          if (existingWorkLog) {
            setMissingTimeRows((prev) => prev.filter((item) => item.key !== row.key));
            alert("A clock entry already exists for this employee and date. Nothing was added.");
            return;
          }

          const { clockIn, clockOut } = getLocalShiftTimes(row.dateKey);
          await addDoc(collection(db, "time_logs"), {
            userId: row.userId,
            userName: row.userName || "",
            clockIn,
            clockOut,
            duration: 480,
            reason: "Approved 8-hour shift from tech assignment verification",
            autoGenerated: true,
            verifiedByAdmin: true,
            source: "JobEstimateSpreadsheetTechAssign",
            workDate: row.dateKey,
            assignedJobRefs: row.jobs.map((job) =>
              job.isOccurrence ? `occurrence:${job.jobId}` : `job:${job.jobId}`
            ),
            assignedWO: row.jobs.map((job) => job.wo).filter(Boolean).join(", "),
            payments: [],
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });

          setMissingTimeRows((prev) => prev.filter((item) => item.key !== row.key));
        } catch (error) {
          console.error("Unable to approve missing time:", error);
          alert("Unable to add the time entry. See console for details.");
        } finally {
          setBusyMessage("");
          setBusyAction("");
        }
      };

      const saveRecurringOccurrence = async (job) => {
        if (!job.__isRecurringOccurrence || !job.occurrenceKey || !job.originalJobId) return;
        setBusyAction(`saveRecurring-${job.id}`);
        setBusyMessage("Saving recurring date...");
        try {
      
        const selectedTechs = Array.isArray(job.techAssign)
          ? job.techAssign.filter(Boolean)
          : [];
      
        const estimatedTotal = numberValue(job.estimatedTotal);
      
        const occurrenceData = {
          originalJobId: job.originalJobId,
          occurrenceKey: job.occurrenceKey,
          occurrenceDate: job.occurrenceDate || job.startDate,
          siteName: job.siteName || "",
          city: job.city || "",
          wo: job.wo || "",
          serviceType: job.serviceType || "",
          scope: job.scope || "",
          estimatedDays: numberValue(job.estimatedDays),
          estimatedLabor: selectedTechs.length,
          estimatedMaterial: numberValue(job.estimatedMaterial),
          subPay: numberValue(job.subPay),
          subcontractorPay: Array.isArray(job.subcontractorPay) ? job.subcontractorPay : [],
    staffCommissionOverrides: Array.isArray(job.staffCommissionOverrides) ? job.staffCommissionOverrides : [],
          staffCommissionOverrides: Array.isArray(job.staffCommissionOverrides) ? job.staffCommissionOverrides : [],
          receipts: Array.isArray(job.receipts) ? job.receipts : [],
          receiptsTotal: numberValue(job.receiptsTotal),
          payments: Array.isArray(job.payments) ? job.payments : [],
          estimatedTotal,
          estimatedLaborCost: numberValue(job.estimatedLaborCost),
          estimatedProfit: numberValue(job.estimatedProfit),
          totalExpenses: numberValue(job.totalExpenses),
          warrantyCost: numberValue(job.warrantyCost),
          isWarranty: job.isWarranty === true,
          warrantyMarkedAt: job.isWarranty ? job.warrantyMarkedAt || new Date().toISOString() : "",
          // Priority's Calendar owns the visit date and completion state. Only
          // keep a date / status here when it was changed on this page, so a
          // later reschedule or completion elsewhere still shows up.
          canonicalOccurrenceKey: job.canonicalOccurrenceKey || "",
          startDate:
            job.startDate && job.startDate !== job.occurrenceDate ? job.startDate : "",
          endDate: job.endDate || "",
          techAssign: selectedTechs,
          status:
            job.status &&
            job.status !==
              visitStatusFromJob(
                jobs.find((item) => item.id === job.originalJobId) || {},
                job.canonicalOccurrenceKey
              )
              ? job.status
              : "",
          updatedAt: serverTimestamp(),
        };
      
        await setDoc(
          doc(db, "recurring_job_occurrences", job.occurrenceKey),
          occurrenceData,
          { merge: true }
        );
      
        await addJobTotal({
          jobId: job.occurrenceKey,
          wo: job.wo ? String(job.wo) : null,
          proposalId: job.proposalId || null,
          total: estimatedTotal,
          source: "JobEstimateSpreadsheetRecurringOccurrence",
        });
      
        setRecurringOverrides((prev) => ({
          ...prev,
          [job.occurrenceKey]: {
            ...prev[job.occurrenceKey],
            ...occurrenceData,
            updatedAt: new Date().toISOString(),
          },
        }));
      
        setEditedRows((prev) => {
          const copy = { ...prev };
          delete copy[job.id];
          return copy;
        });
      
        alert("Recurring date saved separately.");
        } finally {
          setBusyMessage("");
          setBusyAction("");
        }
      };

 const saveRow = async (job) => {
  setBusyAction(`saveRow-${job.id}`);
  setBusyMessage("Saving main job...");
  try {
  const selectedTechs = Array.isArray(job.techAssign)
    ? job.techAssign.filter(Boolean)
    : [];

  const jobKey = String(job.wo || job.id);
  const estimatedTotal = numberValue(job.estimatedTotal);

  await updateDoc(doc(db, "jobs", job.id), {
    serviceType: job.serviceType,
    estimatedDays: numberValue(job.estimatedDays),
    estimatedLabor: selectedTechs.length,
    estimatedMaterial: numberValue(job.estimatedMaterial),
    subPay: numberValue(job.subPay),
    subcontractorPay: Array.isArray(job.subcontractorPay) ? job.subcontractorPay : [],
    receiptsTotal: numberValue(job.receiptsTotal),
    totalExpenses: numberValue(job.totalExpenses),
    warrantyCost: numberValue(job.warrantyCost),
    isWarranty: job.isWarranty === true,
    warrantyMarkedAt: job.isWarranty ? job.warrantyMarkedAt || new Date().toISOString() : "",
    estimatedTotal,
    estimatedLaborCost: numberValue(job.estimatedLaborCost),
    estimatedProfit: numberValue(job.estimatedProfit),
    startDate: job.startDate || "",
    endDate: job.endDate || "",
    techAssign: selectedTechs,
    status: job.status,
    associatedJobIds: Array.isArray(job.associatedJobIds) ? job.associatedJobIds : [],
    associatedChildJobIds: Array.isArray(job.associatedChildJobIds) ? job.associatedChildJobIds : [],
    parentJobId: job.parentJobId || "",
    updatedAt: serverTimestamp(),
  });

  await addJobTotal({
    jobId: jobKey,
    wo: job.wo ? String(job.wo) : null,
    proposalId: job.proposalId || null,
    total: estimatedTotal,
    source: "JobEstimateSpreadsheet",
  });

  setEditedRows((prev) => {
    const copy = { ...prev };
    delete copy[job.id];
    return copy;
  });

  alert("Job saved.");
  } finally {
    setBusyMessage("");
    setBusyAction("");
  }
};

// Priority: recurring = anything Priority's own pages treat as recurring
// (recurrence object or string, daily ... yearly, customDates).
const isRecurringJob = (job) => isRecurring(job);

/**
 * Canonical visit dates of a recurring job whose scheduled day is inside the
 * From/To range - the same visits Priority's Calendar and Home show
 * (getOccurrencesForJob + occurrenceOverrides reschedules, minus excludedDates).
 * With no From/To the window is the series start through 6 months from today.
 */
function generateRecurringDates(job) {
  if (!isRecurringJob(job) || !job.startDate) return [];
  return getVisitsForJob(job).map((visit) => visit.canonicalKey);
}

const runSearch = () => {
  setBusyAction("search");
  setBusyMessage("Searching jobs...");
  window.setTimeout(() => {
    setSearch(searchDraft);
    window.setTimeout(() => {
      setBusyMessage("");
      setBusyAction("");
    }, 250);
  }, 50);
};

const filteredJobs = useMemo(() => {
  let list = jobs.map((job) => calculateRow(job, jobs));

  if (statusFilter) {
    list = list.filter((job) => {
      // Recurring jobs must remain available in the spreadsheet by date range
      // even if the series/status is Active, Completed, or Invoice Sent.
      // Each generated occurrence row can still show its own status.
      if (isRecurringJob(job) && (fromDate || toDate)) {
        return generateRecurringDates(job).length > 0;
      }

      return job.status === statusFilter;
    });
  }

  if (needsSchedulingOnly) {
    list = list.filter((job) => !job.startDate);
  } else if (fromDate || toDate) {
    list = list.filter((job) => {
      if (!job.startDate) return false;

      // Recurring jobs stay visible when any occurrence lands inside the selected range.
      if (isRecurringJob(job)) {
        return generateRecurringDates(job).length > 0;
      }

      if (fromDate && job.startDate < fromDate) return false;
      if (toDate && job.startDate > toDate) return false;

      return true;
    });
  }

  if (search.trim()) {
    const term = search.toLowerCase().trim();
  
    list = list.filter((job) => {
      const wo = String(job.wo || "").toLowerCase();
  
      return (
        job.siteName?.toLowerCase().includes(term) ||
        job.scope?.toLowerCase().includes(term) ||
        wo.includes(term)
      );
    });
  }

  if (hidePaidInFull && !showAllJobs && statusFilter !== "Paid In Full") {
    list = list.filter((job) => checkPaymentStatus(job).label !== "Paid In Full");
  }

  list.sort((a, b) => {
    const av = a[sortBy] || "";
    const bv = b[sortBy] || "";
    return String(av).localeCompare(String(bv), undefined, { numeric: true });
  });

  return list;
}, [
  jobs,
  staff,
  statusFilter,
  search,
  sortBy,
  fromDate,
  toDate,
  needsSchedulingOnly,
  hidePaidInFull,
  showAllJobs,
  deposits,
  associationTree,
  recurringOverrides,
  invoiceMatch,
  recurringPayments,
  summaryRange,
  dailyPayIndex,
  recurringVisitDailyPay,
  techDayJobCounts,
]);

function getOccurrenceKey(jobId, dateKey) {
  return `${jobId}_${dateKey}`;
}

/**
 * One visit of a recurring Priority job as a spreadsheet row.
 *   dateKey  = the CANONICAL visit date (what completedDates/invoicedDates/
 *              occurrenceOverrides use).
 * Layers, lowest first: the job doc -> Priority's "Edit this job only"
 * snapshot for that date (tech / price / scope fields only) -> anything saved
 * from this page in recurring_job_occurrences/{jobId}_{date}.
 * Dollar amounts that belong to ONE visit (payments, checks, receipts, sub
 * pay) are never copied from the job doc onto every visit.
 */
function buildOccurrenceJob(job, dateKey) {
  const occurrenceKey = getOccurrenceKey(job.id, dateKey);
  const override = recurringOverrides[occurrenceKey] || {};
  const visitOverride = pickVisitOverride(job, dateKey);
  const scheduledKey = getOverrideScheduledKey(job, dateKey) || dateKey;
  const subLines = Array.isArray(override.subcontractorPay) ? override.subcontractorPay : [];

  return calculateRow(
    {
      ...job,
      ...visitOverride,
      ...override,
      id: occurrenceKey,
      originalJobId: job.id,
      occurrenceKey,
      canonicalOccurrenceKey: dateKey,
      occurrenceDate: scheduledKey,
      originalOccurrenceDate: dateKey,
      startDate: override.startDate || scheduledKey,
      endDate: override.endDate || "",
      wo: override.wo || `${job.wo}-${dateKey}`,
      masterWO: job.wo || "",
      techAssign: Array.isArray(override.techAssign)
        ? override.techAssign
        : Array.isArray(visitOverride.techAssign)
        ? visitOverride.techAssign
        : Array.isArray(job.techAssign)
        ? job.techAssign
        : [],
      payments: Array.isArray(override.payments) ? override.payments : [],
      checksReceived: Array.isArray(override.checksReceived) ? override.checksReceived : [],
      receipts: Array.isArray(override.receipts) ? override.receipts : [],
      receiptsTotal: numberValue(override.receiptsTotal),
      subcontractorPay: subLines,
      subPay:
        override.subPay !== undefined
          ? numberValue(override.subPay)
          : calculateSubPayTotal(subLines),
      estimatedTotal:
        override.estimatedTotal !== undefined
          ? numberValue(override.estimatedTotal)
          : defaultVisitPrice(job, dateKey, visitOverride),
      estimatedMaterial:
        override.estimatedMaterial !== undefined
          ? numberValue(override.estimatedMaterial)
          : numberValue(visitOverride.estimatedMaterial ?? job.estimatedMaterial),
      isWarranty:
        override.isWarranty !== undefined
          ? override.isWarranty === true
          : job.isWarranty === true,
      warrantyMarkedAt: override.warrantyMarkedAt || job.warrantyMarkedAt || "",
      status: override.status || visitStatusFromJob(job, dateKey),
      __isRecurringOccurrence: true,
    }
  );
}

const displayedJobs = useMemo(() => {
  const rows = [];
  const pushed = new Set();
  const filteredIds = new Set(filteredJobs.map((job) => job.id));

  const pushJobWithChildren = (job, depth = 0, forceShow = false) => {
    if (!job?.id || pushed.has(job.id)) return;
    if (!forceShow && !filteredIds.has(job.id)) return;

    const children = getAssociatedChildren(job).filter((child) => filteredIds.has(child.id) || forceShow);
    const hasChildren = children.length > 0;

    const pushJob = (rowJob, extra = {}) => {
      rows.push({
        ...rowJob,
        ...extra,
        __associationKey: getAssociationKey(rowJob),
        __associationDepth: depth,
        __isAssociatedChild: depth > 0,
        __hasAssociatedChildren: hasChildren,
        __associatedChildren: children,
      });
      pushed.add(rowJob.id);
    };

    if (!isRecurringJob(job)) {
      pushJob(job);
    } else {
      const recurringDates = generateRecurringDates(job);
      const masterDate = recurringDates[0] || job.startDate;
      const masterRow = buildOccurrenceJob(job, masterDate);

      pushJob(masterRow, {
        __isRecurringMain: true,
        __recurringSourceId: job.id,
        __recurringSourceJob: job,
        __recurringDates: recurringDates,
        __recurringMasterDate: masterDate,
      });
      pushed.add(job.id);

      if (expandedRecurringJobs[job.id]) {
        recurringDates.slice(1).forEach((dateKey) => {
          const occurrenceJob = buildOccurrenceJob(job, dateKey);
          rows.push({
            ...occurrenceJob,
            __associationKey: getAssociationKey(job),
            __associationDepth: depth + 1,
            __isAssociatedChild: true,
            __recurringSourceId: job.id,
            __recurringSourceJob: job,
            __recurringDates: recurringDates,
          });
          pushed.add(occurrenceJob.id);
        });
      }
    }

    if (expandedAssociatedJobs[job.id]) {
      children.forEach((child) => pushJobWithChildren(child, depth + 1, true));
    }
  };

  filteredJobs.forEach((job) => {
    const parentId = associationTree.parentMap[job.id];
    if (parentId && !expandedAssociatedJobs[parentId]) return;
    if (parentId && associationTree.byId[parentId] && filteredIds.has(parentId)) return;
    pushJobWithChildren(job, getAssociationDepth(job), false);
  });

  return rows;
}, [filteredJobs, expandedRecurringJobs, expandedAssociatedJobs, fromDate, toDate, recurringOverrides, associationTree, invoiceMatch, recurringVisitDailyPay]);

  const summaryJobs = useMemo(() => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();

    const dateScopedJobs = filteredJobs.flatMap((job) => {
      if (!isRecurringJob(job)) return [job];
      const dates = generateRecurringDates(job);
      return dates.length ? dates.map((dateKey) => buildOccurrenceJob(job, dateKey)) : [job];
    });

    return dateScopedJobs.filter((job) => {
      if (summaryRange === "all") return true;

      const date = job.startDate ? new Date(job.startDate + "T00:00:00") : null;
      if (!date || Number.isNaN(date.getTime())) return false;

      if (summaryRange === "week") {
        const start = new Date(today);
        start.setDate(today.getDate() - today.getDay());

        const end = new Date(start);
        end.setDate(start.getDate() + 6);

        return date >= start && date <= end;
      }

      if (summaryRange === "month") {
        return (
          date.getFullYear() === currentYear && date.getMonth() === currentMonth
        );
      }

      return true;
    });
  }, [filteredJobs, summaryRange, fromDate, toDate, recurringOverrides, invoiceMatch, recurringVisitDailyPay]);

  // Days a person already has pay saved on Tech Daily Pay. Paid time off on
  // those days is not added again.
  const dailyPayWorkerDays = useMemo(() => {
    const set = new Set();
    summarizeDailyLabor(dailyLaborDays, staffById).rows.forEach((row) => {
      if (row.userId && row.date) set.add(`${row.userId}|${row.date}`);
    });
    return set;
  }, [dailyLaborDays, staffById]);

  const timeOffLaborRows = useMemo(() => {
    const { startKey, endKey } = getSummaryRangeKeys();

    return timeOffRequests
      .filter((request) => String(request.status || "").toLowerCase() === "approved")
      .filter(isPaidTimeOffRequest)
      .map((request) => {
        const startDate = toDateKey(request.startDate);
        const endDate = toDateKey(request.endDate || request.startDate);
        const staffMember = getTimeOffStaffMember(request);

        // Weekdays in range, minus days already on Tech Daily Pay.
        let paidDays = 0;
        const activeStart = startDate > startKey ? startDate : startKey;
        const activeEnd = (endDate || startDate) < endKey ? endDate || startDate : endKey;
        if (startDate && activeStart <= activeEnd) {
          const cursor = moment(activeStart, "YYYY-MM-DD");
          const last = moment(activeEnd, "YYYY-MM-DD");
          while (cursor.isSameOrBefore(last, "day")) {
            const day = cursor.day();
            const key = cursor.format("YYYY-MM-DD");
            if (day !== 0 && day !== 6 && !(staffMember?.id && dailyPayWorkerDays.has(`${staffMember.id}|${key}`))) {
              paidDays += 1;
            }
            cursor.add(1, "day");
          }
        }

        const hourlyRate = staffMember ? getHourlyPayRate(staffMember) : 0;
        const hoursPerDay = staffMember ? getHoursPerDay(staffMember) : 8;
        const dayRate = staffMember ? getDefaultDailyPay(staffMember) : 0;
        const laborCost = paidDays * dayRate;

        return {
          id: request.id,
          name: request.name || staffMember?.name || "Unknown User",
          paidTimeType: request.paidTimeType || "Paid Time Off",
          startDate,
          endDate,
          paidDays,
          hourlyRate,
          hoursPerDay,
          laborCost,
        };
      })
      .filter((row) => row.paidDays > 0 && row.laborCost > 0);
  }, [timeOffRequests, staff, summaryRange, fromDate, toDate, dailyPayWorkerDays]);

  const totals = useMemo(() => {
    const baseTotals = summaryJobs.reduce(
      (acc, job) => {
        acc.estimatedTotal += numberValue(job.estimatedTotal);
        acc.revenue += numberValue(job.recognizedIncome);
        acc.invoiced += numberValue(job.invoicedTotal);
        acc.material += numberValue(job.estimatedMaterial);
        acc.receipts += numberValue(job.receiptsTotal);
        acc.labor += numberValue(job.estimatedLaborCost);
        acc.actualDailyPay += numberValue(job.actualDailyPay);
        acc.laborUsed += numberValue(job.laborUsed);
        acc.subPay += numberValue(job.subPay);
        acc.expenses += numberValue(job.totalExpenses);
        acc.warranty += numberValue(job.warrantyCost);
        acc.profit += numberValue(job.estimatedProfit);
        return acc;
      },
      {
        estimatedTotal: 0,
        revenue: 0,
        invoiced: 0,
        material: 0,
        receipts: 0,
        labor: 0,
        actualDailyPay: 0,
        laborUsed: 0,
        periodDailyPay: 0,
        unassignedDailyPay: 0,
        subPay: 0,
        bulkIncome: 0,
        bulkMaterialExpenses: 0,
        monthlyExpenses: 0,
        timeOffLabor: 0,
        expenses: 0,
        warranty: 0,
        profit: 0,
      }
    );
  
    const { startKey, endKey } = getSummaryRangeKeys();
  
    const unmatchedBulkMaterialsTotal = bulkMaterialExpenses
      .filter((item) => !item.jobId && isDateInRange(item.date, startKey, endKey))
      .reduce((sum, item) => sum + numberValue(item.amount), 0);
  
    // Payroll and contractor-pay "monthly expenses" that were created from a bank
    // statement are skipped here: job labor (tech assigned) and Sub Pay already
    // count that money, so including them would count it twice.
    const monthlyExpenseTotal = monthlyExpenses
      .filter((expense) => !isBankPayrollOrSubMonthly(expense))
      .reduce((sum, expense) => {
        return sum + getMonthlyExpenseTotalForRange(expense, startKey, endKey);
      }, 0);

    const unmatchedBulkIncomeTotal = bulkIncome
  .filter((item) => !item.jobId && isDateInRange(item.date, startKey, endKey))
  .reduce((sum, item) => sum + numberValue(item.amount), 0);
  
    const timeOffLaborTotal = timeOffLaborRows.reduce((sum, row) => sum + numberValue(row.laborCost), 0);

    // Tech Daily Pay for the summary period. Pay already shown on a row
    // (Actual Daily Pay) is in that row's labor. Everything else in the period
    // - no job picked, older days saved with only a total, or pay on jobs that
    // are deleted / filtered out of this view - is a period expense here, so
    // the period always adds up to the Tech Daily Pay total exactly once.
    const periodLabor = summarizeDailyLabor(dailyLaborDays, staffById, startKey, endKey);
    const unassignedDailyPay = Math.max(
      Math.round((numberValue(periodLabor.total) - baseTotals.actualDailyPay) * 100) / 100,
      0
    );
    baseTotals.periodDailyPay = numberValue(periodLabor.total);
    baseTotals.unassignedDailyPay = unassignedDailyPay;

    baseTotals.bulkMaterialExpenses = unmatchedBulkMaterialsTotal;
    baseTotals.monthlyExpenses = monthlyExpenseTotal;
    baseTotals.timeOffLabor = timeOffLaborTotal;
  
    const periodExpenses =
      unmatchedBulkMaterialsTotal + monthlyExpenseTotal + timeOffLaborTotal + unassignedDailyPay;
    baseTotals.expenses += periodExpenses;
    baseTotals.profit -= periodExpenses;
    baseTotals.bulkIncome = unmatchedBulkIncomeTotal;
    baseTotals.estimatedTotal += unmatchedBulkIncomeTotal;
    baseTotals.revenue += unmatchedBulkIncomeTotal;
    baseTotals.profit += unmatchedBulkIncomeTotal;

    return baseTotals;
  }, [
    summaryJobs,
    bulkMaterialExpenses,
    monthlyExpenses,
    bulkIncome,
    timeOffLaborRows,
    summaryRange,
    fromDate,
    toDate,
    dailyLaborDays,
    staffById,
  ]);

  /* Identity-aware payables.
     Two things were wrong here before. First, the list was built from `staff`
     alone, so a contractor typed freehand on a job never appeared at all even
     though their money was already in the Sub Pay column. Second, matching used
     one spelling, so a sub listed under their person name on an old job and
     their company name on a new one had their balance split in half.
     Both are fixed by resolving every stored name string through the shared
     identity map before comparing. Nothing stored is rewritten. */

  const subPayLineName = (line = {}) => String(line?.subcontractorName || line?.name || "").trim();

  const subcontractorTargets = useMemo(() => {
    // Everyone on the roster, shown under their company name.
    const targets = contractorIdentities.map((identity) => ({
      key: identity.key,
      sub: identity.staff || { id: identity.key },
      subName: identity.displayName,
      onRoster: true,
      ids: new Set([...(identity.ids || [])].map(String)),
    }));

    // Plus any name that appears on a job (or recurring date) pay line but
    // matches no roster record, so freehand contractors stay visible.
    const seen = new Set(targets.map((t) => t.key));
    const addFreeText = (line) => {
      const raw = subPayLineName(line);
      const key = bucketKeyForName(raw);
      if (!key || seen.has(key)) return;
      seen.add(key);
      targets.push({
        key,
        sub: { id: key, __freeText: true },
        subName: raw,
        onRoster: false,
        ids: new Set(),
      });
    };

    jobs.forEach((job) => {
      (Array.isArray(job.subcontractorPay) ? job.subcontractorPay : []).forEach(addFreeText);
    });
    Object.values(recurringOverrides || {}).forEach((occ) => {
      (Array.isArray(occ?.subcontractorPay) ? occ.subcontractorPay : []).forEach(addFreeText);
    });
    subcontractorPayments.forEach((payment) => addFreeText({ subcontractorName: payment.subcontractorName }));

    return targets.sort((a, b) => String(a.subName).localeCompare(String(b.subName)));
  }, [contractorIdentities, jobs, recurringOverrides, subcontractorPayments, bucketKeyForName]);

  const lineMatchesTarget = (line, target) => {
    if (!line || !target) return false;
    const lineId = line.subcontractorId ? String(line.subcontractorId) : "";
    if (lineId && (lineId === String(target.sub?.id || "") || target.ids?.has(lineId))) return true;
    const name = subPayLineName(line);
    return Boolean(name) && bucketKeyForName(name) === target.key;
  };

  const paymentMatchesTarget = (payment, target) => {
    if (!payment || !target) return false;
    const payId = payment.subcontractorId ? String(payment.subcontractorId) : "";
    if (payId && (payId === String(target.sub?.id || "") || target.ids?.has(payId))) return true;
    return bucketKeyForName(payment.subcontractorName) === target.key;
  };

  const subcontractorPayableRows = subcontractorTargets.map((target) => {
    const sub = target.sub;
    const subName = target.subName;
    let owed = 0;
    let workedDays = 0;
    let jobCount = 0;

    jobs.forEach((job) => {
      const assigned = techAssignMatches(
        (Array.isArray(job.techAssign) ? job.techAssign : []).map(resolveAssignedName),
        subName,
        bucketKeyForName
      );
      // Every pay line for this sub on the job (there can be more than one).
      const explicitTotal = (Array.isArray(job.subcontractorPay) ? job.subcontractorPay : [])
        .filter((line) => lineMatchesTarget(line, target))
        .reduce((sum, line) => sum + subPayLineTotal(line), 0);

      if (explicitTotal > 0) {
        owed += explicitTotal;
        jobCount += 1;
        if (assigned) workedDays += getJobWorkDays(job);
        return;
      }

      if (assigned) {
        const days = getJobWorkDays(job);
        workedDays += days;
        owed += getDefaultDailyPay(sub) * days;
      }
    });

    // Sub pay entered on a single recurring date. Contractor Pay already counts
    // these; this panel did not, so the two pages disagreed.
    Object.values(recurringOverrides || {}).forEach((occ) => {
      const lines = Array.isArray(occ?.subcontractorPay) ? occ.subcontractorPay : [];
      const occTotal = lines
        .filter((line) => lineMatchesTarget(line, target))
        .reduce((sum, line) => sum + subPayLineTotal(line), 0);
      if (occTotal > 0) {
        owed += occTotal;
        jobCount += 1;
      }
    });

    const matchedPayments = subcontractorPayments.filter((payment) => paymentMatchesTarget(payment, target));
    const paid = matchedPayments.reduce(
      (sum, payment) => sum + numberValue(payment.amount ?? payment.amountPaid),
      0
    );
    const lastPaid = matchedPayments.reduce(
      (latest, payment) => (String(payment.date || "") > latest ? String(payment.date || "") : latest),
      ""
    );

    return {
      key: target.key,
      target,
      sub,
      subName,
      onRoster: target.onRoster,
      workedDays,
      jobCount,
      owed,
      paid,
      lastPaid,
      balance: owed - paid,
    };
  });

  const payableContractorOptions = subcontractorPayableRows.map((row) => ({
    key: row.key,
    label: row.onRoster ? row.subName : `${row.subName} (not on roster)`,
    balance: row.balance,
  }));

  const findPayableTarget = (name = "", id = "") => {
    const cleanId = id ? String(id) : "";
    if (cleanId) {
      const byId = subcontractorTargets.find(
        (t) => String(t.sub?.id || "") === cleanId || t.ids?.has(cleanId)
      );
      if (byId) return byId;
    }
    const key = bucketKeyForName(name);
    return key ? subcontractorTargets.find((t) => t.key === key) || null : null;
  };

  const paymentMatchesKey = (payment, key) => {
    const target = subcontractorTargets.find((t) => t.key === key);
    return target ? paymentMatchesTarget(payment, target) : false;
  };

  // Sub Pay dollars that aren't tied to anyone, so they can't reach Payables.
  const unlinkedSubPay = (() => {
    const list = [];
    jobs.forEach((job) => {
      const lines = Array.isArray(job.subcontractorPay) ? job.subcontractorPay : [];
      const unnamed = lines
        .filter((line) => !subPayLineName(line))
        .reduce((sum, line) => sum + subPayLineTotal(line), 0);
      const legacy = lines.length === 0 ? numberValue(job.subPay) : 0;
      const amount = unnamed + legacy;
      if (amount > 0) {
        list.push({
          key: job.id,
          label: `${job.wo ? `WO ${job.wo}` : "No WO"} / ${job.siteName || "Unnamed job"}`,
          amount,
          open: () => openSubPayPopup(job),
        });
      }
    });
    Object.entries(recurringOverrides || {}).forEach(([occKey, occ]) => {
      const lines = Array.isArray(occ?.subcontractorPay) ? occ.subcontractorPay : [];
      const amount = lines
        .filter((line) => !subPayLineName(line))
        .reduce((sum, line) => sum + subPayLineTotal(line), 0);
      if (amount <= 0) return;
      const master = jobs.find((j) => j.id === occ.originalJobId);
      const date = occ.occurrenceDate || occKey.split("_").pop();
      list.push({
        key: occKey,
        label: `${master?.wo ? `WO ${master.wo}` : "No WO"} / ${master?.siteName || "Unnamed job"} (${date})`,
        amount,
        open: master ? () => openSubPayPopup(buildOccurrenceJob(master, date)) : null,
      });
    });
    return list;
  })();

  const describeJobForPayment = (job) => {
    if (!job) return "";
    const isOcc = Boolean(job.__isRecurringOccurrence);
    const master = isOcc ? jobs.find((j) => j.id === job.originalJobId) : job;
    const wo = master?.wo || job.wo || "";
    const site = master?.siteName || job.siteName || "Unnamed job";
    const date = isOcc ? ` (${job.occurrenceDate || job.startDate || ""})` : "";
    return `${wo ? `WO ${wo}` : "No WO"} / ${site}${date}`;
  };

  const paymentIsForJob = (payment, job) => {
    if (!payment || !job) return false;
    if (job.__isRecurringOccurrence) return payment.occurrenceKey === job.occurrenceKey;
    return payment.jobId === job.id && !payment.occurrenceKey;
  };

  const subIdForTarget = (target) =>
    target && !target.sub?.__freeText && target.sub?.id ? String(target.sub.id) : "";

  // Turns the form's contractor choice into a payables target. A typed name
  // that matches someone already tracked lands on that person's balance.
  const resolvePaymentTarget = ({ contractorKey, newName }) => {
    if (contractorKey) {
      return subcontractorTargets.find((t) => t.key === contractorKey) || null;
    }
    const name = String(newName || "").trim();
    if (!name) return null;

    const exact = subcontractorTargets.find((t) => t.key === bucketKeyForName(name));
    if (exact) return exact;

    const loose = looseNameKey(name);
    const near = subcontractorTargets.find((t) => looseNameKey(t.subName) === loose);
    if (near) {
      const useNear = window.confirm(
        `"${name}" looks like "${near.subName}", who is already tracked.\n\nOK = Record it under "${near.subName}"\nCancel = Keep "${name}" as a separate contractor`
      );
      if (useNear) return near;
    }

    const key = bucketKeyForName(name);
    return { key, sub: { id: key, __freeText: true }, subName: name, onRoster: false, ids: new Set() };
  };

  /**
   * Saves one payment document per entry into "subcontractorPay" (the same
   * collection Contractor Pay, ClockTime and the dashboards read) and mirrors
   * each into "transactions". jobId / occurrenceKey / jobLabel link a payment
   * to a job; scheduleId groups the payments of one plan. Returns what saved.
   */
  const writeSubPayments = async (target, entries, job = null, frequency = "once") => {
    const created = [];
    const jobId = job ? (job.__isRecurringOccurrence ? job.originalJobId : job.id) : "";
    const occurrenceKey = job?.__isRecurringOccurrence ? job.occurrenceKey : "";
    const jobLabel = describeJobForPayment(job);
    const isPlan = entries.length > 1;
    const scheduleId = isPlan ? makeScheduleId() : "";
    const staffId = target.sub && !target.sub.__freeText && !String(target.sub.id || "").includes(":")
      ? String(target.sub.id)
      : "";

    try {
      for (let index = 0; index < entries.length; index += 1) {
        const entry = entries[index];
        const amount = numberValue(entry.amount);
        const date = entry.date || todayKey();

        const payload = {
          subcontractorId: String(target.sub?.id || target.key),
          userId: staffId,
          subcontractorName: target.subName,
          userName: target.subName,
          vendor: target.subName,
          property: target.subName,
          amount,
          amountPaid: amount,
          date,
          datePaid: new Date(`${date}T12:00:00`),
          method: entry.method || "Other",
          reference: entry.reference || "",
          note: entry.note || "",
          jobId,
          occurrenceKey,
          jobLabel,
          payFrequency: isPlan ? frequency : "once",
          ...(isPlan ? { scheduleId, scheduleIndex: index, scheduleCount: entries.length } : {}),
          category: "Subcontractor",
          effect: "subtract",
          sourcePage: "JobEstimateSpreadsheet",
          createdAt: new Date().toISOString(),
        };

        const refDoc = await addDoc(collection(db, "subcontractorPay"), payload);
        const txRef = await addDoc(collection(db, "transactions"), {
          date,
          type: "expense",
          amount,
          category: "Subcontractor",
          vendor: target.subName,
          memo:
            payload.note ||
            `Subcontractor payment${payload.reference ? ` - ${payload.reference}` : ""}${
              jobLabel ? ` (${jobLabel})` : ""
            }`,
          jobId: jobId || null,
          method: payload.method,
          sourceCollection: "subcontractorPay",
          sourceId: refDoc.id,
          updatedAt: new Date(),
        });
        await updateDoc(doc(db, "subcontractorPay", refDoc.id), { txId: txRef.id });

        created.push({
          id: refDoc.id,
          ...payload,
          txId: txRef.id,
          sourceCollection: "subcontractorPay",
        });
      }
    } catch (error) {
      console.error("Subcontractor payment did not save:", error);
      alert(
        created.length
          ? `Only ${created.length} of ${entries.length} payments saved. The rest did not — check Payment History before trying again.`
          : "The payment did not save. Check your connection and try again."
      );
    }

    if (created.length) {
      setSubcontractorPayments((prev) => [...prev, ...created]);
    }
    return created;
  };

  // Record Payment inside a job's Sub Pay popup.
  const handleJobSubPaymentSubmit = async (form) => {
    const job = subPayJob;
    if (!job) return false;
    const target = resolvePaymentTarget(form);
    if (!target) {
      alert("Pick the subcontractor you paid.");
      return false;
    }

    setSubPaySaving(true);
    try {
      const created = await writeSubPayments(target, form.entries, job, form.frequency);
      if (!created.length) return false;

      // Make sure this job's Sub Pay covers what was paid to this sub on it.
      const draft = JSON.parse(JSON.stringify(subPayDraft)).filter(
        (line) => subPayLineName(line) || subPayLineTotal(line) > 0 || line.__legacyAmount
      );

      let lineIndex = draft.findIndex((line) => lineMatchesTarget(line, target));
      if (lineIndex === -1) {
        // Claim an empty line, or the unnamed amount an older job carried.
        lineIndex = draft.findIndex(
          (line) => !subPayLineName(line) && (line.__legacyAmount || subPayLineTotal(line) === 0)
        );
        if (lineIndex === -1) {
          draft.push({ subcontractorName: "", basePay: 0, changeOrders: [] });
          lineIndex = draft.length - 1;
        }
        draft[lineIndex].subcontractorName = target.subName;
      }
      if (!draft[lineIndex].subcontractorId && subIdForTarget(target)) {
        draft[lineIndex].subcontractorId = subIdForTarget(target);
      }

      const paidOnJob = [...subcontractorPayments, ...created]
        .filter((p) => paymentIsForJob(p, job) && paymentMatchesTarget(p, target))
        .reduce((sum, p) => sum + numberValue(p.amount), 0);
      const lineTotal = subPayLineTotal(draft[lineIndex]);
      const shortBy = Math.round((paidOnJob - lineTotal) * 100) / 100;
      if (shortBy > 0) {
        draft[lineIndex].basePay = Math.round((numberValue(draft[lineIndex].basePay) + shortBy) * 100) / 100;
      }

      const techNames = draft
        .filter((line) => subPayLineName(line))
        .map((line) => {
          const lineTarget = lineMatchesTarget(line, target)
            ? target
            : findPayableTarget(line.subcontractorName, line.subcontractorId);
          return lineTarget ? techNameForTarget(lineTarget) : subPayLineName(line);
        });

      const { cleaned } = await persistJobSubPay(job, draft, techNames);
      setSubPayDraft(cleaned);
      return true;
    } catch (error) {
      console.error("Job sub pay did not update after the payment:", error);
      alert(
        "The payment saved, but this job's Sub Pay / Tech Assign did not update. Click Save in this popup to try again."
      );
      return true;
    } finally {
      setSubPaySaving(false);
    }
  };

  // Record Payment in the Subcontractor Payables panel (overall balance).
  const handlePayablesPaymentSubmit = async (form) => {
    const target = resolvePaymentTarget(form);
    if (!target) {
      alert("Pick the subcontractor you paid.");
      return false;
    }
    setSubPaySaving(true);
    try {
      const created = await writeSubPayments(target, form.entries, null, form.frequency);
      if (created.length > 1) {
        alert(`Recorded ${created.length} payments to ${target.subName}.`);
      }
      if (created.length) setPayablesPickKey(target.key);
      return created.length > 0;
    } finally {
      setSubPaySaving(false);
    }
  };

  const deleteSubPayment = async (payment) => {
    if (!payment?.id) return;
    const collectionName = payment.sourceCollection || "subcontractorPay";
    const planMates = payment.scheduleId
      ? subcontractorPayments.filter(
          (p) => p.scheduleId === payment.scheduleId && (p.sourceCollection || "subcontractorPay") === collectionName
        )
      : [];

    const ok = window.confirm(
      `Delete the ${money(payment.amount)} payment to ${payment.subcontractorName || "this subcontractor"} dated ${payment.date || "(no date)"}?` +
        (payment.jobId
          ? "\n\nThe job's Sub Pay amount stays the same. Change it in that job's Sub Pay popup if needed."
          : "")
    );
    if (!ok) return;

    let toDelete = [payment];
    if (planMates.length > 1) {
      const all = window.confirm(
        `This payment is part of a plan of ${planMates.length} payments.\n\nOK = Delete all ${planMates.length}\nCancel = Delete only this one`
      );
      if (all) toDelete = planMates;
    }

    setDeletingSubPaymentId(payment.id);
    const removed = [];
    try {
      for (const item of toDelete) {
        await deleteDoc(doc(db, collectionName, item.id));
        removed.push(item.id);
        if (item.txId) {
          try {
            await deleteDoc(doc(db, "transactions", item.txId));
          } catch (txError) {
            console.warn("Payment deleted, but its transaction was not:", txError);
          }
        }
      }
    } catch (error) {
      console.error("Could not delete payment:", error);
      alert(
        removed.length
          ? `Deleted ${removed.length} of ${toDelete.length}. The rest are still there.`
          : "That payment was not deleted. Try again."
      );
    } finally {
      if (removed.length) {
        const gone = new Set(removed);
        setSubcontractorPayments((prev) =>
          prev.filter(
            (p) => !(gone.has(p.id) && (p.sourceCollection || "subcontractorPay") === collectionName)
          )
        );
      }
      setDeletingSubPaymentId("");
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading job spreadsheet..." />;
  }

  return (
    <div className="jes-page" style={{ padding: 20 }}>
      {busyMessage && <LoadingOverlay message={busyMessage} />}
      <h1>Job Estimate Spreadsheet</h1>
      <div style={{ marginTop: -6, marginBottom: 12, color: "#475569", fontWeight: 600 }}>
        Priority Pest Defense LLC
      </div>
      <button
        type="button"
        onClick={loadMissingTimeVerifications}
        style={{ marginBottom: 12, marginRight: 10, background: "#0369a1", color: "white", border: "none", borderRadius: 6, padding: "9px 14px", fontWeight: 700, cursor: "pointer" }}
      >
        Verify Missing Clock Time
      </button>
      <button
        type="button"
        onClick={() => setShowSubPayables((prev) => !prev)}
        style={{ marginBottom: 12, background: "#7c3aed", color: "white", border: "none", borderRadius: 6, padding: "9px 14px", fontWeight: 700, cursor: "pointer" }}
      >
        {showSubPayables ? "Hide Subcontractor Payables" : "Show Subcontractor Payables"}
      </button>

      {showTimeVerification && (
        <Popup width={1050} onClose={() => setShowTimeVerification(false)}>
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
              <div>
                <h2 style={{ margin: 0 }}>Verify Missing Clock Time</h2>
                <p style={{ margin: "6px 0 14px", color: "#475569" }}>
                  These employees were assigned to jobs on past dates but have no work log for that date. Click Yes only after confirming they worked. Saving a job never adds time automatically.
                </p>
              </div>
              <button type="button" onClick={() => setShowTimeVerification(false)}>Close</button>
            </div>

            {loadingTimeVerification ? (
              <p>Comparing tech assignments with Clock Time...</p>
            ) : missingTimeRows.length === 0 ? (
              <div style={{ padding: 18, background: "#dcfce7", borderRadius: 8, color: "#166534", fontWeight: 700 }}>
                No missing assigned workdays were found through today.
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
                  <thead>
                    <tr><th>Date</th><th>Employee / Sub</th><th>Assigned Jobs</th><th>Hours</th><th>Verify</th></tr>
                  </thead>
                  <tbody>
                    {missingTimeRows.map((row) => (
                      <tr key={row.key}>
                        <td>{row.dateKey}</td>
                        <td>{row.userName}</td>
                        <td>{row.jobs.map((job) => `${job.siteName || "Job"}${job.wo ? ` (WO ${job.wo})` : ""}`).join(", ")}</td>
                        <td>8.00</td>
                        <td>
                          <button
                            type="button"
                            disabled={busyAction === `approveTime-${row.key}`}
                            onClick={() => approveMissingTimeRow(row)}
                            style={{ background: "#15803d", color: "white", border: 0, borderRadius: 6, padding: "7px 12px", fontWeight: 700, cursor: "pointer" }}
                          >
                            Yes, Add to Clock Time
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Popup>
      )}

      {showSubPayables && (
        <div className="subpay-panel">
          <h2>Subcontractor Payables</h2>
          <p className="subpay-muted">
            Owed comes from each job's Sub Pay (or hourly rate when a sub is assigned with no Sub Pay).
            Paid comes from every payment recorded here, in a job's Sub Pay popup, or on Contractor Pay.
          </p>

          {unlinkedSubPay.length > 0 && (
            <div className="subpay-alert" role="status">
              <strong>
                {money(unlinkedSubPay.reduce((sum, item) => sum + item.amount, 0))} of Sub Pay isn't tied to a
                subcontractor
              </strong>{" "}
              — it counts as job cost but can't show on anyone's balance. Open each job and pick who it was for.
              <ul>
                {unlinkedSubPay.slice(0, 12).map((item) => (
                  <li key={item.key}>
                    {item.label}: {money(item.amount)}{" "}
                    {item.open && (
                      <button type="button" className="subpay-link" onClick={item.open}>
                        Pick subcontractor
                      </button>
                    )}
                  </li>
                ))}
                {unlinkedSubPay.length > 12 && <li>…and {unlinkedSubPay.length - 12} more</li>}
              </ul>
            </div>
          )}

          <div className="subpay-table-wrap">
            <table className="subpay-table">
              <thead>
                <tr>
                  <th>Subcontractor</th>
                  <th className="subpay-num">Jobs w/ Sub Pay</th>
                  <th className="subpay-num">Worked Days</th>
                  <th className="subpay-num">Total Owed</th>
                  <th className="subpay-num">Total Paid</th>
                  <th className="subpay-num">Remaining Balance</th>
                  <th>Last Paid</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {subcontractorPayableRows.map((row) => (
                  <tr key={row.key}>
                    <td>
                      {row.subName}
                      {!row.onRoster && <small className="subpay-badge">not on roster</small>}
                    </td>
                    <td className="subpay-num">{row.jobCount}</td>
                    <td className="subpay-num">{row.workedDays}</td>
                    <td className="subpay-num">{money(row.owed)}</td>
                    <td className="subpay-num">{money(row.paid)}</td>
                    <td className={`subpay-num ${row.balance > 0.004 ? "subpay-owe" : "subpay-clear"}`}>
                      {money(row.balance)}
                    </td>
                    <td>{row.lastPaid || "—"}</td>
                    <td>
                      <button
                        type="button"
                        className="subpay-link"
                        onClick={() => {
                          setPayablesPickKey(row.key);
                          document
                            .getElementById("subpay-record-payment")
                            ?.scrollIntoView({ behavior: "smooth", block: "start" });
                        }}
                      >
                        Record payment
                      </button>
                    </td>
                  </tr>
                ))}
                {subcontractorPayableRows.length === 0 && (
                  <tr>
                    <td colSpan="8">No staff marked as Sub Contractor.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <h3 id="subpay-record-payment">Record Payment</h3>
          <div className="subpay-card">
            <SubPaymentEntry
              contractorOptions={payableContractorOptions}
              defaultContractorKey={payablesPickKey}
              busy={subPaySaving}
              submitLabel="Save Sub Payment"
              intro="Pays down the subcontractor's overall balance. To pay for a specific job, use that job's Sub Pay button. For a payroll plan, choose how often under Repeat and set the first (oldest) date."
              onSubmit={handlePayablesPaymentSubmit}
            />
          </div>

          <h3>Payment History</h3>
          <SubPaymentHistory
            payments={subcontractorPayments}
            contractorOptions={payableContractorOptions}
            matchesContractor={paymentMatchesKey}
            busyId={deletingSubPaymentId}
            onDelete={deleteSubPayment}
          />
        </div>
      )}

      <div style={{ marginBottom: 10, color: "#475569", fontWeight: 600 }}>
  Default range is the 1st of the current month through today. Use From/To dates to run a custom report.
</div>

      <div style={{ color: "#b91c1c", background: "#fee2e2", border: "2px solid #dc2626", borderRadius: 10, padding: 12, margin: "12px 0 18px", fontWeight: 700, lineHeight: 1.45 }}>
        IMPORTANT PAGE CONNECTIONS: Job Estimate Spreadsheet reads jobs (recurring visits are the same ones Calendar and Home show), users (Staff pay rates), Approved Invoices, deposits (Checks Received), job_totals, Tech Daily Pay, recurring_job_occurrences (per-visit edits made here), vendors, bulk_material_expenses, monthly_expenses, bulk_income, timeOff and subcontractor pay. A row's revenue is its Approved Invoice total when invoiced, otherwise its estimate. Labor uses Actual Tech Daily Pay when any is saved for that job/visit, otherwise the estimate. It does NOT read bankStatementEntries directly. Do not use deposits/checks to change the estimated contract total.
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>

      <button
  type="button"
  onClick={() => {
    setBulkMaterialForm(emptyBulkMaterialForm);
    setShowBulkMaterialPopup(true);
  }}
  style={{
    background: "#0f766e",
    color: "white",
    border: "none",
    borderRadius: 6,
    padding: "8px 12px",
    cursor: "pointer",
    fontWeight: "bold",
  }}
>
  + Add Bulk Material
</button>

<button
  type="button"
  onClick={() => {
    setMonthlyExpenseForm(emptyMonthlyExpenseForm);
    setEditingMonthlyExpense(null);
    setMonthlyExpenseEditEffectiveDate(todayKey());
    setShowMonthlyExpensePopup(true);
  }}
  style={{
    background: "#9333ea",
    color: "white",
    border: "none",
    borderRadius: 6,
    padding: "8px 12px",
    cursor: "pointer",
    fontWeight: "bold",
  }}
>
  + Monthly Expense
</button>

<button
  type="button"
  onClick={() => {
    setBulkIncomeForm(emptyBulkIncomeForm);
    setShowBulkIncomePopup(true);
  }}
  style={{
    background: "#16a34a",
    color: "white",
    border: "none",
    borderRadius: 6,
    padding: "8px 12px",
    cursor: "pointer",
    fontWeight: "bold",
  }}
>
  + Add Bulk Income
</button>

      <button
  style={{ background: showAllJobs ? "#16a34a" : "#93c5fd" }}
  onClick={() => {
    setShowAllJobs(true);
    setStatusFilter("");
    setNeedsSchedulingOnly(false);
    setHidePaidInFull(false);
    setFromDate(currentMonthStartKey());
    setToDate(todayKey());
  }}
>
  Show All Jobs
</button>

<button
  type="button"
  style={{ background: hidePaidInFull ? "#16a34a" : "#93c5fd" }}
  onClick={() => {
    setShowAllJobs(false);
    setHidePaidInFull((prev) => !prev);
  }}
>
  {hidePaidInFull ? "Paid In Full Hidden" : "Hide Paid In Full"}
</button>

<button
  style={{ background: statusFilter === "Active" ? "#16a34a" : "#93c5fd" }}
  onClick={() => {
    setShowAllJobs(false);
    setStatusFilter("Active");
    setNeedsSchedulingOnly(false);
  }}
>
  Active
</button>

<button
  style={{ background: statusFilter === "Completed" ? "#16a34a" : "#93c5fd" }}
  onClick={() => {
    setShowAllJobs(false);
    setStatusFilter("Completed");
    setNeedsSchedulingOnly(false);
  }}
>
  Completed
</button>

<button
  style={{ background: statusFilter === "Invoice Sent" ? "#16a34a" : "#93c5fd" }}
  onClick={() => {
    setShowAllJobs(false);
    setStatusFilter("Invoice Sent");
    setNeedsSchedulingOnly(false);
  }}
>
  Invoice Sent
</button>

<button
  style={{ background: needsSchedulingOnly ? "#f97316" : "#93c5fd" }}
  onClick={() => {
    setShowAllJobs(false);
    setNeedsSchedulingOnly(true);
    setStatusFilter("");
    setFromDate("");
    setToDate("");
  }}
>
  Needs Scheduling
</button>

<button
  onClick={() => {
    setShowAllJobs(false);
    setStatusFilter("");
    setFromDate(currentMonthStartKey());
    setToDate(todayKey());
    setNeedsSchedulingOnly(false);
    setSummaryRange("all");
    setSearch("");
    setSearchDraft("");
  }}
>
  Clear Sort
</button>

<select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
  <option value="wo">Sort by WO Number</option>
  <option value="siteName">Sort by Property Name</option>
  <option value="city">Sort by City</option>
  <option value="startDate">Sort by Start Date</option>
  <option value="serviceType">Sort by Service Type</option>
</select>

        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input
            placeholder="Type site, scope, or WO — press Enter"
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                runSearch();
              }
            }}
            style={{ padding: 8, minWidth: 260 }}
          />
          <BusyButton
            type="button"
            onClick={runSearch}
            isBusy={busyAction === "search"}
            disabled={!!busyMessage}
          >
            Search
          </BusyButton>
          {(search || searchDraft) && (
            <button
              type="button"
              onClick={() => {
                setSearch("");
                setSearchDraft("");
              }}
            >
              Clear
            </button>
          )}
          {search && (
            <span style={{ fontSize: 12, color: "#555" }}>
              Showing results for: <strong>{search}</strong>
            </span>
          )}
        </div>

<div style={{ display: "flex", gap: 8, alignItems: "center" }}>
  <label>From:</label>
  <input
    type="date"
    value={fromDate}
    onChange={(e) => {
      setFromDate(e.target.value);
      setNeedsSchedulingOnly(false);
      setSummaryRange("all");
    }}
    style={{ padding: 8 }}
  />

  <label>To:</label>
  <input
    type="date"
    value={toDate}
    onChange={(e) => {
      setToDate(e.target.value);
      setNeedsSchedulingOnly(false);
      setSummaryRange("all");
    }}
    style={{ padding: 8 }}
  />
</div>

        <select value={summaryRange} onChange={(e) => setSummaryRange(e.target.value)}>
          <option value="all">All Jobs Summary</option>
          <option value="week">This Week</option>
          <option value="month">This Month</option>
        </select>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 12,
          marginBottom: 20,
        }}
      >
        <SummaryBox title="Revenue (Invoice or Estimate)" value={money(totals.revenue)} note="Invoiced rows use the Approved Invoice total" />
        <SummaryBox title="Approved Invoices" value={money(totals.invoiced)} />
        <SummaryBox title="Estimated Revenue" value={money(totals.estimatedTotal)} />
        <SummaryBox title="Unmatched Bulk Income" value={money(totals.bulkIncome)} />
        <SummaryBox title="Estimated Materials" value={money(totals.material)} />
        <SummaryBox title="Receipt Expenses" value={money(totals.receipts)} />
        <SummaryBox title="Unmatched Bulk Materials" value={money(totals.bulkMaterialExpenses)} />
<SummaryBox title="Monthly Expenses" value={money(totals.monthlyExpenses)} />
<SummaryBox title="User Time Off" value={money(totals.timeOffLabor)} />
<SummaryBox title="Estimated Labor" value={money(totals.labor)} note="Staff rates x days, for the rows shown" />
<SummaryBox title="Actual Tech Daily Pay" value={money(totals.actualDailyPay)} note={`On these rows. Whole period: ${money(totals.periodDailyPay)}`} />
<SummaryBox title="Unassigned Daily Pay" value={money(totals.unassignedDailyPay)} note="Period pay not on a row shown (no job, or job hidden/deleted) - counted as a period expense" />
<SummaryBox title="Labor Used In Profit" value={money(totals.laborUsed)} note="Actual daily pay when saved, else estimate" />
<SummaryBox title="Sub Pay" value={money(totals.subPay)} />
<SummaryBox title="Warranty Spend" value={money(totals.warranty)} />
<SummaryBox title="Total Expenses" value={money(totals.expenses)} />
<SummaryBox title="Estimated Profit" value={money(totals.profit)} />
      </div>

      {timeOffLaborRows.length > 0 && (
        <div style={{ marginBottom: 20, overflowX: "auto" }}>
          <h3 style={{ marginBottom: 8 }}>User Time Off Labor Cost</h3>
          <table className="styled-table" style={{ minWidth: 850 }}>
            <thead>
              <tr>
                <Th>User</Th>
                <Th>Type</Th>
                <Th>Start</Th>
                <Th>End</Th>
                <Th>Paid Days</Th>
                <Th>Hours/Day</Th>
                <Th>Hourly Rate</Th>
                <Th>Labor Cost</Th>
              </tr>
            </thead>
            <tbody>
              {timeOffLaborRows.map((row) => (
                <tr key={row.id}>
                  <td>{row.name}</td>
                  <td>{row.paidTimeType}</td>
                  <td>{row.startDate}</td>
                  <td>{row.endDate}</td>
                  <td>{row.paidDays}</td>
                  <td>{row.hoursPerDay}</td>
                  <td>{money(row.hourlyRate)}</td>
                  <td>{money(row.laborCost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1600 }}>
          <thead>
            <tr>
              <Th>Site Name</Th>
              <Th>City</Th>
              <Th>WO</Th>
              <Th>Assoc.</Th>
              <Th>Service Type</Th>
              <Th>Scope</Th>
              <Th>Status</Th>
              <Th>Warranty</Th>
              <Th>Estimated Days</Th>
              <Th>Labor Count</Th>
              <Th>Start Date</Th>
              <Th>End Date</Th>
              <Th>Tech Assign</Th>
              <Th>Estimated Material</Th>
<Th>Sub Pay</Th>
<Th>Receipts Total</Th>
              <Th>Estimated Total</Th>
              <Th>Approved Invoice</Th>
              <Th>Deposits / Payments</Th>
<Th>Checks Received</Th>
              <Th>Payment Flag</Th>
              <Th>Est. Labor Cost</Th>
              <Th>Actual Daily Pay</Th>
              <Th>Estimated Profit</Th>
              <Th>Save</Th>
            </tr>
          </thead>

          <tbody>
          {displayedJobs.map((job, rowIndex) => {
              const associationDepth = job.__associationDepth || 0;
              const recurringToggleId = job.__recurringSourceId || job.id;
              const rowAssociatedChildren = getRowAssociatedChildren(job);
              const hasOpenBranch = !!expandedAssociatedJobs[job.id];
              const associationRow = job.__isAssociatedChild || hasOpenBranch;
              const branchColors = ["#e0f2fe", "#ecfeff", "#f0fdf4", "#fefce8", "#fdf2f8"];
              const rowBackground = editedRows[job.originalJobId || job.id]
                ? "#fff7ed"
                : associationRow
                ? branchColors[Math.min(associationDepth, branchColors.length - 1)]
                : job.__isRecurringOccurrence
                ? "#dbeafe"
                : rowIndex % 2 === 0
                ? "#ffffff"
                : "#e0f2fe";

              return (
                <tr
  key={job.id}
  style={{
    background: rowBackground,
    borderLeft: associationRow ? `${Math.max(6, 6 + associationDepth * 3)}px solid #0f766e` : undefined,
  }}
>
<Td style={{ paddingLeft: 8 + associationDepth * 22 }}>
  {rowAssociatedChildren.length > 0 && !job.__isRecurringOccurrence && (
    <button
      type="button"
      onClick={() =>
        setExpandedAssociatedJobs((prev) => ({
          ...prev,
          [job.id]: !prev[job.id],
        }))
      }
      style={{
        marginRight: 8,
        background: expandedAssociatedJobs[job.id] ? "#0f766e" : "#64748b",
        color: "white",
        border: "none",
        borderRadius: 4,
        padding: "4px 8px",
        cursor: "pointer",
        fontWeight: 700,
      }}
    >
      {expandedAssociatedJobs[job.id] ? "▼" : "▶"}
    </button>
  )}

  {job.__isRecurringMain ? (
    <button
      type="button"
      onClick={() =>
        setExpandedRecurringJobs((prev) => ({
          ...prev,
          [recurringToggleId]: !prev[recurringToggleId],
        }))
      }
      style={{
        marginRight: 8,
        background: "#2563eb",
        color: "white",
        border: "none",
        borderRadius: 4,
        padding: "4px 8px",
        cursor: "pointer",
      }}
    >
      {expandedRecurringJobs[recurringToggleId] ? "Hide Dates" : "Show Dates"}
    </button>
  ) : null}

  <strong>
    {job.__isRecurringOccurrence ? "↳ " : job.__isAssociatedChild ? "└─ " : ""}
    {job.siteName}
  </strong>

  {job.__isAssociatedChild && (
    <div style={{ fontSize: 12, color: "#0f766e", marginTop: 4, fontWeight: 700 }}>
      Branch level {associationDepth} under WO {getRowControllerWO(job)}
    </div>
  )}

  {expandedAssociatedJobs[job.id] && rowAssociatedChildren.length > 0 && (
    <div style={{ fontSize: 12, color: "#0f766e", marginTop: 4, fontWeight: 700 }}>
      Branch open — showing {rowAssociatedChildren.length} direct child job(s)
    </div>
  )}

  {job.__isRecurringMain && (
    <div style={{ fontSize: 12, color: "#475569", marginTop: 4 }}>
      Recurring group — master date {job.__recurringMasterDate || job.startDate || ""}; {job.__recurringDates?.length || 0} date(s) in range
    </div>
  )}

  {job.__isRecurringOccurrence && (
    <div style={{ fontSize: 12, color: "#475569", marginTop: 4 }}>
      Recurring date
    </div>
  )}
</Td>
                  <Td>{job.city}</Td>
                  <Td>
  <strong>{job.__isAssociatedChild ? "└─ " : ""}{job.wo}</strong>

  {getWOGroupJobs(job).length > 1 && (
    <div style={{ fontSize: 12, color: "#475569", marginTop: 4 }}>
      Branch Total From: {Array.from(new Set(getWOGroupJobs(job).map((j) => j.wo).filter(Boolean))).sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true })).join(", ")}
      <br />
      Branch Estimated: {money(groupedEstimatedTotal(job))}
      <br />
      Branch Deposits / Payments: {money(groupedPaymentTotal(job))}
      <br />
      Branch Checks Received: {money(checksTotal(job))}
      <br />
      Branch Receipts: {money(groupedReceiptsTotal(job))}
      <br />
      Branch Estimated Material: {money(groupedMaterialTotal(job))}
      <br />
      Branch Sub Pay: {money(groupedSubPayTotal(job))}
      <br />
      Branch Invoiced: {money(groupedInvoicedTotal(job))}
      <br />
      Branch Est. Labor: {money(groupedLaborCost(job))}
      <br />
      Branch Actual Daily Pay: {money(groupedActualDailyPay(job))}
      <br />
      Branch Estimated Profit: {money(groupedProfitTotal(job))}
      {groupedWarrantyCost(job) > 0 && (
        <>
          <br />
          Warranty Spend: {money(groupedWarrantyCost(job))}
        </>
      )}
    </div>
  )}
</Td>

<Td>
  <div style={{ minWidth: 190 }}>
    <strong>{getAssociatedJobCount(job)}</strong> total below
    {getAssociatedChildren(job).length > 0 && (
      <button
        type="button"
        onClick={() =>
          setExpandedAssociatedJobs((prev) => ({
            ...prev,
            [job.id]: !prev[job.id],
          }))
        }
        style={{ marginLeft: 6, fontSize: 12 }}
      >
        {expandedAssociatedJobs[job.id] ? "Hide Branch" : "Show Branch"}
      </button>
    )}
    <br />
    <span style={{ fontSize: 12, color: "#475569" }}>
      Direct: {getAssociatedChildren(job).length}
    </span>
    {getAssociatedChildren(job).length > 0 && (
      <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 4 }}>
        {getAssociatedChildren(job).map((child) => (
          <div
            key={child.id}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 6,
              padding: "4px 6px",
              background: "#fff7ed",
              border: "1px solid #fed7aa",
              borderRadius: 6,
              fontSize: 11,
            }}
          >
            <span>Child WO {child.wo || "No WO"}</span>
            <button
              type="button"
              onClick={() => removeJobAssociation(job, child.id)}
              style={{
                fontSize: 11,
                color: "#dc2626",
                background: "white",
                border: "1px solid #fecaca",
                borderRadius: 4,
                padding: "2px 5px",
                cursor: "pointer",
              }}
            >
              Unlink
            </button>
          </div>
        ))}
      </div>
    )}
    <br />
    <button type="button" onClick={() => openAssociatePopup(job)} style={{ fontSize: 12, marginTop: 4 }}>
      Add Child Job
    </button>
    {job.parentJobId && (
      <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>
        Parent WO: {associationTree.byId[job.parentJobId]?.wo || ""}
        <br />
        <button
          type="button"
          onClick={() => removeJobAssociation(associationTree.byId[job.parentJobId], job.id)}
          style={{
            fontSize: 11,
            color: "white",
            background: "#dc2626",
            border: "none",
            borderRadius: 4,
            padding: "3px 6px",
            marginTop: 4,
            cursor: "pointer",
          }}
        >
          Unlink From Parent
        </button>
      </div>
    )}
    {getAssociatedChildren(job).length > 0 && (
      <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>
        This row tallies itself + everything below it.
      </div>
    )}
  </div>
</Td>

                  <Td>
                    <select
                      value={job.serviceType}
                      onChange={(e) => updateRow(job.id, "serviceType", e.target.value)}
                    >
                      <option value="">Select</option>
                      {withCurrentValue(serviceTypes, job.serviceType).map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </Td>

                  <Td>
                    <div style={{ width: 280, whiteSpace: "pre-wrap" }}>
                      {job.scope}
                    </div>
                  </Td>

                  <Td>
                    <select
                      value={job.status}
                      onChange={(e) => updateRow(job.id, "status", e.target.value)}
                    >
                      {statusOptions.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </Td>

                  <Td>
                    <button
                      type="button"
                      onClick={() => updateRow(job.id, "isWarranty", !job.isWarranty)}
                      style={{
                        background: job.isWarranty ? "#f97316" : "#e5e7eb",
                        color: job.isWarranty ? "white" : "#111827",
                        border: "1px solid #cbd5e1",
                        borderRadius: 6,
                        padding: "6px 10px",
                        cursor: "pointer",
                        fontWeight: 700,
                      }}
                    >
                      {job.isWarranty ? "Warranty" : "Mark Warranty"}
                    </button>
                    {job.isWarranty && (
                      <div style={{ fontSize: 12, color: "#9a3412", marginTop: 4 }}>
                        Spend: {money(job.warrantyCost)} · Profit: $0.00
                      </div>
                    )}
                  </Td>

                  <Td>
                    <CellInput
                      type="number"
                      value={job.estimatedDays}
                      onChange={(v) => updateRow(job.id, "estimatedDays", v)}
                    />
                  </Td>

                  <Td>
  <strong>{hasBranchTotals(job) ? groupedLaborCount(job) : (Array.isArray(job.techAssign) ? job.techAssign.filter(Boolean).length : 0)}</strong>
  {hasBranchTotals(job) && (
    <div style={branchOwnLabelStyle}>This WO only: {Array.isArray(job.techAssign) ? job.techAssign.filter(Boolean).length : 0}</div>
  )}
</Td>

                  <Td>
                    <CellInput
                      type="date"
                      value={job.startDate}
                      onChange={(v) => updateRow(job.id, "startDate", v)}
                    />
                  </Td>

                  <Td>
                    <CellInput
                      type="date"
                      value={job.endDate}
                      onChange={(v) => updateRow(job.id, "endDate", v)}
                    />
                  </Td>

                  <Td>
  <div style={{ minWidth: 260 }}>
    {(!Array.isArray(job.techAssign) || job.techAssign.length === 0) && (
      <div style={{ fontSize: 12, color: "#64748b", marginBottom: 6 }}>
        No techs assigned
      </div>
    )}

    {(Array.isArray(job.techAssign) ? job.techAssign : []).map((techName, index) => (
      <div
        key={index}
        style={{
          display: "flex",
          gap: 6,
          marginBottom: 6,
          alignItems: "center",
        }}
      >
        <select
          value={techName || ""}
          onChange={(e) => updateTechDropdown(job.id, index, e.target.value, job.techAssign)}
          style={{ width: "100%" }}
        >
          <option value="">Select Tech {index + 1}</option>
          {typeof techName === "string" && techName && !staff.some((tech) => tech.name === techName) && (
            <option value={techName}>{techName}</option>
          )}
          {staff.map((tech) => (
  <option key={tech.id} value={tech.name}>
    {tech.name}
  </option>
))}
        </select>

        <button
          type="button"
          onClick={() => removeTechAssign(job.id, index, job.techAssign)}
          style={{
            background: "#dc2626",
            color: "white",
            border: "none",
            borderRadius: 4,
            padding: "5px 8px",
            cursor: "pointer",
          }}
        >
          X
        </button>
      </div>
    ))}

    <button
      type="button"
      onClick={() => addTechAssign(job.id, job.techAssign)}
      style={{
        background: "#2563eb",
        color: "white",
        border: "none",
        borderRadius: 4,
        padding: "6px 10px",
        cursor: "pointer",
      }}
    >
      + Add Tech
    </button>
  </div>
</Td>

                  <Td>
                    {hasBranchTotals(job) && (
                      <div style={{ fontWeight: 700, marginBottom: 6 }}>
                        Branch: {money(groupedMaterialTotal(job))}
                      </div>
                    )}
                    <CellInput
                      type="number"
                      value={job.estimatedMaterial}
                      onChange={(v) => updateRow(job.id, "estimatedMaterial", v)}
                    />
                    {hasBranchTotals(job) && (
                      <div style={branchOwnLabelStyle}>This WO only: {money(job.estimatedMaterial)}</div>
                    )}
                  </Td>
                  <Td>
  <strong>{money(hasBranchTotals(job) ? groupedSubPayTotal(job) : job.subPay)}</strong>
  {hasBranchTotals(job) && (
    <div style={branchOwnLabelStyle}>This WO only: {money(job.subPay)}</div>
  )}
  <br />
  <button
    type="button"
    onClick={() => openSubPayPopup(job)}
    style={{
      marginTop: 6,
      background: "#7c3aed",
      color: "white",
      border: "none",
      borderRadius: 4,
      padding: "6px 10px",
      cursor: "pointer",
    }}
  >
    Sub Pay / CO
  </button>
</Td>
                 
<Td>
  <strong>{money(hasBranchTotals(job) ? groupedReceiptsTotal(job) : job.receiptsTotal)}</strong>
  {hasBranchTotals(job) && (
    <div style={branchOwnLabelStyle}>This WO only: {money(job.receiptsTotal)}</div>
  )}

  <br />

  <button
    type="button"
    onClick={() => {
      setReceiptJob(job);
      setReceiptAmount("");
      setReceiptNote("");
      setReceiptFile(null);
      setEditingReceiptIndex(null);
      setShowReceiptPopup(true);
    }}
    style={{
      marginTop: 6,
      background: "#2563eb",
      color: "white",
      border: "none",
      borderRadius: 4,
      padding: "6px 10px",
      cursor: "pointer",
    }}
  >
    + Add Receipt
  </button>

  {Array.isArray(job.receipts) && job.receipts.length > 0 && (
    <div style={{ marginTop: 6, fontSize: 12 }}>
      {job.receipts.map((receipt, index) => (
        <div key={index} style={{ marginBottom: 8 }}>
          <strong>{money(receipt.amount)}</strong>
          {receipt.note ? ` — ${receipt.note}` : ""}

          {receipt.fileURL && (
            <div>
              <a href={receipt.fileURL} target="_blank" rel="noopener noreferrer">
                View Receipt
              </a>
            </div>
          )}

          <button
            type="button"
            onClick={() => {
              setReceiptJob(job);
              setReceiptAmount(receipt.amount || "");
              setReceiptNote(receipt.note || "");
              setReceiptFile(null);
              setEditingReceiptIndex(index);
              setShowReceiptPopup(true);
            }}
            style={{ fontSize: 11, marginRight: 4 }}
          >
            Edit
          </button>

          <button
            type="button"
            onClick={() => deleteReceipt(job, index)}
            style={{ fontSize: 11, color: "#dc2626" }}
          >
            Delete
          </button>
        </div>
      ))}
    </div>
  )}
</Td>

                  <Td>
  <CellInput
    type="number"
    value={job.estimatedTotal}
    onChange={(v) => updateRow(job.id, "estimatedTotal", v)}
  />

{hasBranchTotals(job) && (
  <div style={{ fontSize: 12, color: "#475569", marginTop: 4 }}>
    Branch estimated total: <strong>{money(groupedEstimatedTotal(job))}</strong>
    <br />
    This WO only: {money(job.estimatedTotal)}
  </div>
)}
                  </Td>

                  <Td>
                    {(() => {
                      const rowInvoices = getRowInvoices(job);
                      if (!rowInvoices.length) {
                        return (
                          <div style={{ fontSize: 12, color: "#64748b", minWidth: 150 }}>
                            Not invoiced yet
                            <br />
                            Profit uses the estimate.
                          </div>
                        );
                      }
                      return (
                        <div style={{ minWidth: 170 }}>
                          <strong>{money(job.invoicedTotal)}</strong>
                          {rowInvoices.map((invoice) => (
                            <div key={invoice.id} style={{ fontSize: 12, marginTop: 4 }}>
                              #{invoiceNumberOf(invoice)} · {money(invoiceTotal(invoice))} ·{" "}
                              <span
                                style={{
                                  fontWeight: 700,
                                  color:
                                    invoiceStatusOf(invoice) === "Paid"
                                      ? "#16a34a"
                                      : invoiceStatusOf(invoice) === "Sent"
                                      ? "#2563eb"
                                      : "#92400e",
                                }}
                              >
                                {invoiceStatusOf(invoice)}
                              </span>
                            </div>
                          ))}
                          <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>
                            Revenue for this row = invoice total.
                          </div>
                        </div>
                      );
                    })()}
                    {hasBranchTotals(job) && groupedInvoicedTotal(job) > 0 && (
                      <div style={branchOwnLabelStyle}>Branch invoiced: {money(groupedInvoicedTotal(job))}</div>
                    )}
                  </Td>

                  <Td>
                  <strong>
  {money(getWOGroupJobs(job).length > 1 ? groupedPaymentTotal(job) : singleJobPaymentTotal(job))}
</strong>
  <br />
  <button
  type="button"
  onClick={() => {
    setPaymentJob(job);
    setPaymentType("Deposit");
    setPaymentAmount("");
    setPaymentDate("");
    setEditingPaymentIndex(null);
    setShowPaymentPopup(true);
  }}
>
  + Add Deposit / Payment
</button>

  {Array.isArray(job.payments) && job.payments.length > 0 && (
    <div style={{ fontSize: 12, marginTop: 6 }}>
      {job.payments.map((payment, index) => (
  <div key={index} style={{ marginBottom: 6 }}>
    {payment.type}: {money(payment.amount)}
    {payment.date ? ` - ${payment.date}` : ""}

    <br />

    <button
      type="button"
      onClick={() => {
        setPaymentJob(job);
        setPaymentType(payment.type || "Deposit");
        setPaymentAmount(payment.amount || "");
        setPaymentDate(payment.date || "");
        setEditingPaymentIndex(index);
        setShowPaymentPopup(true);
      }}
      style={{ fontSize: 11, marginTop: 3 }}
    >
      Edit
    </button>

    <button
  type="button"
  onClick={() => deletePayment(job, index)}
  style={{ fontSize: 11, marginTop: 3, marginLeft: 4, color: "#dc2626" }}
>
  Delete
</button>

  </div>
))}
    </div>
  )}
</Td>

<Td>
<strong>
  {money(checksTotal(job))}
</strong>
  <br />
  <button
  type="button"
  onClick={() => {
    setCheckJob(job);
    setCheckVendor(DEFAULT_CHECK_VENDOR);
    setCheckVendorOther("");
    setCheckNumber("");
    setCheckDate("");
    setCheckReference("");
    setCheckAmount("");
    setCheckPaymentType("Deposit");
    setEditingCheckIndex(null);
    setShowCheckPopup(true);
  }}
>
  + Add Check
</button>

<br />

<button
  type="button"
  onClick={() => openSplitCheckPopup(job)}
  style={{
    marginTop: 6,
    background: "#7c3aed",
    color: "white",
    border: "none",
    borderRadius: 4,
    padding: "6px 10px",
    cursor: "pointer",
  }}
>
  + Split Check
</button>

  <div style={{ fontSize: 12, marginTop: 6 }}>
  Balance Due: {money(checkBalanceDue(job))}
  </div>

  {getJobDeposits(job).length > 0 && (
  <div style={{ fontSize: 12, marginTop: 6 }}>
    {getJobDeposits(job).map((check, index) => (
  <div key={index} style={{ marginBottom: 6 }}>
    {check.__applied ? (
      <>
        {check.paymentType}: {money(check.amount)}
        <div style={{ fontSize: 11, color: "#64748b" }}>
          {check.description}. Change it on Approved Invoices / Deposits.
        </div>
      </>
    ) : (
      <>
    {check.paymentType || "Other"}: {check.company} #{check.checkNumber} - {money(check.amount)}
    {check.__listOnly && (
      <div style={{ fontSize: 11, color: "#64748b" }}>
        Recorded on this visit - counted in the paid amount above, not added again.
      </div>
    )}

    <br />

    <button
      type="button"
      onClick={() => {
        if (check.splitCheck && check.checkGroupId) {
          const groupDeposits = deposits.filter(
            (deposit) => deposit.checkGroupId === check.checkGroupId
          );
          openSplitCheckPopup(job, groupDeposits.length ? groupDeposits : [check]);
          return;
        }

        setCheckJob(job);
        setVendorChoice(check.company, setCheckVendor, setCheckVendorOther);
setCheckNumber(check.checkNumber || "");
setCheckDate(check.date || "");
setCheckReference(check.invoiceNumber2 || "");
setCheckAmount(check.amount || "");
setCheckPaymentType(check.paymentType || "Other");
        setEditingCheckIndex(index);
        setShowCheckPopup(true);
      }}
      style={{ fontSize: 11, marginTop: 3 }}
    >
      Edit
    </button>
    <button
  type="button"
  onClick={() => deleteCheckReceived(check.id)}
  style={{ fontSize: 11, marginTop: 3, marginLeft: 4, color: "#dc2626" }}
>
  Delete
</button>
      </>
    )}
  </div>
))}
    </div>
  )}
</Td>

<Td>
  {(() => {
    const status = checkPaymentStatus(job);
    return (
      <div>
        <span
          style={{
            display: "inline-block",
            background: status.color,
            color: "white",
            borderRadius: 999,
            padding: "4px 8px",
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          {status.flag ? "⚠ " : "✓ "}{status.label}
        </span>
        <div style={{ fontSize: 12, marginTop: 6 }}>
          Contract: {money(contractTotalForJob(job))}
          <br />
          Received: {money(checksTotal(job))}
          <br />
          Balance: {money(checkBalanceDue(job))}
        </div>
        {numberValue(job.estimatedTotal) === 0 && contractTotalForJob(job) === 0 && status.label !== "Paid In Full" && (
          <BusyButton
            onClick={() => markZeroDollarJobPaidInFull(job)}
            isBusy={busyAction === `markPaid-${job.id}`}
            disabled={!!busyMessage}
            style={{ marginTop: 8, fontSize: 12 }}
          >
            Mark $0 Paid In Full
          </BusyButton>
        )}
      </div>
    );
  })()}
</Td>

<Td>
  <strong>
    {money(getWOGroupJobs(job).length > 1
          ? groupedLaborCost(job)
          : job.estimatedLaborCost)}
  </strong>
  <br />
  <button
    type="button"
    onClick={() => openCommissionPopup(job)}
    style={{
      marginTop: 6,
      background: "#0f766e",
      color: "white",
      border: "none",
      borderRadius: 4,
      padding: "6px 10px",
      cursor: "pointer",
    }}
  >
    Staff Pay / %
  </button>
</Td>

<Td>
  {(() => {
    const actual = hasBranchTotals(job) ? groupedActualDailyPay(job) : numberValue(job.actualDailyPay);
    return (
      <div style={{ minWidth: 140 }}>
        <strong style={{ color: actual > 0 ? "#0f766e" : "#64748b" }}>{money(actual)}</strong>
        {hasBranchTotals(job) && (
          <div style={branchOwnLabelStyle}>This row only: {money(job.actualDailyPay)}</div>
        )}
        <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>
          {numberValue(job.actualDailyPay) > 0
            ? "From Tech Daily Pay - used in profit instead of the estimate."
            : "No Tech Daily Pay saved - profit uses the estimate."}
        </div>
      </div>
    );
  })()}
</Td>

                  <Td
                    style={{
                      fontWeight: "bold",
                      color: numberValue(hasBranchTotals(job) ? groupedProfitTotal(job) : job.estimatedProfit) < 0 ? "red" : "green",
                    }}
                  >
                  {money(hasBranchTotals(job) ? groupedProfitTotal(job) : job.estimatedProfit)}
                  </Td>

                  <Td>
                  {job.__isRecurringOccurrence ? (
  <BusyButton
    onClick={() => saveRecurringOccurrence(job)}
    isBusy={busyAction === `saveRecurring-${job.id}`}
    disabled={!!busyMessage}
  >
    Save This Date
  </BusyButton>
) : (
  <BusyButton
    onClick={() => saveRow(job)}
    isBusy={busyAction === `saveRow-${job.id}`}
    disabled={!!busyMessage}
  >
    Save Main
  </BusyButton>
)}
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showCommissionPopup && commissionJob && (
        <Popup
          width={980}
          onClose={() => {
            setShowCommissionPopup(false);
            setCommissionJob(null);
            setCommissionDraft([]);
          }}
        >
          <div>
            <h2>Staff Pay / Commission — WO {commissionJob.wo || ""}</h2>
            <p style={{ color: "#475569" }}>
              Estimated Total: <strong>{money(commissionJob.estimatedTotal)}</strong>.
              Blank overrides use the Staff-page default. Job overrides apply only to this staff member on this job.
            </p>

            <div style={{ overflowX: "auto" }}>
              <table className="styled-table" style={{ width: "100%", minWidth: 850 }}>
                <thead>
                  <tr>
                    <Th>Staff Member</Th>
                    <Th>Staff Default Method</Th>
                    <Th>Staff Default %</Th>
                    <Th>Job Method Override</Th>
                    <Th>Job % Override</Th>
                  </tr>
                </thead>
                <tbody>
                  {commissionDraft.map((item, index) => (
                    <tr key={`${item.staffId || item.staffName}-${index}`}>
                      <td>{item.staffName}</td>
                      <td>{PAY_METHOD_LABELS[item.defaultMethod] || "Hourly Only"}</td>
                      <td>{item.defaultRate !== "" ? `${item.defaultRate}%` : "None"}</td>
                      <td>
                        <select
                          value={item.payCalculationMethod || ""}
                          onChange={(e) =>
                            setCommissionDraft((prev) =>
                              prev.map((row, rowIndex) =>
                                rowIndex === index
                                  ? { ...row, payCalculationMethod: e.target.value }
                                  : row
                              )
                            )
                          }
                        >
                          <option value="">Use Staff Default</option>
                          <option value="hourly_only">Hourly Only</option>
                          <option value="greater_of_hourly_or_percentage">Greater of Hourly or Job %</option>
                          <option value="percentage_only">Job % Only</option>
                          <option value="hourly_plus_percentage">Hourly + Job %</option>
                        </select>
                      </td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.commissionRate}
                          onChange={(e) =>
                            setCommissionDraft((prev) =>
                              prev.map((row, rowIndex) =>
                                rowIndex === index
                                  ? { ...row, commissionRate: e.target.value }
                                  : row
                              )
                            )
                          }
                          placeholder="Use default"
                          style={{ width: 130 }}
                        />
                      </td>
                    </tr>
                  ))}
                  {commissionDraft.length === 0 && (
                    <tr>
                      <td colSpan="5">No staff are currently assigned to this job.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: 12, padding: 10, background: "#f8fafc", borderRadius: 6 }}>
              <strong>Rule:</strong> blank = use Staff default. To give commission only on this job,
              choose a percentage-based method and enter a Job % Override.
            </div>

            <div className="jes-popup-buttons" style={{ marginTop: 16 }}>
              <BusyButton
                onClick={saveCommissionOverrides}
                isBusy={busyAction === "saveCommissionOverrides"}
                disabled={!!busyMessage}
              >
                Save Staff Pay Overrides
              </BusyButton>
              <button
                type="button"
                onClick={() => {
                  setShowCommissionPopup(false);
                  setCommissionJob(null);
                  setCommissionDraft([]);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </Popup>
      )}

      {showBulkMaterialPopup && (
  <Popup width={760} onClose={() => setShowBulkMaterialPopup(false)}>
    <div>
      <h2>Add Bulk Material From Bank Statement</h2>

      <label>Date of Purchase</label>
      <input
        type="date"
        value={bulkMaterialForm.date}
        onChange={(e) =>
          setBulkMaterialForm((prev) => ({
            ...prev,
            date: e.target.value,
            jobId: "",
          }))
        }
      />

      <label>Store Name</label>
      <input
        type="text"
        value={bulkMaterialForm.storeName}
        onChange={(e) =>
          setBulkMaterialForm((prev) => ({
            ...prev,
            storeName: e.target.value,
          }))
        }
        placeholder="Example: Home Depot, Lowes, Sherwin Williams"
      />

      <label>Amount</label>
      <input
        type="number"
        value={bulkMaterialForm.amount}
        onChange={(e) =>
          setBulkMaterialForm((prev) => ({
            ...prev,
            amount: e.target.value,
          }))
        }
        placeholder="Amount from bank statement"
      />

      {materialReceiptMatches.length > 0 && (
        <div
          style={{
            background: "#fef3c7",
            border: "1px solid #f59e0b",
            padding: 10,
            borderRadius: 6,
            marginTop: 10,
            marginBottom: 10,
          }}
        >
          <strong>Possible receipt match found:</strong>

          {materialReceiptMatches.map((match, index) => (
            <div key={index} style={{ marginTop: 6, fontSize: 13 }}>
              {match.job.siteName} — WO {match.job.wo} — Receipt Date:{" "}
              {match.receiptDate} — {money(match.receipt.amount)}
            </div>
          ))}
        </div>
      )}

      <label>Possible Job Match</label>
      <select
        value={bulkMaterialForm.jobId}
        onChange={(e) =>
          setBulkMaterialForm((prev) => ({
            ...prev,
            jobId: e.target.value,
          }))
        }
      >
        <option value="">No job match / keep as unmatched expense</option>

        {materialPossibleJobs.map((job) => (
          <option key={job.id} value={job.id}>
            {job.startDate} — WO {job.wo} — {job.siteName} — {job.scope}
          </option>
        ))}
      </select>

      <div style={{ fontSize: 12, color: "#475569", marginTop: 4 }}>
        Showing jobs within 3 days of the purchase date.
      </div>

      <label>Note</label>
      <input
        type="text"
        value={bulkMaterialForm.note}
        onChange={(e) =>
          setBulkMaterialForm((prev) => ({
            ...prev,
            note: e.target.value,
          }))
        }
        placeholder="Optional note"
      />

      <div className="jes-popup-buttons">
        <BusyButton className="green-button" onClick={saveBulkMaterialExpense} isBusy={busyAction === "saveBulkMaterialExpense"} disabled={!!busyMessage}>
          Save Bulk Material
        </BusyButton>

        <button className="red-button" onClick={() => setShowBulkMaterialPopup(false)}>
          Cancel
        </button>
      </div>
    </div>
  </Popup>
)}

{showBulkIncomePopup && (
  <Popup width={760} onClose={() => setShowBulkIncomePopup(false)}>
    <div>
      <h2>Add Bulk Income</h2>

      <label>Date Received</label>
      <input
        type="date"
        value={bulkIncomeForm.date}
        onChange={(e) =>
          setBulkIncomeForm((prev) => ({
            ...prev,
            date: e.target.value,
            jobId: "",
          }))
        }
      />

      <label>Payer / Source</label>
      <input
        type="text"
        value={bulkIncomeForm.payerName}
        onChange={(e) =>
          setBulkIncomeForm((prev) => ({
            ...prev,
            payerName: e.target.value,
          }))
        }
        placeholder="Example: ACH Deposit, Check Deposit, Client Name"
      />

      <label>Amount</label>
      <input
        type="number"
        value={bulkIncomeForm.amount}
        onChange={(e) =>
          setBulkIncomeForm((prev) => ({
            ...prev,
            amount: e.target.value,
          }))
        }
      />

      <label>Income Type</label>
      <select
        value={bulkIncomeForm.incomeType}
        onChange={(e) =>
          setBulkIncomeForm((prev) => ({
            ...prev,
            incomeType: e.target.value,
          }))
        }
      >
        <option value="General Income">General Income</option>
        <option value="Deposit">Deposit</option>
        <option value="Final Payment">Final Payment</option>
        <option value="Progress Payment">Progress Payment</option>
        <option value="Reimbursement">Reimbursement</option>
        <option value="Refund">Refund</option>
        <option value="Credit">Credit</option>
        <option value="Other">Other</option>
      </select>

      <label>Possible Job Match</label>
      <select
        value={bulkIncomeForm.jobId}
        onChange={(e) =>
          setBulkIncomeForm((prev) => ({
            ...prev,
            jobId: e.target.value,
          }))
        }
      >
        <option value="">No job match / keep as date income</option>

        {bulkIncomePossibleJobs.map((job) => (
          <option key={job.id} value={job.id}>
            {job.startDate} — WO {job.wo} — {job.siteName} — {job.scope}
          </option>
        ))}
      </select>

      <div style={{ fontSize: 12, color: "#475569", marginTop: 4 }}>
        Showing jobs within 3 days of the income date.
      </div>

      <label>Description</label>
      <input
        type="text"
        value={bulkIncomeForm.description}
        onChange={(e) =>
          setBulkIncomeForm((prev) => ({
            ...prev,
            description: e.target.value,
          }))
        }
        placeholder="Optional description"
      />

      <div className="jes-popup-buttons">
        <BusyButton className="green-button" onClick={saveBulkIncome} isBusy={busyAction === "saveBulkIncome"} disabled={!!busyMessage}>
          Save Bulk Income
        </BusyButton>

        <button className="red-button" onClick={() => setShowBulkIncomePopup(false)}>
          Cancel
        </button>
      </div>
    </div>
  </Popup>
)}

{showMonthlyExpensePopup && (
  <Popup width={820} onClose={() => setShowMonthlyExpensePopup(false)}>
    <div>
      <h2>{editingMonthlyExpense ? "Edit Monthly Expense" : "Add Monthly Expense"}</h2>

      {editingMonthlyExpense && (
        <div
          style={{
            background: "#dbeafe",
            border: "1px solid #2563eb",
            padding: 10,
            borderRadius: 6,
            marginBottom: 10,
          }}
        >
          <strong>Important:</strong> Editing this will not change previous records.
          The old version will end the day before the new effective date.
        </div>
      )}

      {editingMonthlyExpense && (
        <>
          <label>Change Starts On</label>
          <input
            type="date"
            value={monthlyExpenseEditEffectiveDate}
            onChange={(e) => setMonthlyExpenseEditEffectiveDate(e.target.value)}
          />
        </>
      )}

      <label>Vendor</label>
      <select
        value={monthlyExpenseForm.vendorId}
        onChange={(e) => {
          const vendor = vendors.find((v) => v.id === e.target.value);

          setMonthlyExpenseForm((prev) => ({
            ...prev,
            vendorId: vendor?.id || "",
            vendorName: vendor?.companyName || "",
          }));
        }}
      >
        <option value="">Select Vendor</option>

        {vendors.map((vendor) => (
          <option key={vendor.id} value={vendor.id}>
            {vendor.companyName}
          </option>
        ))}
      </select>

      <label>Bill Type</label>
      <select
        value={monthlyExpenseForm.billType}
        onChange={(e) =>
          setMonthlyExpenseForm((prev) => ({
            ...prev,
            billType: e.target.value,
          }))
        }
      >
        {monthlyBillTypes.map((type) => (
          <option key={type} value={type}>
            {type}
          </option>
        ))}
      </select>

      {monthlyExpenseForm.billType === "Other" && (
        <>
          <label>Other Bill Type Name</label>
          <input
            type="text"
            value={monthlyExpenseForm.billTypeOther}
            onChange={(e) =>
              setMonthlyExpenseForm((prev) => ({
                ...prev,
                billTypeOther: e.target.value,
              }))
            }
            placeholder="Enter custom bill type"
          />
        </>
      )}

      <label>Description</label>
      <input
        type="text"
        value={monthlyExpenseForm.description}
        onChange={(e) =>
          setMonthlyExpenseForm((prev) => ({
            ...prev,
            description: e.target.value,
          }))
        }
        placeholder="Example: Verizon phones, truck loan, office rent"
      />

      <label>Amount</label>
      <input
        type="number"
        value={monthlyExpenseForm.amount}
        onChange={(e) =>
          setMonthlyExpenseForm((prev) => ({
            ...prev,
            amount: e.target.value,
          }))
        }
      />

      <label>Frequency</label>
      <select
        value={monthlyExpenseForm.frequency}
        onChange={(e) =>
          setMonthlyExpenseForm((prev) => ({
            ...prev,
            frequency: e.target.value,
          }))
        }
      >
        <option value="One Time">One Time</option>
        <option value="Weekly">Weekly</option>
        <option value="Monthly">Monthly</option>
        <option value="Quarterly">Quarterly</option>
        <option value="Yearly">Yearly</option>
      </select>

      <label>Start Date / Paid Date</label>
      <input
        type="date"
        value={monthlyExpenseForm.startDate}
        onChange={(e) =>
          setMonthlyExpenseForm((prev) => ({
            ...prev,
            startDate: e.target.value,
          }))
        }
      />

      <div style={{ fontSize: 12, color: "#475569", marginTop: 4 }}>
        Example: if monthly and paid on 05/10/2026, it will count on the 10th of each month.
      </div>

      <div className="jes-popup-buttons">
        <BusyButton className="green-button" onClick={saveMonthlyExpense} isBusy={busyAction === "saveMonthlyExpense"} disabled={!!busyMessage}>
          {editingMonthlyExpense ? "Save New Version" : "Save Monthly Expense"}
        </BusyButton>

        <button
          className="red-button"
          onClick={() => {
            setShowMonthlyExpensePopup(false);
            setEditingMonthlyExpense(null);
            setMonthlyExpenseForm(emptyMonthlyExpenseForm);
          }}
        >
          Cancel
        </button>
      </div>

      {monthlyExpenses.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <h3>Saved Monthly Expenses</h3>

          {monthlyExpenses
            .sort((a, b) => String(a.vendorName || "").localeCompare(String(b.vendorName || "")))
            .map((expense) => (
              <div
                key={expense.id}
                style={{
                  border: "1px solid #ddd",
                  padding: 10,
                  borderRadius: 6,
                  marginBottom: 8,
                  background: expense.endDate ? "#f1f5f9" : "#fff",
                }}
              >
                <strong>{expense.vendorName}</strong> — {expense.billType} —{" "}
                {money(expense.amount)} — {expense.frequency}
                {isBankPayrollOrSubMonthly(expense) && (
                  <div style={{ fontSize: 12, color: "#92400e", fontWeight: 700, marginTop: 2 }}>
                    Not counted in totals: this payroll / contractor pay is already counted in job labor or Sub Pay.
                  </div>
                )}
                <br />
                Start: {expense.startDate}
                {expense.endDate ? ` | Ended: ${expense.endDate}` : ""}
                {expense.description ? (
                  <>
                    <br />
                    {expense.description}
                  </>
                ) : null}

               {!expense.endDate && (
  <>
    <br />
    <button
      type="button"
      onClick={() => editMonthlyExpense(expense)}
      style={{ marginTop: 6, marginRight: 6 }}
    >
      Edit From New Date
    </button>

    <button
      type="button"
      onClick={() => deleteMonthlyExpenseFromDate(expense)}
      style={{
        marginTop: 6,
        marginRight: 6,
        background: "#f97316",
        color: "white",
        border: "none",
        borderRadius: 4,
        padding: "6px 10px",
        cursor: "pointer",
      }}
    >
      Delete From Date
    </button>

    <button
      type="button"
      onClick={() => deleteMonthlyExpenseTotal(expense)}
      style={{
        marginTop: 6,
        background: "#dc2626",
        color: "white",
        border: "none",
        borderRadius: 4,
        padding: "6px 10px",
        cursor: "pointer",
      }}
    >
      Delete Total Expense
    </button>
  </>
)}
              </div>
            ))}
        </div>
      )}
    </div>
  </Popup>
)}

      {showReceiptPopup && (
        <Popup width={560} onClose={() => setShowReceiptPopup(false)}>
          <div>
          <h2>{editingReceiptIndex !== null ? "Edit Receipt" : "Add Receipt"}</h2>

            <p>
              <strong>Job:</strong> {receiptJob?.siteName}
            </p>

            <label>Receipt Amount</label>
            <input
              type="number"
              value={receiptAmount}
              onChange={(e) => setReceiptAmount(e.target.value)}
              placeholder="Enter receipt amount"
            />

            <label>Receipt Note</label>
            <input
              type="text"
              value={receiptNote}
              onChange={(e) => setReceiptNote(e.target.value)}
              placeholder="Example: Home Depot materials"
            />

            <label>Upload Receipt Image/PDF</label>
            <input
              type="file"
              accept="image/*,.pdf"
              onChange={(e) => setReceiptFile(e.target.files?.[0] || null)}
            />

            <div className="jes-popup-buttons">
              <BusyButton
                className="green-button"
                onClick={saveReceipt}
                isBusy={busyAction === "saveReceipt" || savingReceipt}
                disabled={!!busyMessage || savingReceipt}
              >
                {editingReceiptIndex !== null ? "Update Receipt" : "Save Receipt"}
              </BusyButton>

              <button
                className="red-button"
                onClick={() => setShowReceiptPopup(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </Popup>
            )}

            {showAssociatePopup && (
  <Popup width={760} onClose={() => setShowAssociatePopup(false)}>
    <div>
      <h2>Add Child Job</h2>
      <p>
        <strong>Parent job:</strong> WO {associateJob?.wo} — {associateJob?.siteName}
      </p>
      <p style={{ fontSize: 13, color: "#475569" }}>
        The selected job will sit under this WO as a child branch. The parent totals will include the child and anything below that child.
      </p>

      <label>Type child WO number(s)</label>
      <textarea
        value={associateWOInput}
        onChange={(e) => {
          const value = e.target.value;
          setAssociateWOInput(value);

          const typedTokens = String(value || "")
            .split(/[\s,;]+/)
            .map((item) => item.trim().toLowerCase())
            .filter(Boolean);

          const matches = candidateChildJobs.filter((job) => {
            const wo = String(job.wo || "").trim().toLowerCase();
            const baseWO = getBaseWO(job.wo || "").toLowerCase();
            return typedTokens.includes(wo) || typedTokens.includes(baseWO);
          });

          setAssociateTargetJobIds(matches.map((job) => job.id));
          setAssociateTargetJobId(matches[0]?.id || "");
        }}
        onKeyDown={(e) => {
          if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
            e.preventDefault();
            associateJobs();
          }
        }}
        placeholder={"Example: 2222, 3333, 4444"}
        rows={3}
        style={{ width: "100%" }}
      />
      <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
        Add multiple WO numbers separated by commas, spaces, or new lines. Press Ctrl+Enter / Command+Enter, or click Add Selected Child Jobs.
      </div>

      <label style={{ marginTop: 10 }}>Or choose one or more child jobs</label>
      <select
        multiple
        size={Math.min(Math.max(candidateChildJobs.length, 4), 10)}
        value={associateTargetJobIds}
        onChange={(e) => {
          const selectedIds = Array.from(e.target.selectedOptions).map((option) => option.value);
          setAssociateTargetJobIds(selectedIds);
          setAssociateTargetJobId(selectedIds[0] || "");
          const selectedWOs = selectedIds
            .map((id) => jobs.find((job) => job.id === id)?.wo)
            .filter(Boolean);
          setAssociateWOInput(selectedWOs.join(", "));
        }}
        style={{ width: "100%", minHeight: 120 }}
      >
        {candidateChildJobs.map((job) => (
            <option key={job.id} value={job.id}>
              WO {job.wo} — {job.siteName} — {job.startDate || "No date"} — {job.scope}
            </option>
          ))}
      </select>

      <div style={{ marginTop: 8, fontSize: 12, color: "#475569" }}>
        Selected to add: <strong>{getSelectedAssociationTargetIds().length}</strong>
      </div>

      {associateWOInput && getTypedWOTokens().length > 0 && findAssociationTargetsFromWO().length !== getTypedWOTokens().length && (
        <div style={{ marginTop: 8, fontSize: 12, color: "#dc2626", fontWeight: 700 }}>
          One or more typed WO numbers were not found or are not available. They may already have another parent, or the WO may not exist.
        </div>
      )}

      {associateJob && getAssociatedChildren(associateJob).length > 0 && (
        <div style={{ marginTop: 12, fontSize: 13 }}>
          <strong>Direct child jobs — click Unlink to remove from this branch:</strong>
          {getAssociatedChildren(associateJob).map((job) => (
              <div key={job.id} style={{ marginTop: 6, padding: "6px 8px", border: "1px solid #e2e8f0", borderRadius: 6 }}>
                WO {job.wo} — {job.siteName}{" "}
                <button
                  type="button"
                  onClick={() => removeJobAssociation(associateJob, job.id)}
                  style={{
                    fontSize: 11,
                    color: "white",
                    background: "#dc2626",
                    border: "none",
                    borderRadius: 4,
                    padding: "3px 6px",
                    marginLeft: 8,
                    cursor: "pointer",
                  }}
                >
                  Unlink
                </button>
              </div>
            ))}
        </div>
      )}

      <div className="jes-popup-buttons">
        <BusyButton className="green-button" onClick={associateJobs} isBusy={busyAction === "associateJobs"} disabled={!!busyMessage}>Add Selected Child Jobs</BusyButton>
        <button className="red-button" onClick={() => { setAssociateTargetJobIds([]); setAssociateTargetJobId(""); setAssociateWOInput(""); setShowAssociatePopup(false); }}>Cancel</button>
      </div>
    </div>
  </Popup>
)}

            {showPaymentPopup && (
              <Popup width={520} onClose={() => setShowPaymentPopup(false)}>
                <div>
                <h2>{editingPaymentIndex !== null ? "Edit Deposit / Payment" : "Add Deposit / Payment"}</h2>
      
                  <p><strong>Job:</strong> {paymentJob?.siteName}</p>
      
                  <label>Payment Type</label>
                  <select value={paymentType} onChange={(e) => setPaymentType(e.target.value)}>
                    <option value="Deposit">Deposit</option>
                    <option value="Final">Final</option>
                    <option value="Progress">Progress</option>
                    <option value="Other">Other</option>
                  </select>
                  <div style={{ fontSize: 12, color: "#64748b", marginBottom: 8 }}>
                    This is a payment note only. It does not change the estimated/contract total or profit.
                  </div>
      
                  <label>Amount</label>
                  <input
                    type="number"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                  />
      
                  <label>Date</label>
                  <input
                    type="date"
                    value={paymentDate}
                    onChange={(e) => setPaymentDate(e.target.value)}
                  />
      
                  <div className="jes-popup-buttons">
                    <BusyButton className="green-button" onClick={savePayment} isBusy={busyAction === "savePayment"} disabled={!!busyMessage}>
                    {editingPaymentIndex !== null ? "Update Payment" : "Save Payment"}
                    </BusyButton>
                    <button className="red-button" onClick={() => setShowPaymentPopup(false)}>
                      Cancel
                    </button>
                  </div>
                </div>
              </Popup>
            )}
      
      {showSubPayPopup && subPayJob && (() => {
        const jobPayments = subcontractorPayments.filter((p) => paymentIsForJob(p, subPayJob));
        const jobPaidTotal = jobPayments.reduce((sum, p) => sum + numberValue(p.amount), 0);
        const draftTotal = calculateSubPayTotal(subPayDraft);
        const closePopup = () => {
          setShowSubPayPopup(false);
          setSubPayJob(null);
          setSubPayDraft([]);
        };
        const updateLine = (index, changes) =>
          setSubPayDraft((prev) => prev.map((line, i) => (i === index ? { ...line, ...changes } : line)));
        const updateCO = (lineIndex, coIndex, changes) =>
          setSubPayDraft((prev) =>
            prev.map((line, i) =>
              i === lineIndex
                ? {
                    ...line,
                    changeOrders: (line.changeOrders || []).map((co, j) => (j === coIndex ? { ...co, ...changes } : co)),
                  }
                : line
            )
          );

        return (
          <Popup width={1000} onClose={closePopup} className="jes-subpay-popup">
            <div
              className="subpay-modal"
              aria-labelledby="subpay-title"
              style={{ width: "100%", padding: 0, boxShadow: "none", borderRadius: 0 }}
            >
              <div className="subpay-modal-head">
                <div>
                  <h2 id="subpay-title">Sub Pay</h2>
                  <p className="subpay-muted" style={{ margin: 0 }}>
                    {describeJobForPayment(subPayJob)}
                  </p>
                </div>
                <button type="button" className="subpay-secondary" onClick={closePopup}>
                  Close
                </button>
              </div>

              <section className="subpay-section is-primary">
                <h3>Record a payment on this job</h3>
                <SubPaymentEntry
                  contractorOptions={payableContractorOptions}
                  defaultContractorKey={
                    (() => {
                      const named = subPayDraft.filter((line) => subPayLineName(line));
                      if (named.length !== 1) return "";
                      return findPayableTarget(named[0].subcontractorName, named[0].subcontractorId)?.key || "";
                    })()
                  }
                  busy={subPaySaving}
                  intro="Saves to Payment History and Subcontractor Payables, adds the sub to Tech Assign, and raises this job's Sub Pay if you've paid more than it shows. Any edits below are saved too."
                  onSubmit={handleJobSubPaymentSubmit}
                />
              </section>

              <section className="subpay-section">
                <h3>Payments on this job</h3>
                <SubPaymentHistory
                  payments={jobPayments}
                  showJob={false}
                  showFilter={false}
                  emptyText="No payments recorded on this job yet."
                  busyId={deletingSubPaymentId}
                  onDelete={deleteSubPayment}
                />
              </section>

              <section className="subpay-section">
                <h3>What each sub is owed on this job</h3>
                <p className="subpay-muted">
                  This is the job's Sub Pay. It's the cost side; payments above are the paid side.
                </p>

                {subPayDraft.map((line, lineIndex) => {
                  const target = subPayLineName(line)
                    ? findPayableTarget(line.subcontractorName, line.subcontractorId)
                    : null;
                  const selectValue = subPayOtherLines[lineIndex]
                    ? "__other__"
                    : target
                    ? target.key
                    : subPayLineName(line)
                    ? "__asTyped__"
                    : "";
                  const lineTotal = subPayLineTotal(line);
                  const paidForLine = target
                    ? jobPayments
                        .filter((p) => paymentMatchesTarget(p, target))
                        .reduce((sum, p) => sum + numberValue(p.amount), 0)
                    : 0;

                  return (
                    <div className="subpay-line" key={lineIndex}>
                      {line.__legacyAmount && !subPayLineName(line) && (
                        <div className="subpay-warn" style={{ marginTop: 0, marginBottom: 8 }}>
                          This job has {money(line.basePay)} of Sub Pay with no subcontractor picked. Pick who it was for.
                        </div>
                      )}
                      <div className="subpay-line-head">
                        <label className="subpay-field">
                          <span>Subcontractor</span>
                          <select
                            value={selectValue}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === "__other__") {
                                setSubPayOtherLines((prev) => ({ ...prev, [lineIndex]: true }));
                                return;
                              }
                              setSubPayOtherLines((prev) => ({ ...prev, [lineIndex]: false }));
                              if (value === "__asTyped__") return;
                              const picked = subcontractorTargets.find((t) => t.key === value);
                              updateLine(lineIndex, {
                                subcontractorName: picked ? picked.subName : "",
                                subcontractorId: picked ? subIdForTarget(picked) : "",
                              });
                            }}
                          >
                            <option value="">Select contractor</option>
                            {selectValue === "__asTyped__" && (
                              <option value="__asTyped__">{subPayLineName(line)} (as typed)</option>
                            )}
                            {subcontractorTargets.map((t) => (
                              <option key={t.key} value={t.key}>
                                {t.subName}
                                {t.onRoster ? "" : " (not on roster)"}
                              </option>
                            ))}
                            <option value="__other__">Someone not listed…</option>
                          </select>
                          {subPayOtherLines[lineIndex] && (
                            <input
                              style={{ marginTop: 6 }}
                              placeholder="Company or person name"
                              value={line.subcontractorName || ""}
                              onChange={(e) =>
                                updateLine(lineIndex, { subcontractorName: e.target.value, subcontractorId: "" })
                              }
                            />
                          )}
                        </label>

                        <label className="subpay-field">
                          <span>Base pay (agreed price)</span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={line.basePay ?? ""}
                            onChange={(e) => updateLine(lineIndex, { basePay: e.target.value })}
                          />
                        </label>

                        <button
                          type="button"
                          className="subpay-link-danger"
                          onClick={() => {
                            if (paidForLine > 0) {
                              const ok = window.confirm(
                                `${money(paidForLine)} has been paid to this sub on this job. Remove the line anyway? The payments stay in Payment History.`
                              );
                              if (!ok) return;
                            }
                            setSubPayDraft((prev) => prev.filter((_, i) => i !== lineIndex));
                            setSubPayOtherLines({});
                          }}
                        >
                          Remove
                        </button>
                      </div>

                      <div style={{ marginTop: 8 }}>
                        <strong style={{ fontSize: 13 }}>Change orders</strong>
                        {(line.changeOrders || []).map((co, coIndex) => (
                          <div className="subpay-co-row" key={coIndex}>
                            <input
                              placeholder="Description"
                              value={co.description || ""}
                              onChange={(e) => updateCO(lineIndex, coIndex, { description: e.target.value })}
                            />
                            <input
                              type="number"
                              placeholder="Amount"
                              value={co.amount ?? ""}
                              onChange={(e) => updateCO(lineIndex, coIndex, { amount: e.target.value })}
                            />
                            <input
                              type="date"
                              value={co.date || ""}
                              onChange={(e) => updateCO(lineIndex, coIndex, { date: e.target.value })}
                            />
                            <button
                              type="button"
                              className="subpay-x"
                              aria-label="Remove change order"
                              onClick={() =>
                                updateLine(lineIndex, {
                                  changeOrders: (line.changeOrders || []).filter((_, j) => j !== coIndex),
                                })
                              }
                            >
                              ×
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          className="subpay-link"
                          onClick={() =>
                            updateLine(lineIndex, {
                              changeOrders: [...(line.changeOrders || []), { description: "", amount: "", date: "" }],
                            })
                          }
                        >
                          + Add change order
                        </button>
                      </div>

                      <div className="subpay-line-stats">
                        <span>Owed: <strong>{money(lineTotal)}</strong></span>
                        <span>Paid on this job: <strong>{money(paidForLine)}</strong></span>
                        <span className={lineTotal - paidForLine > 0.004 ? "subpay-owe" : "subpay-clear"}>
                          Left: {money(lineTotal - paidForLine)}
                        </span>
                      </div>
                    </div>
                  );
                })}

                <button
                  type="button"
                  className="subpay-link"
                  style={{ marginTop: 8 }}
                  onClick={() =>
                    setSubPayDraft((prev) => [...prev, { subcontractorName: "", basePay: "", changeOrders: [] }])
                  }
                >
                  + Add subcontractor
                </button>
              </section>

              <div className="subpay-footer">
                <div>
                  <strong>Total Sub Pay: {money(draftTotal)}</strong>
                  <div className="subpay-muted" style={{ margin: 0 }}>
                    Paid on this job: {money(jobPaidTotal)} · Saving adds every sub listed to Tech Assign.
                  </div>
                </div>
                <div className="subpay-actions" style={{ marginTop: 0 }}>
                  <button type="button" className="subpay-secondary" onClick={closePopup}>
                    Cancel
                  </button>
                  <BusyButton
                    className="green-button"
                    onClick={saveSubPay}
                    isBusy={busyAction === "saveSubPay"}
                    disabled={!!busyMessage || subPaySaving}
                  >
                    Save Sub Pay
                  </BusyButton>
                </div>
              </div>
            </div>
          </Popup>
        );
      })()}

            {showCheckPopup && (
              <Popup width={520} onClose={() => setShowCheckPopup(false)}>
                <div>
                <h2>{editingCheckIndex !== null ? "Edit Check Received" : "Add Check Received"}</h2>
      
                  <p><strong>Job:</strong> {checkJob?.siteName}</p>
      
                  <label>Vendor</label>
                  <select value={checkVendor} onChange={(e) => setCheckVendor(e.target.value)}>
                    {checkVendorOptions.map((vendor) => (
                      <option key={vendor} value={vendor}>{vendor}</option>
                    ))}
                  </select>
                  {checkVendor === "Other" && (
                    <input
                      value={checkVendorOther}
                      onChange={(e) => setCheckVendorOther(e.target.value)}
                      placeholder="Who the check was made out to"
                    />
                  )}
      
                  <label>Check Number</label>
                  <input value={checkNumber} onChange={(e) => setCheckNumber(e.target.value)} />
      
                  <label>Check Date</label>
                  <input type="date" value={checkDate} onChange={(e) => setCheckDate(e.target.value)} />

                  <label>Payment Type</label>
                  <select value={checkPaymentType} onChange={(e) => setCheckPaymentType(e.target.value)}>
                    {paymentTypeOptions.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
      
                  <label>Reference</label>
                  <input value={checkReference} onChange={(e) => setCheckReference(e.target.value)} />
      
                  <label>Check Amount</label>
                  <input type="number" value={checkAmount} onChange={(e) => setCheckAmount(e.target.value)} />
      
                  <div className="jes-popup-buttons">
                    <BusyButton className="green-button" onClick={saveCheckReceived} isBusy={busyAction === "saveCheckReceived"} disabled={!!busyMessage}>
                    {editingCheckIndex !== null ? "Update Check" : "Save Check"}
                    </BusyButton>
                    <button className="red-button" onClick={() => setShowCheckPopup(false)}>
                      Cancel
                    </button>
                  </div>
                </div>
              </Popup>
            )}

{showSplitCheckPopup && (
  <Popup width={900} onClose={() => { setShowLinkableWOsSection(false); setShowSplitCheckPopup(false); }}>
    <div>
      <h2>{editingSplitCheckGroupId ? "Edit Split Check" : "Split Check Across Jobs"}</h2>

      <p>
        <strong>Property:</strong> {splitCheckJob?.siteName}
      </p>

      <label>Vendor</label>
      <select
        value={splitCheckVendor}
        onChange={(e) => setSplitCheckVendor(e.target.value)}
      >
        {checkVendorOptions.map((vendor) => (
          <option key={vendor} value={vendor}>{vendor}</option>
        ))}
      </select>
      {splitCheckVendor === "Other" && (
        <input
          value={splitCheckVendorOther}
          onChange={(e) => setSplitCheckVendorOther(e.target.value)}
          placeholder="Who the check was made out to"
        />
      )}

      <label>Check Number</label>
      <input
        value={splitCheckNumber}
        onChange={(e) => setSplitCheckNumber(e.target.value)}
      />

      <label>Check Date</label>
      <input
        type="date"
        value={splitCheckDate}
        onChange={(e) => setSplitCheckDate(e.target.value)}
      />

      <label>Payment Type</label>
      <select
        value={splitCheckPaymentType}
        onChange={(e) => setSplitCheckPaymentType(e.target.value)}
      >
        {paymentTypeOptions.map((type) => (
          <option key={type} value={type}>{type}</option>
        ))}
      </select>

<label>Total Check Amount</label>
<input
  type="number"
  value={splitCheckTotal}
  onChange={(e) => setSplitCheckTotal(e.target.value)}
/>

<button
  type="button"
  className="blue-button"
  style={{ marginTop: 8, marginBottom: 10 }}
  onClick={equalDivideSplitCheck}
>
  Equal Divide
</button>

<p style={{ fontSize: 12, color: "#64748b", marginTop: 0 }}>
  Use <strong>Exclude Split</strong> on an invoice/master WO that should stay checked for reference only. Equal Divide and Save will skip that row.
</p>

<div
  style={{
    marginTop: 12,
    display: "flex",
    gap: 8,
    alignItems: "end",
    flexWrap: "wrap",
  }}
>
  <div style={{ flex: "1 1 180px" }}>
    <label>Sort Selected Jobs</label>
    <select
      value={splitSortBy}
      onChange={(e) => {
        const nextSort = e.target.value;
        setSplitSortBy(nextSort);
        setSplitRows((prev) => sortSplitRows(prev, nextSort));
      }}
    >
      <option value="wo">Work Order #</option>
      <option value="date">Start Date</option>
    </select>
  </div>

  <button
    type="button"
    className="blue-button"
    onClick={() => {
      setLinkableWOSearch("");
      setShowLinkableWOsSection((prev) => !prev);
    }}
    style={{ minWidth: 190 }}
  >
    Select Linkable WOs
  </button>
</div>

<div style={{ marginTop: 8, fontSize: 12, color: "#64748b" }}>
  Selected: <strong>{splitRows.filter((row) => row.selected).length}</strong> of{" "}
  <strong>{splitRows.length}</strong> available WOs
</div>

<div
  style={{
    marginTop: 12,
    maxHeight: 300,
    overflowY: "auto",
    border: "1px solid #dbe3ec",
    borderRadius: 6,
    padding: "0 10px",
  }}
>
  {sortSplitRows(
    splitRows.filter((row) => row.selected),
    splitSortBy
  ).map((row) => {
    const rowKey = String(row.wo || row.jobId || "");

    return (
      <div
        key={rowKey}
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(180px, 1fr) 120px 150px",
          gap: 8,
          alignItems: "center",
          borderBottom: "1px solid #ddd",
          padding: "8px 0",
        }}
      >
        <div>
          <strong>WO {row.displayWO || row.wo}</strong>
          <div style={{ fontSize: 12, color: "#64748b" }}>
            {row.scope || "No scope"}
          </div>
          {row.startDate && (
            <div style={{ fontSize: 11, color: "#94a3b8" }}>
              {row.startDate}
            </div>
          )}
        </div>

        <input
          type="number"
          placeholder={row.invoiceOnly ? "Invoice Only" : "Amount"}
          value={row.amount}
          disabled={row.invoiceOnly}
          onChange={(e) =>
            updateSplitRowByKey(rowKey, { amount: e.target.value })
          }
        />

        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          <button
            type="button"
            className={row.invoiceOnly ? "green-button" : "blue-button"}
            style={{ padding: "6px 8px", fontSize: 12 }}
            onClick={() =>
              updateSplitRowByKey(rowKey, (current) => {
                const nextInvoiceOnly = !current.invoiceOnly;
                return {
                  selected: true,
                  invoiceOnly: nextInvoiceOnly,
                  amount: nextInvoiceOnly ? "" : current.amount,
                };
              })
            }
          >
            {row.invoiceOnly ? "Invoice Only" : "Exclude Split"}
          </button>

          <button
            type="button"
            className="red-button"
            style={{ padding: "6px 8px", fontSize: 12 }}
            onClick={() =>
              updateSplitRowByKey(rowKey, {
                selected: false,
                invoiceOnly: false,
                amount: "",
              })
            }
          >
            Remove
          </button>
        </div>
      </div>
    );
  })}

  {splitRows.filter((row) => row.selected).length === 0 && (
    <div style={{ padding: 18, textAlign: "center", color: "#64748b" }}>
      No WOs selected. Click <strong>Select Linkable WOs</strong>.
    </div>
  )}
</div>


      {showLinkableWOsSection && (
        <div
          style={{
            marginTop: 18,
            paddingTop: 16,
            borderTop: "3px solid #2563eb",
          }}
        >
          <h3 style={{ margin: "0 0 10px" }}>
            Select Linkable Work Orders
          </h3>

          <p style={{ marginTop: 0, color: "#64748b" }}>
            Property: <strong>{splitCheckJob?.siteName || ""}</strong>
          </p>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(260px, 1fr) 180px auto auto",
              gap: 10,
              alignItems: "end",
              marginBottom: 10,
            }}
          >
            <div>
              <label>Search WOs, Scope, or Date</label>
              <input
                type="text"
                value={linkableWOSearch}
                onChange={(e) => setLinkableWOSearch(e.target.value)}
                placeholder="Example: 3345, drywall, 2026-06"
              />
            </div>

            <div>
              <label>Sort Jobs</label>
              <select
                value={splitSortBy}
                onChange={(e) => setSplitSortBy(e.target.value)}
              >
                <option value="wo">Work Order #</option>
                <option value="date">Start Date</option>
              </select>
            </div>

            <button
              type="button"
              className="blue-button"
              onClick={() =>
                setSplitRows((prev) =>
                  prev.map((row) => ({ ...row, selected: true }))
                )
              }
            >
              Select All
            </button>

            <button
              type="button"
              className="red-button"
              onClick={() =>
                setSplitRows((prev) =>
                  prev.map((row) => ({
                    ...row,
                    selected: false,
                    invoiceOnly: false,
                    amount: "",
                  }))
                )
              }
            >
              Clear All
            </button>
          </div>

          <div
            style={{
              fontSize: 12,
              color: "#64748b",
              marginBottom: 8,
            }}
          >
            Showing{" "}
            <strong>{getFilteredLinkableSplitRows().length}</strong> WOs.
            Selected{" "}
            <strong>{splitRows.filter((row) => row.selected).length}</strong>.
          </div>

          <div
            style={{
              height: "52vh",
              minHeight: 360,
              maxHeight: 620,
              overflowY: "auto",
              border: "1px solid #cbd5e1",
              borderRadius: 8,
              background: "#fff",
            }}
          >
            {getFilteredLinkableSplitRows().map((row) => {
              const rowKey = String(row.wo || row.jobId || "");

              return (
                <label
                  key={rowKey}
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "42px 150px 130px minmax(260px, 1fr)",
                    gap: 8,
                    alignItems: "center",
                    padding: "10px 12px",
                    borderBottom: "1px solid #e5e7eb",
                    cursor: "pointer",
                    background: row.selected ? "#eff6ff" : "white",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={Boolean(row.selected)}
                    onChange={(e) =>
                      updateSplitRowByKey(rowKey, {
                        selected: e.target.checked,
                        ...(e.target.checked
                          ? {}
                          : {
                              invoiceOnly: false,
                              amount: "",
                            }),
                      })
                    }
                  />

                  <strong>WO {row.displayWO || row.wo}</strong>

                  <span
                    style={{
                      fontSize: 12,
                      color: "#64748b",
                    }}
                  >
                    {row.startDate || "No date"}
                  </span>

                  <span
                    style={{
                      fontSize: 12,
                      color: "#475569",
                      whiteSpace: "normal",
                    }}
                  >
                    {row.scope || "No scope"}
                  </span>
                </label>
              );
            })}

            {getFilteredLinkableSplitRows().length === 0 && (
              <div
                style={{
                  padding: 24,
                  textAlign: "center",
                  color: "#64748b",
                }}
              >
                No matching work orders.
              </div>
            )}
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: 10,
            }}
          >
            <button
              type="button"
              className="green-button"
              onClick={() => setShowLinkableWOsSection(false)}
            >
              Done Selecting
            </button>
          </div>
        </div>
      )}

      <div style={{ marginTop: 12, fontWeight: "bold" }}>
        Split Total:{" "}
        {money(
          splitRows
            .filter((row) => row.selected && !row.invoiceOnly)
            .reduce((sum, row) => sum + numberValue(row.amount), 0)
        )}
        {splitRows.some((row) => row.selected && row.invoiceOnly) && (
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
            Invoice-only rows are checked for reference/invoice tracking, but they do not receive split amounts and do not update estimated total, received, balance, or profit.
          </div>
        )}
      </div>

      <div className="jes-popup-buttons">
        <BusyButton className="green-button" onClick={saveSplitCheck} isBusy={busyAction === "saveSplitCheck"} disabled={!!busyMessage}>
          {editingSplitCheckGroupId ? "Update Split Check" : "Save Split Check"}
        </BusyButton>

        <button
          className="red-button"
          onClick={() => setShowSplitCheckPopup(false)}
        >
          Cancel
        </button>
      </div>
    </div>
  </Popup>
)}


          </div>
        );
      }



function ButtonSpinner() {
  return (
    <span
      aria-hidden="true"
      style={{
        display: "inline-block",
        width: 14,
        height: 14,
        border: "2px solid rgba(255,255,255,0.65)",
        borderTop: "2px solid white",
        borderRadius: "50%",
        animation: "jesSpin 0.8s linear infinite",
        marginRight: 6,
        verticalAlign: "-2px",
      }}
    />
  );
}

function BusyButton({ children, isBusy = false, disabled = false, style = {}, className = "", type = "button", onClick }) {
  return (
    <button
      type={type}
      className={className}
      onClick={onClick}
      disabled={disabled || isBusy}
      style={{
        opacity: disabled || isBusy ? 0.75 : 1,
        cursor: disabled || isBusy ? "not-allowed" : "pointer",
        ...style,
      }}
    >
      {isBusy && <ButtonSpinner />}
      {children}
    </button>
  );
}

function LoadingSpinner({ message = "Loading..." }) {
  return (
    <div style={{ padding: 30, display: "flex", alignItems: "center", gap: 12 }}>
      <div
        style={{
          width: 28,
          height: 28,
          border: "4px solid #cbd5e1",
          borderTop: "4px solid #2563eb",
          borderRadius: "50%",
          animation: "jesSpin 0.8s linear infinite",
        }}
      />
      <style>{`@keyframes jesSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      <strong>{message}</strong>
    </div>
  );
}

function LoadingOverlay({ message = "Working..." }) {
  // Portaled above every popup (popups use 999998-1999999).
  return createPortal(
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.25)",
        zIndex: 2999998,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: 12,
          padding: 20,
          boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
          display: "flex",
          alignItems: "center",
          gap: 12,
          minWidth: 240,
        }}
      >
        <div
          style={{
            width: 28,
            height: 28,
            border: "4px solid #cbd5e1",
            borderTop: "4px solid #0f766e",
            borderRadius: "50%",
            animation: "jesSpin 0.8s linear infinite",
          }}
        />
        <style>{`@keyframes jesSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        <strong>{message}</strong>
      </div>
    </div>,
    document.body
  );
}

function CellInput({ value, onChange, type = "text" }) {
  return (
    <input
      type={type}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      style={{
        width: "100%",
        minWidth: 120,
        padding: 6,
        border: "1px solid #ccc",
        borderRadius: 4,
      }}
    />
  );
}

function Th({ children }) {
  return (
    <th
      style={{
        border: "1px solid #ddd",
        padding: 8,
        background: "#1d4ed8",
        color: "white",
        position: "sticky",
        top: 0,
        zIndex: 1,
      }}
    >
      {children}
    </th>
  );
}

function Td({ children, style = {} }) {
  return (
    <td
      style={{
        border: "1px solid #ddd",
        padding: 8,
        verticalAlign: "top",
        ...style,
      }}
    >
      {children}
    </td>
  );
}

function SummaryBox({ title, value, note = "" }) {
  return (
    <div
      style={{
        border: "1px solid #1d4ed8",
        borderRadius: 10,
        padding: 14,
        background: "#f8fafc",
      }}
    >
      <div style={{ fontSize: 13, color: "#475569" }}>{title}</div>
      <div style={{ fontSize: 22, fontWeight: "bold" }}>{value}</div>
      {note && <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>{note}</div>}
    </div>
  );
}

// Popup rendered on document.body with inline styles only (no global .modal
// classes), so it sits above the Accounting tabs and app chrome.
function Popup({ onClose, width = 640, layer = 0, className = "jes-popup", children }) {
  const zBase = layer ? 1999998 : 999998;
  return createPortal(
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,.55)",
          zIndex: zBase,
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        className={className}
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%,-50%)",
          zIndex: zBase + 1,
          width: `min(${width}px, 95vw)`,
          maxHeight: "90vh",
          overflowY: "auto",
          background: "#fff",
          color: "#0f172a",
          borderRadius: 12,
          padding: 20,
          boxSizing: "border-box",
          boxShadow: "0 20px 55px rgba(0,0,0,.3)",
          textAlign: "left",
        }}
      >
        {children}
      </div>
    </>,
    document.body
  );
}